<template>
    <div class="operationsjournal">
        <modal-basic ref="dialog" />
        <h1>Operationsjournal</h1>
        <KSection
            name="operationsuppgifter"
            heading="Operationsuppgifter"
            :numbered="true"
            :collapsible="true"
            :expanded="sectionsExpanded"
            class="operationsuppgifter"
            :custom-config-provide-suffix="operationsuppgifterConfigSuffix"
        >
            <FormulateForm
                :readonly="readonlyOperationsUppgifter"
                @submit="submitOperationsuppgifter"
                v-model="operationsuppgifterFormData"
                :errors="formError"
                ref="operationsuppgifterFormRef"
                autocomplete="off"
            >
                <KInputWrapper
                    class="typoperationer"
                    name="typoperation"
                    v-slot="{ inputName, shouldBeVisible, shouldBeRequired }"
                >
                    <FormulateInput
                        v-if="shouldBeVisible"
                        :readonly="readonlyOperationsUppgifter"
                        :clearable="!readonlyOperationsUppgifter"
                        :name="inputName"
                        :options="typoperationer"
                        type="vueselect"
                        isDropdown="true"
                        placeholder="Välj typoperation"
                        :label="`Typoperation ${checkAddAsterisk(shouldBeRequired)}`"
                        v-on:input="typoperationChanged()"
                        :validation="shouldBeRequired ? 'required' : ''"
                    />
                </KInputWrapper>

                <KInputWrapper name="huvuddiagnos" v-slot="{ inputName, shouldBeVisible, shouldBeRequired }">
                    <FormulateInput
                        v-if="shouldBeVisible"
                        :readonly="readonlyOperationsUppgifter"
                        id="huvuddiagnos"
                        :name="inputName"
                        :label="`Huvuddiagnoskod ${checkAddAsterisk(shouldBeRequired)}`"
                        type="vueselect"
                        :options="huvuddiagnoser"
                        @search="searchHuvuddiagnos"
                        v-on:input="huvuddiagnosChanged()"
                        class="autocomplete"
                        :validation="shouldBeRequired ? 'required' : ''"
                    />
                </KInputWrapper>

                <KInputWrapper name="bidiagnoser" v-slot="{ inputName, shouldBeVisible, shouldBeRequired }">
                    <FormulateInput
                        v-if="shouldBeVisible"
                        :readonly="readonlyOperationsUppgifter"
                        type="group"
                        :name="inputName"
                        :repeatable="true"
                        add-label="Lägg till bidiagnoser"
                        remove-position="after"
                    >
                        <template #default="{ index }">
                            <FormulateInput
                                :readonly="readonlyOperationsUppgifter"
                                name="bidiagnos"
                                :label="`Bidiagnoskod ${checkAddAsterisk(shouldBeRequired)}`"
                                type="vueselect"
                                isDropdown="true"
                                :options="bidiagnoser[index]"
                                @search="(search, loading) => searchBidiagnoser(index, search, loading)"
                                v-on:input="bidiagnosChanged(index)"
                                class="autocomplete"
                                :validation="shouldBeRequired ? 'required' : ''"
                            />
                        </template>

                        <template #remove="{ index, removeItem }">
                            <a
                                role="button"
                                class="formulate-input-group-repeatable-remove"
                                @click="removeBidiagnoser(index, removeItem)"
                            >
                                Radera fält
                            </a>
                        </template>

                        <template #add-more="{ addMore }">
                            <div class="formulate-input-group-add-more">
                                <div class="readonly">
                                    <button
                                        type="button"
                                        :disabled="readonlyOperationsUppgifter"
                                        data-minor
                                        :data-ghost="readonlyOperationsUppgifter"
                                        @click="addMoreBidiagnoser(addMore)"
                                    >
                                        <span class="formulate-input-element--button--label"
                                            >Lägg till fler bidiagnoser</span
                                        >
                                    </button>
                                </div>
                            </div>
                        </template>
                    </FormulateInput>
                </KInputWrapper>

                <KInputWrapper name="huvudoperationskod" v-slot="{ inputName, shouldBeVisible, shouldBeRequired }">
                    <FormulateInput
                        v-if="shouldBeVisible"
                        :readonly="readonlyOperationsUppgifter"
                        id="huvudoperationskod"
                        :name="inputName"
                        :label="`Huvudoperationskod ${checkAddAsterisk(shouldBeRequired)}`"
                        type="vueselect"
                        isDropdown="true"
                        :options="huvudoperationskoder"
                        @search="searchHuvudoperationskod"
                        v-on:input="huvudoperationskodChanged()"
                        class="autocomplete"
                        :validation="shouldBeRequired ? 'required' : ''"
                    />
                </KInputWrapper>

                <KInputWrapper name="bioperationskoder" v-slot="{ inputName, shouldBeVisible, shouldBeRequired }">
                    <FormulateInput
                        v-if="shouldBeVisible"
                        :readonly="readonlyOperationsUppgifter"
                        type="group"
                        :name="inputName"
                        :repeatable="true"
                        add-label="Lägg till bioperationskod"
                        remove-position="after"
                    >
                        <template #default="{ index }">
                            <FormulateInput
                                :readonly="readonlyOperationsUppgifter"
                                name="bioperationskod"
                                :label="`Bioperationskod ${checkAddAsterisk(shouldBeRequired)}`"
                                type="vueselect"
                                :options="bioperationskoder[index]"
                                @search="(search, loading) => searchBioperationskoder(index, search, loading)"
                                v-on:input="bioperationskodChanged(index)"
                                class="autocomplete"
                                :validation="shouldBeRequired ? 'required' : ''"
                            />
                        </template>
                        <template #remove="{ index, removeItem }">
                            <a
                                role="button"
                                class="formulate-input-group-repeatable-remove"
                                @click="removeBioperationskoder(index, removeItem)"
                            >
                                Radera fält
                            </a>
                        </template>

                        <template #addMore="{ addMore }">
                            <div class="formulate-input-group-add-more">
                                <button
                                    :readonly="readonlyOperationsUppgifter"
                                    type="button"
                                    something="something"
                                    data-minor=""
                                    data-ghost=""
                                    @click="addMoreBioperationskoder(addMore)"
                                >
                                    <span class="formulate-input-element--button--label"
                                        >Lägg till fler bioperationskoder</span
                                    >
                                </button>
                            </div>
                        </template>
                    </FormulateInput>
                </KInputWrapper>

                <div class="row">
                    <KInputWrapper name="operationsdatum" v-slot="{ inputName, shouldBeVisible }">
                        <FormulateInput
                            v-if="shouldBeVisible"
                            readonly
                            type="date"
                            :name="inputName"
                            label="Operationsdatum"
                            @input="removeError('operationsdatum')"
                        />
                    </KInputWrapper>

                    <KInputWrapper name="sal" v-slot="{ inputName, shouldBeVisible, shouldBeRequired }">
                        <FormulateInput
                            v-if="shouldBeVisible"
                            :readonly="readonlyOperationsUppgifter"
                            :name="inputName"
                            :label="`Opsal ${checkAddAsterisk(shouldBeRequired)}`"
                            type="vueselect"
                            :options="salar"
                            placeholder="Välj Opsal"
                            :validation="shouldBeRequired ? 'required' : ''"
                        />
                    </KInputWrapper>

                    <KInputWrapper name="reop" v-slot="{ inputName, shouldBeVisible }">
                        <FormulateInput
                            v-if="shouldBeVisible"
                            :disabled="readonlyOperationsUppgifter"
                            type="checkbox"
                            :name="inputName"
                            label="Reoperation"
                        />
                    </KInputWrapper>

                    <KInputWrapper name="garantiop" v-slot="{ inputName, shouldBeVisible }">
                        <FormulateInput
                            v-if="shouldBeVisible"
                            :disabled="readonlyOperationsUppgifter"
                            type="checkbox"
                            :name="inputName"
                            label="Garantioperation"
                        />
                    </KInputWrapper>
                </div>

                <div class="row">
                    <KInputWrapper name="lateralitet" v-slot="{ inputName, shouldBeVisible, shouldBeRequired }">
                        <FormulateInput
                            v-if="shouldBeVisible"
                            :readonly="readonlyOperationsUppgifter"
                            :name="inputName"
                            :label="`Sida ${checkAddAsterisk(shouldBeRequired)}`"
                            type="vueselect"
                            :options="lateraliteter"
                            :validation="shouldBeRequired ? 'required' : ''"
                        />
                    </KInputWrapper>
                    <KInputWrapper name="oplage" v-slot="{ inputName, shouldBeVisible, shouldBeRequired }">
                        <FormulateInput
                            v-if="shouldBeVisible"
                            :readonly="readonlyOperationsUppgifter"
                            :name="inputName"
                            :label="`Patientläge ${checkAddAsterisk(shouldBeRequired)}`"
                            type="vueselect"
                            :options="oplageOptions"
                            :validation="shouldBeRequired ? 'required' : ''"
                        />
                    </KInputWrapper>
                    <KInputWrapper name="vardform" v-slot="{ inputName, shouldBeVisible, shouldBeRequired }">
                        <FormulateInput
                            v-if="shouldBeVisible"
                            :readonly="readonlyOperationsUppgifter"
                            :name="inputName"
                            :label="`Vårdform ${checkAddAsterisk(shouldBeRequired)}`"
                            type="vueselect"
                            :options="vardformOptions"
                            :validation="shouldBeRequired ? 'required' : ''"
                        />
                    </KInputWrapper>
                </div>
                <KInputWrapper name="niva_fran_till" v-slot="{ inputName, shouldBeVisible, shouldBeRequired }">
                    <FormulateInput
                        v-if="shouldBeVisible && nivaerGroupVisible"
                        type="group"
                        :name="inputName"
                        :repeatable="true"
                        add-label="Lägg till nivå"
                        remove-label="Ta bort nivå"
                        remove-position="after"
                        :readonly="readonlyOperationsUppgifter"
                        class="niva_fran_till"
                    >
                        <template #default="{ index }">
                            <FormulateInput
                                :readonly="false"
                                name="fran"
                                :label="`Nivå från ${checkAddAsterisk(shouldBeRequired)}`"
                                type="vueselect"
                                :validation="shouldBeRequired ? 'required' : ''"
                                :options="ryggkotor"
                                @input="removeErrors([`niva_fran_till.${index}.fran`, `niva_fran_till.${index}.till`])"
                            />

                            <FormulateInput
                                :readonly="false"
                                name="till"
                                :label="`Nivå till ${checkAddAsterisk(shouldBeRequired)}`"
                                type="vueselect"
                                :validation="shouldBeRequired ? 'required' : ''"
                                :options="ryggkotor"
                                @input="removeErrors([`niva_fran_till.${index}.fran`, `niva_fran_till.${index}.till`])"
                            />
                            <div v-if="index === 0" class="formulate-input">
                                <KLabel content="Nivåer" />
                                <p>{{ nivaDiffs }}</p>
                            </div>
                        </template>
                    </FormulateInput>
                </KInputWrapper>
                <KInputWrapper name="digitum" v-slot="{ inputName, shouldBeVisible, shouldBeRequired }">
                    <FormulateInput
                        v-if="shouldBeVisible && digitumGroupVisible"
                        type="group"
                        :name="inputName"
                        :repeatable="true"
                        add-label="Lägg till digitum/digitus"
                        remove-label="Ta bort digitum/digitus"
                        :readonly="readonlyOperationsUppgifter"
                        remove-position="after"
                    >
                        <template #default="{ index }">
                            <KAutocomplete
                                name="id"
                                :options="digitumOptions"
                                label="Digitum/digitus"
                                :value="operationsuppgifterFormData.digitum?.[index]?.id ?? null"
                            />
                            <div v-if="index === 0" class="formulate-input">
                                <label class="formulate-input-label--default formulate-input-label">
                                    Antal digitum/digitus
                                </label>
                                <p>{{ antalDigitum }}</p>
                            </div>
                        </template>
                        <template #add-more="{ addMore }">
                            <div class="formulate-input-group-add-more">
                                <button type="button" :disabled="readonlyOperationsUppgifter" @click="addMore">
                                    <span>Lägg till digitum/digitus</span>
                                </button>
                            </div>
                        </template>
                        <template #remove="{ removeItem }">
                            <a role="button" class="formulate-input-group-repeatable-remove" @click="removeItem">
                                Ta bort digitum/digitus
                            </a>
                        </template>
                    </FormulateInput>
                </KInputWrapper>
                <KInputWrapper name="resurser" v-slot="{ inputName, shouldBeVisible, shouldBeRequired }">
                    <FormulateInput
                        v-if="shouldBeVisible"
                        type="group"
                        :name="inputName"
                        :repeatable="true"
                        add-label="Lägg till resurs"
                        remove-label="Ta bort resurs"
                        remove-position="after"
                        :readonly="readonlyOperationsUppgifter"
                        class="resurser"
                    >
                        <template #default="{ index }">
                            <KAutocomplete
                                name="id"
                                :options="fetchResurserOptions"
                                label="Resurs"
                                :value="operationsuppgifterFormData.resurser?.[index]?.id ?? null"
                            />
                        </template>
                        <template #add-more="{ addMore }">
                            <div class="formulate-input-group-add-more">
                                <button type="button" :disabled="readonlyOperationsUppgifter" @click="addMore">
                                    <span>Lägg till resurs</span>
                                </button>
                            </div>
                        </template>
                        <template #remove="{ removeItem }">
                            <a role="button" class="formulate-input-group-repeatable-remove" @click="removeItem">
                                Ta bort resurs
                            </a>
                        </template>
                    </FormulateInput>
                </KInputWrapper>
                <KInputWrapper name="implantat" v-slot="{ inputName, shouldBeVisible, shouldBeRequired }">
                    <FormulateInput
                        v-if="shouldBeVisible"
                        type="group"
                        :name="inputName"
                        :repeatable="true"
                        add-label="Lägg till implantat"
                        remove-label="Ta bort implantat"
                        :readonly="readonlyOperationsUppgifter"
                        remove-position="after"
                    >
                        <template #default="{ index }">
                            <KAutocomplete
                                name="id"
                                :options="fetchImplantatOptions"
                                label="Implantat"
                                :value="operationsuppgifterFormData.implantat?.[index]?.id ?? null"
                            />
                        </template>
                        <template #add-more="{ addMore }">
                            <div class="formulate-input-group-add-more">
                                <button type="button" :disabled="readonlyOperationsUppgifter" @click="addMore">
                                    <span>Lägg till implantat</span>
                                </button>
                            </div>
                        </template>
                        <template #remove="{ removeItem }">
                            <a role="button" class="formulate-input-group-repeatable-remove" @click="removeItem">
                                Ta bort implantat
                            </a>
                        </template>
                    </FormulateInput>
                </KInputWrapper>
                <div class="row">
                    <KInputWrapper name="anmarkning" v-slot="{ inputName, shouldBeVisible, shouldBeRequired }">
                        <FormulateInput
                            v-if="shouldBeVisible"
                            :readonly="readonlyOperationsUppgifter"
                            class="anmarkning"
                            :name="inputName"
                            type="textarea"
                            :label="`Anmärkning ${checkAddAsterisk(shouldBeRequired)}`"
                            :validation="shouldBeRequired ? 'required' : ''"
                        />
                    </KInputWrapper>
                </div>
            </FormulateForm>
            <div class="grey-div">
                <Signeringsansvarig
                    :readonly="readonlyOperationsUppgifter"
                    type="submit"
                    @emitSigneringsansvarigaAction="submitOperationsuppgifter($event, true)"
                    :isSignable="true"
                    :currentProfile="currentProfile"
                    :isSigned="operationsuppgifterFormData.is_signed"
                    :loading="saveLoading"
                    :orgUnit="operationsanmalan.operationsenhet"
                    :accountableHealthcareProfessional="operationsuppgifterFormData.accountableHealthcareProfessional"
                ></Signeringsansvarig>

                <sign-frame
                    v-if="signVisibleOperationsuppgifter"
                    title="Operationsuppgifter"
                    :url="signUrlOperationsuppgifter"
                    :data="signDataOperationsuppgifter"
                    @success="handleSignOperationsuppgifterSuccess"
                    @error="handleSignOperationsuppgifterError"
                    @cancel="handleSignOperationsuppgifterCancel"
                />
            </div>
        </KSection>

        <KSection
            name="tider"
            heading="Tider"
            :numbered="true"
            :collapsible="true"
            :expanded="sectionsExpanded"
            class="tider"
            :custom-config-provide-suffix="tiderConfigSuffix"
        >
            <FormulateForm v-model="tiderFormData" :errors="formError" autocomplete="off">
                <ModalContent
                    title="Tider förklaring"
                    show-close-button
                    v-if="tiderForklaring"
                    @close="tiderForklaring = false"
                    class="forklaring-modal"
                >
                    <dl class="forklaringar">
                        <h3>Förberedelser</h3>
                        <dd></dd>

                        <dt>Ankomsttid preopenhet</dt>
                        <dd>Tidpunkt när patient anländer till preopenhet/dagkirurgi.</dd>

                        <dt>Patienttid start</dt>
                        <dd>
                            Tidpunkt då operations/anestesipersonalen på operationssalen tar sig an/låser sig vid en
                            patient eller till patientrelaterad förberedelse.
                        </dd>

                        <dt>Uppdukning start</dt>
                        <dd>Tidpunkt då uppdukning inför operation påbörjas.</dd>

                        <dt>Uppdukning slut</dt>
                        <dd>Tidpunkt då uppdukning inför operation avslutas.</dd>

                        <dt>Antibiotikaprofylax start</dt>
                        <dd>
                            Den tidpunkt då administration av antibiotikaprofylax påbörjas. Om givet på vårdavdelning;
                            ange då uppskattad tid då det gavs.
                        </dd>

                        <dt>Preoperativ anestesi start</dt>
                        <dd>
                            Tidpunkt när preoperativa anestesiologiska ingrepp startas, exempelvis blockad börjar
                            anläggas (fylls endast i om detta sker innan "Patienttid start" har börjat registreras).
                        </dd>

                        <dt>Patient in på opsal</dt>
                        <dd>Tidpunkt då patienten förs in på operationssalen.</dd>

                        <dt>Anestesitid start</dt>
                        <dd>
                            Tidpunkt då då anestiinduktion påbörjas alternativt när lokalbedövningsmedlet injiceras vid
                            regional anestesi (vid epiduralanestesi; när testdosen ges). "Patienttid start" skall ha
                            börjat, annars skall det registreras som "Preoperativ anestesi start". Är "Preoperativ
                            anestesi start" ifylld behöver inte denna tid fyllas i.
                        </dd>

                        <dt>Anestesi klar</dt>
                        <dd>
                            Tidpunkt där tillräcklig anestesinivå har uppnåtts för att påbörja hudprepareing, KAD et.c.
                            Anestesisidan klar för operation. (EJ samma som anestesi slut)
                        </dd>

                        <dt>Operationsförberedelse klar</dt>
                        <dd>
                            Tidpunkt då det är klart för operation. Patienten huddesinfekterad och steril draperad samt
                            utrustning är på plats.
                        </dd>

                        <h3>Optid</h3>
                        <dd></dd>

                        <dt>Operation start</dt>
                        <dd>
                            Tidpunkt då operatören påbörjar operationen. Inkluderar oblodiga förberedande ingrepp, t ex
                            sluten reposition och anläggande av lokalbedövning av operatör (om detta sker efter att
                            patienttid startats).
                        </dd>

                        <dt>{{ operationXMinuterKvar }} minuter kvar</dt>
                        <dd>
                            Tidpunkt då personal på op uppskattar att det endast är {{ operationXMinuterKvar }} minuter
                            kvar av optiden.
                        </dd>

                        <dt>Operation slut</dt>
                        <dd>
                            Tidpunkt då operatören avslutar operationen alternativt tidpunkten då sista suturen är satt.
                            Inkluderar oblodiga avslutande ingrepp som utförs av operatören, t ex förbandsläggning och
                            gipsning. Förbandsläggning av operationssköterska ingår ej i operationstiden. Skall även
                            inkludera oblodiga ingrepp såsom till exempel Rtg-undersökningar, ECT mm och då relateras
                            till ingreppets slut. (SFAI-definition gäller)
                        </dd>

                        <dt>Robot in</dt>
                        <dd>tba</dd>

                        <dt>Robot ut</dt>
                        <dd>tba</dd>

                        <dt>Blodtomt fält start</dt>
                        <dd>tba</dd>

                        <dt>Blodtomt fält slut</dt>
                        <dd>tba</dd>

                        <h3>Avveckling</h3>
                        <dd></dd>

                        <dt>Anestesi slut</dt>
                        <dd>
                            Tidpunkt då patienten enligt anestesikompetent personal bedöms vara klar att lämna
                            operationssalen. (EJ samma som anestesi klar)
                        </dd>

                        <dt>Patient ut från opsal</dt>
                        <dd>Tidpunkt då patienten förs ut från opsalen.</dd>

                        <dt>Efterarbete sal slut</dt>
                        <dd>tba</dd>

                        <dt>Patienttid slut</dt>
                        <dd>
                            Tidpunkt då anestesipersonal avlämnar på postopavdelning alternativt avslutar uppgift. Tid
                            för överrapportering ingår.
                        </dd>

                        <dt>Efterarbete sal slut</dt>
                        <dd>tba</dd>
                    </dl>

                    <i>SPOR version: 4.1</i>

                    <ButtonGroup>
                        <Button type="button" size="primary" @click.prevent="tiderForklaring = false">Stäng</Button>
                    </ButtonGroup>
                </ModalContent>
                <ButtonLink type="button" class="forklaring-button" @click.prevent="tiderForklaring = true">
                    Förklaring tider
                </ButtonLink>
                <KGroup name="forberedelser" row-gap="none">
                    <h4>Förberedelser</h4>
                    <div class="wrapper-3-1">
                        <TidInput
                            :readonly="readonlyTider"
                            name="ankomsttid_preopenhet"
                            label="Ankomsttid preopenhet"
                            :submitTid="submitTid"
                        />
                        <TidInput
                            :readonly="readonlyTider"
                            name="uppdukning_start"
                            label="Uppdukning start"
                            :submitTid="submitTid"
                        />
                        <TidInput
                            :readonly="readonlyTider"
                            name="uppdukning_slut"
                            label="Uppdukning slut"
                            :submitTid="submitTid"
                        />
                        <TidInput
                            :readonly="readonlyTider"
                            name="preoperativ_anestesi_start"
                            label="Preoperativ anestesi start"
                            :submitTid="submitTid"
                        />
                        <TidInput
                            :readonly="readonlyTider"
                            :markAsRequired="true"
                            name="patienttid_start"
                            label="Patienttid start"
                            :submitTid="submitTid"
                        />
                        <TidInput
                            :readonly="readonlyTider"
                            name="antibiotikaprofylax_start"
                            label="Antibiotikaprofylax start"
                            :submitTid="submitTid"
                        />
                        <TidInput
                            :readonly="readonlyTider"
                            name="patient_in_opsal"
                            label="Patient in på opsal"
                            :submitTid="submitTid"
                        />
                    </div>
                    <div class="wrapper-3-1">
                        <TidInput
                            :readonly="readonlyTider"
                            name="anestesitid_start"
                            label="Anestesitid start"
                            :submitTid="submitTid"
                        />
                        <TidInput
                            :readonly="readonlyTider"
                            name="anestesitid_klar"
                            label="Anestesi klar"
                            :submitTid="submitTid"
                        />
                        <TidInput
                            :readonly="readonlyTider"
                            name="operationsforberedelse_klar"
                            label="Operationsförberedelse klar"
                            :submitTid="submitTid"
                        />
                    </div>
                </KGroup>

                <KGroup name="optider" row-gap="none">
                    <h4>Optid</h4>
                    <div class="wrapper-3-1">
                        <TidInput
                            :readonly="readonlyTider"
                            :markAsRequired="true"
                            name="opstart"
                            label="Operation start"
                            :submitTid="submitTid"
                        />

                        <TidInput
                            :readonly="readonlyTider"
                            name="knivtid_start"
                            label="Knivtid start"
                            :submitTid="submitTid"
                        />

                        <TidInput
                            :readonly="readonlyTider"
                            :markAsRequired="true"
                            name="x_minuter_kvar"
                            :label="`${operationXMinuterKvar} minuter kvar`"
                            :submitTid="submitTid"
                        />

                        <TidInput
                            :readonly="readonlyTider"
                            name="knivtid_slut"
                            label="Knivtid slut"
                            :submitTid="submitTid"
                        />

                        <TidInput
                            :readonly="readonlyTider"
                            :markAsRequired="true"
                            name="opslut"
                            label="Operation slut"
                            :submitTid="submitTid"
                        />

                        <!--
                            Specialstyling som vi inte kommer behöva när vi använder KForm. Görs för att grupperingen
                            nedan ska hamna på en egen rad
                         -->
                        <div style="display: grid; grid-column: 1 / -1; grid-template-columns: subgrid">
                            <KInputWrapper name="robot_in" v-slot="{ inputName, shouldBeVisible, shouldBeRequired }">
                                <FormulateInput
                                    v-if="shouldBeVisible"
                                    :required="shouldBeRequired"
                                    :readonly="readonlyTider"
                                    type="timepicker"
                                    useNow
                                    :useDropdown="false"
                                    :name="inputName"
                                    label="Robot in"
                                    @blur="submitTid(inputName)"
                                    @clicknow="submitTid(inputName)"
                                />
                            </KInputWrapper>

                            <KInputWrapper name="robot_ut" #default="{ inputName, shouldBeVisible, shouldBeRequired }">
                                <FormulateInput
                                    v-if="shouldBeVisible"
                                    :required="shouldBeRequired"
                                    :readonly="readonlyTider"
                                    type="timepicker"
                                    useNow
                                    :useDropdown="false"
                                    :name="inputName"
                                    label="Robot ut"
                                    @blur="submitTid(inputName)"
                                    @clicknow="submitTid(inputName)"
                                />
                            </KInputWrapper>

                            <TidInput
                                :readonly="readonlyTider"
                                name="blodtomt_falt_start"
                                groupName="blodtomt"
                                label="Blodtomt fält start"
                                :submitTid="submitTid"
                            />

                            <TidInput
                                :readonly="readonlyTider"
                                name="blodtomt_falt_slut"
                                groupName="blodtomt"
                                label="Blodtomt fält slut"
                                :submitTid="submitTid"
                            />
                        </div>
                    </div>
                </KGroup>

                <KGroup name="avveckling" row-gap="none">
                    <h4>Avveckling</h4>
                    <div class="wrapper-3-1">
                        <TidInput
                            :readonly="readonlyTider"
                            name="anestesi_slut"
                            label="Anestesi slut"
                            :submitTid="submitTid"
                        />
                        <TidInput
                            :readonly="readonlyTider"
                            name="patient_ut_opsal"
                            label="Patient ut från opsal"
                            :submitTid="submitTid"
                        />
                        <TidInput
                            :readonly="readonlyTider"
                            name="patienttid_slut"
                            label="Patienttid slut"
                            :submitTid="submitTid"
                            :markAsRequired="true"
                        />
                        <TidInput
                            :readonly="readonlyTider"
                            name="efterarbete_sal_slut"
                            label="Efterarbete sal slut"
                            :submitTid="submitTid"
                            :markAsRequired="true"
                        />
                    </div>
                </KGroup>

                <div class="wrapper-3-1">
                    <FormulateInput
                        :readonly="readonlyTider"
                        type="textarea"
                        name="fritext"
                        label="Kommentar tider"
                        class="fritext fritext-normal-width"
                        @blur="submitTid('fritext')"
                    ></FormulateInput>
                    <KInputWrapper name="operationstid_utrakning" v-slot="{ shouldBeVisible }">
                        <div v-if="shouldBeVisible" class="formulate-input">
                            <label class="formulate-input-label">Operationstid antal minuter</label>
                            <br />
                            <span>{{ getOperationsTidInMinute }}</span>
                        </div>
                    </KInputWrapper>
                    <KInputWrapper name="blodtomt_utrakning" groupName="blodtomt" v-slot="{ shouldBeVisible }">
                        <div v-if="shouldBeVisible" class="formulate-input">
                            <label class="formulate-input-label">Blodtomt fält antal minuter</label>
                            <br />
                            <span>{{ getBlodtomtTidInMinute }}</span>
                        </div>
                    </KInputWrapper>
                </div>
            </FormulateForm>
        </KSection>

        <ModalContent
            title="Checklista 1 - Förberedelser"
            :showCloseButton="true"
            v-if="showWHOchecklistaForberedelse"
            @close="showWHOchecklistaForberedelse = false"
            class="who-modal"
        >
            <div class="header">
                <DialogAktivPatient :patient="operationsanmalan.patient_display" />

                <div class="datum">
                    <strong>Datum</strong>
                    {{ getCurrentDate }}
                </div>

                <div class="tid">
                    <strong>Tid</strong>
                    {{ getCurrentHourMinute }}
                </div>
            </div>

            <h3>Före inledning av anestesi</h3>

            <hr />

            <FormulateForm @submit="submitWHOchecklistaForberedelse" v-model="whochecklistaForberedelseFormData">
                <FormulateInput :disabled="readonlyWhoForberwdelse" name="patient_bekraftat" type="checkbox">
                    <template #label="{ id }">
                        <div>
                            <label :for="id">Patienten har bekräftad följande:</label>
                            <ul>
                                <li>
                                    <label>identitet</label>
                                </li>
                                <li>
                                    <label>plats för incision</label>
                                </li>
                                <li>
                                    <label>informerad om och samtycker till operation</label>
                                </li>
                            </ul>
                        </div>
                    </template>
                </FormulateInput>

                <hr />

                <FormulateInput
                    :disabled="readonlyWhoForberwdelse"
                    name="operationsomrade_markerat"
                    type="checkbox"
                    label="Operationsområde markerat/ej tillämpligt"
                />

                <hr />

                <FormulateInput
                    :disabled="readonlyWhoForberwdelse"
                    name="sakerhetskontroll_for_anestesi"
                    type="checkbox"
                    label="Säkerhetskontroll för anestesi genomförd"
                />

                <hr />

                <FormulateInput
                    :disabled="readonlyWhoForberwdelse"
                    name="fungerande_pulsoximeter"
                    type="checkbox"
                    label="Fungerande pulsoximeter kopplad"
                />

                <hr />

                <p>Har patienten något av följande:</p>

                <FormulateInput
                    :disabled="readonlyWhoForberwdelse"
                    name="kand_allergi"
                    label="Känd allergi?"
                    :options="{ false: 'Nej', true: 'Ja' }"
                    type="radio"
                />

                <FormulateInput
                    :disabled="readonlyWhoForberwdelse"
                    name="risk_for_aspiration"
                    label="Risk för aspiration/svår intubation?"
                    :options="{
                        false: 'Nej',
                        true: 'Ja och utrustning/assistans är tillgänglig',
                    }"
                    type="radio"
                />

                <FormulateInput
                    :disabled="readonlyWhoForberwdelse"
                    name="risk_for_blodforlust"
                    label="Risk för >500 ml blodförlust (7 ml/kg för barn)?"
                    :options="{
                        false: 'Nej',
                        true: 'Ja, och tillfredsställande intravenösa infarter och blod/vätskor är planerade',
                    }"
                    type="radio"
                />

                <FormulateInput
                    :disabled="readonlyWhoForberwdelse"
                    name="risk_for_hypotermi"
                    label="Risk för hypotermi?"
                    :options="{
                        false: 'Nej',
                        true: 'Ja, och åtgärder är planerade/vidtagna',
                    }"
                    type="radio"
                />

                <hr />

                <i>
                    Published by the World Health Organization in 2008 under the title WHO surgical safety checklist 1st
                    edition 2008 (TR/08/216). Producerad av Landstingens Ömsesidiga Försäkringsbolag mars 2009.
                </i>
                <hr />

                <ButtonGroup>
                    <Button :disabled="readonlyWhoForberwdelse" type="submit" size="primary">Spara</Button>
                    <Button
                        :disabled="readonlyWhoForberwdelse"
                        type="button"
                        size="secondary"
                        variant="contour"
                        @click.prevent="showWHOchecklistaForberedelse = false"
                    >
                        Stäng
                    </Button>
                </ButtonGroup>
            </FormulateForm>
        </ModalContent>

        <ModalContent
            title="Checklista 2 - Timeout"
            :showCloseButton="true"
            v-if="showWHOchecklistaTimeOut"
            @close="showWHOchecklistaTimeOut = false"
            class="who-modal"
        >
            <div class="header">
                <DialogAktivPatient :patient="operationsanmalan.patient_display" />

                <div class="datum">
                    <strong>Datum</strong>
                    {{ getCurrentDate }}
                </div>

                <div class="tid">
                    <strong>Tid</strong>
                    {{ getCurrentHourMinute }}
                </div>
            </div>

            <h3>Före incision</h3>

            <hr />

            <FormulateForm v-model="whochecklistaTimeoutFormData" @submit="submitWHOchecklistaTimeOut">
                <FormulateInput
                    :disabled="readonlyWhoTimeout"
                    name="medlemmar_presenterat_sig"
                    type="checkbox"
                    label="Bekräfta att alla medlemmar i laget presenterat sig med namn och roll"
                />

                <hr />

                <FormulateInput :disabled="readonlyWhoTimeout" type="checkbox" name="bekraftar_muntligt">
                    <template #label="{ id }">
                        <div>
                            <label :for="id">
                                Anestesiolog/anestesisjuksköterska och operationssjuksköterska, operatör bekräftar
                                muntligt:
                            </label>
                            <ul>
                                <li>
                                    <label>patient ID</label>
                                </li>
                                <li>
                                    <label>plats för incision</label>
                                </li>
                                <li>
                                    <label>planerad operation</label>
                                </li>
                            </ul>
                        </div>
                    </template>
                </FormulateInput>

                <hr />

                <div class="checkbox-group">
                    <p>Väntade kritiska moment under operationen</p>

                    <FormulateInput
                        :disabled="readonlyWhoTimeout"
                        type="checkbox"
                        name="bedomning_patientspecifika_problem"
                    >
                        <template #label="{ id }">
                            <label :for="id">
                                <strong>Anestesipersonalens bedömning: </strong> finns några patientspecifika eller
                                anestesiologiska problem?
                            </label>
                        </template>
                    </FormulateInput>

                    <FormulateInput
                        :disabled="readonlyWhoTimeout"
                        type="checkbox"
                        name="bedomning_sterilitet_bekraftat"
                    >
                        <template #label="{ id }">
                            <label :for="id">
                                <strong>Operationssjuksköterskans bedömning: </strong> har sterilitet bekräftats?
                            </label>
                        </template>
                    </FormulateInput>

                    <FormulateInput
                        :disabled="readonlyWhoTimeout"
                        name="bedomning_problem_utrustning"
                        type="checkbox"
                        label="Finns några problem med utrustning eller annat?"
                    />

                    <FormulateInput
                        :disabled="readonlyWhoTimeout"
                        type="checkbox"
                        name="bedomning_korrekt_upplagd"
                        label="Är patienten korrekt upplagd?"
                    />

                    <FormulateInput :disabled="readonlyWhoTimeout" type="checkbox" name="bedomning_kritiska_moment">
                        <template #label="{ id }">
                            <label :for="id">
                                <strong>Operatörens bedömning: </strong> vilka kritiska eller oväntade moment finns,
                                operationens längd, förväntad blodförlust?
                            </label>
                        </template>
                    </FormulateInput>
                </div>

                <hr />

                <FormulateInput
                    :disabled="readonlyWhoTimeout"
                    name="antibiotikaprofylax"
                    label="Har antibiotikaprofylax givits inom de senaste 60 minuterna?"
                    :options="{
                        true: 'Ja',
                        false: 'Ej tillämplig',
                    }"
                    type="radio"
                />

                <hr />

                <FormulateInput
                    :disabled="readonlyWhoTimeout"
                    name="trombosprofylax"
                    label="Har trombosprofylax givits enligt ordination?"
                    :options="{
                        true: 'Ja',
                        false: 'Ej tillämplig',
                    }"
                    type="radio"
                />

                <hr />

                <FormulateInput
                    :disabled="readonlyWhoTimeout"
                    name="bildinformation"
                    label="Visas nödvändig bildinformation?"
                    :options="{
                        true: 'Ja',
                        false: 'Ej tillämplig',
                    }"
                    type="radio"
                />
                <hr />

                <i>
                    Published by the World Health Organization in 2008 under the title WHO surgical safety checklist 1st
                    edition 2008 (TR/08/216). Producerad av Landstingens Ömsesidiga Försäkringsbolag mars 2009.
                </i>

                <ButtonGroup>
                    <Button :disabled="readonlyWhoTimeout" type="submit" size="primary">Spara</Button>
                    <Button
                        :disabled="readonlyWhoTimeout"
                        type="button"
                        size="secondary"
                        variant="contour"
                        @click.prevent="showWHOchecklistaTimeOut = false"
                    >
                        Stäng
                    </Button>
                </ButtonGroup>
            </FormulateForm>
        </ModalContent>

        <ModalContent
            title="Checklista 3 - Avslutning"
            :showCloseButton="true"
            v-if="showWHOchecklistaAvslutning"
            @close="showWHOchecklistaAvslutning = false"
            class="who-modal"
        >
            <div class="header">
                <DialogAktivPatient :patient="operationsanmalan.patient_display" />

                <div class="datum">
                    <strong>Datum</strong>
                    {{ getCurrentDate }}
                </div>

                <div class="tid">
                    <strong>Tid</strong>
                    {{ getCurrentHourMinute }}
                </div>
            </div>

            <h3>Innan patienten lämnar operationssalen</h3>

            <hr />

            <FormulateForm v-model="whochecklistaAvslutningFormData" @submit="submitWHOchecklistaAvslutning">
                <div class="checkbox-group">
                    <p>Checklisteansvarig får muntlig bekräftelse av laget:</p>

                    <FormulateInput
                        :disabled="readonlyWhoAvslutning"
                        name="vilket_ingrepp"
                        type="checkbox"
                        label="Vilket ingrepp har utförts"
                    />

                    <FormulateInput
                        :disabled="readonlyWhoAvslutning"
                        name="antal_instrument_torkar_nalar"
                        type="checkbox"
                        label="Att antal instrument, torkar och nålar stämmer (eller ej tillämpligt)"
                    />

                    <FormulateInput
                        :disabled="readonlyWhoAvslutning"
                        name="preparat_markta"
                        type="checkbox"
                        label="Hur preparat är märkta (inklusive patientens namn och personnummer)"
                    />

                    <FormulateInput
                        :disabled="readonlyWhoAvslutning"
                        name="problem_utrustning_uppmarksammas"
                        type="checkbox"
                        label="Finns problem med utrustningen som behöver uppmärksammas?"
                    />
                </div>

                <hr />

                <FormulateInput
                    :disabled="readonlyWhoAvslutning"
                    name="huvudpunkter_postoperativt_omhandertagande"
                    type="checkbox"
                >
                    <template #label="{ id }">
                        <div class="checkbox-texts-wrapper">
                            <label :for="id">
                                Operatör, anestesiläkare/sjuksköterska och operationssjuksköterska går igenom
                                huvudpunkterna för det initiala postoperativa omhändertagandet
                            </label>
                        </div>
                    </template>
                </FormulateInput>

                <hr />

                <FormulateInput
                    :disabled="readonlyWhoAvslutning"
                    name="vad_kan_vi_lara"
                    type="checkbox"
                    label="Vad kan vi lära? Vad kan vi göra bättre nästa gång?"
                />
                <hr />

                <i
                    >Published by the World Health Organization in 2008 under the title WHO surgical safety checklist
                    1st edition 2008 (TR/08/216). Producerad av Landstingens Ömsesidiga Försäkringsbolag mars 2009.
                </i>
                <ButtonGroup>
                    <Button :disabled="readonlyWhoAvslutning" type="submit" size="primary">Spara</Button>
                    <Button
                        :disabled="readonlyWhoAvslutning"
                        type="button"
                        size="secondary"
                        variant="contour"
                        @click.prevent="showWHOchecklistaAvslutning = false"
                    >
                        Stäng
                    </Button>
                </ButtonGroup>
            </FormulateForm>
        </ModalContent>

        <KSection name="who_checklista" heading="WHO-checklista" :numbered="true" :expanded="sectionsExpanded">
            <div class="who-checklista link-section">
                <p>Checklista för säkerhet vid operationer</p>

                <div class="two-elements-grid">
                    <ButtonLink type="button" @click.prevent="openWhoForberedelse">
                        Checklista 1 - Förberedelse
                        <small>
                            <span>Före inledning av anestesi</span>
                            <span class="updated" v-if="!isEmpty(whochecklistaForberedelseFormData.updated_by)">
                                Sparad {{ moment(whochecklistaForberedelseFormData.updated_at).format("YYYY-MM-DD") }}
                            </span>
                        </small>
                    </ButtonLink>

                    <FormulateInput
                        v-model="whochecklistaForberedelseFormData.is_completed"
                        class="bg-grey checkbox-green"
                        type="checkbox"
                        label="Checklista förberedelse genomförd"
                        @click="completeWhochecklistaForberedelse"
                    />
                </div>

                <div class="two-elements-grid">
                    <ButtonLink type="button" @click.prevent="openWhoTimeout">
                        Checklista 2 - Timeout
                        <small>
                            <span>Före incision</span>
                            <span class="updated" v-if="!isEmpty(whochecklistaTimeoutFormData.updated_by)">
                                Sparad {{ moment(whochecklistaTimeoutFormData.updated_at).format("YYYY-MM-DD") }}
                            </span>
                        </small>
                    </ButtonLink>

                    <FormulateInput
                        v-model="whochecklistaTimeoutFormData.is_completed"
                        class="bg-grey checkbox-green"
                        type="checkbox"
                        label="Checklista timeout genomförd"
                        @click="completeWhochecklistaTimeout"
                    />
                </div>

                <div class="two-elements-grid">
                    <ButtonLink type="button" @click.prevent="openWhoAvslutning">
                        Checklista 3 - Avslutning
                        <small>
                            <span>Innan patienten lämnar operationssalen</span>
                            <span class="updated" v-if="!isEmpty(whochecklistaAvslutningFormData.updated_by)">
                                Sparad {{ moment(whochecklistaAvslutningFormData.updated_at).format("YYYY-MM-DD") }}
                            </span>
                        </small>
                    </ButtonLink>

                    <FormulateInput
                        v-model="whochecklistaAvslutningFormData.is_completed"
                        type="checkbox"
                        class="bg-grey checkbox-green"
                        label="Checklista avslutning genomförd"
                        @click="completeWhochecklistaAvslutning"
                    />
                </div>
            </div>
        </KSection>

        <KSection
            name="personal"
            heading="Personal"
            :numbered="true"
            :collapsible="true"
            :expanded="sectionsExpanded"
            class="personal"
            :custom-config-provide-suffix="personalConfigSuffix"
        >
            <FormulateForm
                :readonly="readonlyPersonal"
                @submit="submitPersonal"
                v-model="personalFormData"
                :errors="formError"
                ref="personalFormRef"
                autocomplete="off"
            >
                <div class="wrapper-3-1">
                    <KInputWrapper name="huvudoperator" v-slot="{ inputName, shouldBeVisible, shouldBeRequired }">
                        <FormulateInput
                            :readonly="readonlyPersonal"
                            label="Huvudoperatör *"
                            :name="inputName"
                            validation="required"
                            validation-name="Huvudoperatör"
                            type="vueselect"
                            :options="operatorer"
                            @search="(search, loading) => searchOperatorer(search, loading)"
                            @input="removeError('huvudoperator')"
                        />
                    </KInputWrapper>
                    <KInputWrapper name="huvudoperator_intid" v-slot="{ inputName, shouldBeVisible, shouldBeRequired }">
                        <FormulateInput
                            v-if="shouldBeVisible"
                            :readonly="readonlyPersonal"
                            :label="`Tid in ${checkAddAsterisk(shouldBeRequired)}`"
                            type="timepicker"
                            useNow
                            :name="inputName"
                            :validation="shouldBeRequired ? 'required' : ''"
                            validation-name="Tid in"
                        />
                    </KInputWrapper>
                    <KInputWrapper name="huvudoperator_uttid" v-slot="{ inputName, shouldBeVisible, shouldBeRequired }">
                        <FormulateInput
                            v-if="shouldBeVisible"
                            :readonly="readonlyPersonal"
                            :label="`Tid ut ${checkAddAsterisk(shouldBeRequired)}`"
                            type="timepicker"
                            useNow
                            :name="inputName"
                            :validation="shouldBeRequired ? 'required' : ''"
                            validation-name="Tid ut"
                        />
                    </KInputWrapper>
                </div>
                <KInputWrapper name="ass_operatorer" v-slot="{ inputName, shouldBeVisible, shouldBeRequired }">
                    <FormulateInput
                        v-if="shouldBeVisible"
                        :readonly="readonlyPersonal"
                        type="group"
                        :name="inputName"
                        :repeatable="true"
                        add-label="Lägg till fler ass operatörer"
                        remove-position="after"
                    >
                        <template #default="{ index }">
                            <FormulateInput
                                :readonly="readonlyPersonal"
                                :label="`Assisterande operatör ${checkAddAsterisk(shouldBeRequired)}`"
                                type="vueselect"
                                name="profile"
                                :validation="shouldBeRequired ? 'required' : ''"
                                validation-name="Assisterande operatör"
                                :options="ass_operatorer"
                                @input="personalChanged(index, 'ass_operatorer')"
                            />
                            <FormulateInput
                                :readonly="readonlyPersonal"
                                :label="`Tid in ${checkAddAsterisk(shouldBeRequired)}`"
                                type="timepicker"
                                :validation="shouldBeRequired ? 'required' : ''"
                                validation-name="Tid in"
                                useNow
                                name="intid"
                            />
                            <FormulateInput
                                :readonly="readonlyPersonal"
                                :label="`Tid ut ${checkAddAsterisk(shouldBeRequired)}`"
                                type="timepicker"
                                useNow
                                name="uttid"
                                :validation="shouldBeRequired ? 'required' : ''"
                                validation-name="Tid ut"
                            />
                        </template>
                        <template #remove="{ index, removeItem }">
                            <a
                                role="button"
                                class="formulate-input-group-repeatable-remove"
                                @click="removeOpassistenter(index, removeItem)"
                            >
                                Radera fält
                            </a>
                        </template>

                        <template #addMore="{ addMore }">
                            <div class="formulate-input-group-add-more">
                                <button
                                    type="button"
                                    data-minor=""
                                    data-ghost=""
                                    @click="addMoreAssOperatorer(addMore)"
                                >
                                    <span class="formulate-input-element--button--label"
                                        >Lägg till fler opassistenter</span
                                    >
                                </button>
                            </div>
                        </template>
                    </FormulateInput>
                </KInputWrapper>
                <div class="wrapper-3-1">
                    <KInputWrapper name="anestesilakare" v-slot="{ inputName, shouldBeVisible, shouldBeRequired }">
                        <FormulateInput
                            v-if="shouldBeVisible"
                            :readonly="readonlyPersonal"
                            :name="inputName"
                            :validation="shouldBeRequired ? 'required' : ''"
                            validation-name="Anestesiläkare"
                            :label="`Anestesiläkare ${checkAddAsterisk(shouldBeRequired)}`"
                            type="vueselect"
                            :options="anestesilakare"
                            @search="searchAnestesilakare"
                            @input="removeError('anestesilakare')"
                        />
                    </KInputWrapper>
                    <KInputWrapper
                        name="anestesilakare_intid"
                        v-slot="{ inputName, shouldBeVisible, shouldBeRequired }"
                    >
                        <FormulateInput
                            v-if="shouldBeVisible"
                            :readonly="readonlyPersonal"
                            :label="`Tid in ${checkAddAsterisk(shouldBeRequired)}`"
                            type="timepicker"
                            useNow
                            :name="inputName"
                            :validation="shouldBeRequired ? 'required' : ''"
                            validation-name="Tid in"
                        />
                    </KInputWrapper>
                    <KInputWrapper
                        name="anestesilakare_uttid"
                        v-slot="{ inputName, shouldBeVisible, shouldBeRequired }"
                    >
                        <FormulateInput
                            v-if="shouldBeVisible"
                            :readonly="readonlyPersonal"
                            :label="`Tid ut ${checkAddAsterisk(shouldBeRequired)}`"
                            type="timepicker"
                            useNow
                            :name="inputName"
                            :validation="shouldBeRequired ? 'required' : ''"
                            validation-name="Tid ut"
                        />
                    </KInputWrapper>
                </div>
                <KInputWrapper name="anestesisjukskoterskor" v-slot="{ inputName, shouldBeVisible, shouldBeRequired }">
                    <FormulateInput
                        v-if="shouldBeVisible"
                        :readonly="readonlyPersonal"
                        type="group"
                        :name="inputName"
                        :repeatable="true"
                        add-label="Lägg till fler anestesisjuksköterskor"
                        remove-label="Radera fält"
                        remove-position="after"
                    >
                        <template #default="{ index }">
                            <FormulateInput
                                :readonly="readonlyPersonal"
                                :validation="shouldBeRequired ? 'required' : ''"
                                validation-name="Anestesisjuksköterska"
                                name="profile"
                                :label="`Anestesisjuksköterska ${checkAddAsterisk(shouldBeRequired)}`"
                                type="vueselect"
                                :options="anestesisjukskoterskor"
                                @search="(search, loading) => searchAnestesisjukskoterska(index, search, loading)"
                            />

                            <FormulateInput
                                :readonly="readonlyPersonal"
                                :label="`Tid in ${checkAddAsterisk(shouldBeRequired)}`"
                                :validation="shouldBeRequired ? 'required' : ''"
                                validation-name="Tid in"
                                type="timepicker"
                                useNow
                                name="intid"
                            />

                            <FormulateInput
                                :readonly="readonlyPersonal"
                                :label="`Tid ut ${checkAddAsterisk(shouldBeRequired)}`"
                                :validation="shouldBeRequired ? 'required' : ''"
                                validation-name="Tid ut"
                                type="timepicker"
                                useNow
                                name="uttid"
                            />
                        </template>
                    </FormulateInput>
                </KInputWrapper>
                <KInputWrapper
                    name="operationssjukskoterskor"
                    v-slot="{ inputName, shouldBeVisible, shouldBeRequired }"
                >
                    <FormulateInput
                        v-if="shouldBeVisible"
                        :readonly="readonlyPersonal"
                        type="group"
                        :name="inputName"
                        :repeatable="true"
                        add-label="Lägg till fler opsjuksköterskor"
                        remove-position="after"
                    >
                        <template #default>
                            <FormulateInput
                                :readonly="readonlyPersonal"
                                type="vueselect"
                                :label="`Operationssjuksköterska ${checkAddAsterisk(shouldBeRequired)}`"
                                :options="operationssjukskoterskor"
                                name="profile"
                                :validation="shouldBeRequired ? 'required' : ''"
                                validation-name="Operationssjuksköterska"
                                @input="removeError('operationssjukskoterskor')"
                            />
                            <FormulateInput
                                :readonly="readonlyPersonal"
                                :label="`Tid in ${checkAddAsterisk(shouldBeRequired)}`"
                                type="timepicker"
                                useNow
                                name="intid"
                                :validation="shouldBeRequired ? 'required' : ''"
                                validation-name="Tid in"
                            />
                            <FormulateInput
                                :readonly="readonlyPersonal"
                                :label="`Tid ut ${checkAddAsterisk(shouldBeRequired)}`"
                                type="timepicker"
                                useNow
                                name="uttid"
                                :validation="shouldBeRequired ? 'required' : ''"
                                validation-name="Tid ut"
                            />
                        </template>
                        <template #remove="{ index, removeItem }">
                            <a
                                role="button"
                                class="formulate-input-group-repeatable-remove"
                                @click="removeOperationssjukskoterska(index, removeItem)"
                            >
                                Radera fält
                            </a>
                        </template>

                        <template #addMore="{ addMore }">
                            <div class="formulate-input-group-add-more">
                                <button
                                    type="button"
                                    data-minor=""
                                    data-ghost=""
                                    @click="addMoreOperationssjukskoterska(addMore)"
                                >
                                    <span class="formulate-input-element--button--label">
                                        Lägg till fler opsjuksköterskor
                                    </span>
                                </button>
                            </div>
                        </template>
                    </FormulateInput>
                </KInputWrapper>
                <KInputWrapper
                    name="operationsunderskoterskor"
                    v-slot="{ inputName, shouldBeVisible, shouldBeRequired }"
                >
                    <FormulateInput
                        v-if="shouldBeVisible"
                        :readonly="readonlyPersonal"
                        type="group"
                        :name="inputName"
                        :repeatable="true"
                        add-label="Lägg till fler undersköterskor"
                        remove-position="after"
                    >
                        <template #default="{ index }">
                            <FormulateInput
                                :readonly="readonlyPersonal"
                                name="profile"
                                :validation="shouldBeRequired ? 'required' : ''"
                                validation-name="Undersköterska"
                                :label="`Undersköterska ${checkAddAsterisk(shouldBeRequired)}`"
                                type="vueselect"
                                @input="personalChanged(index, 'operationsunderskoterskor')"
                                :options="operationsunderskoterskor"
                            />
                            <FormulateInput
                                :readonly="readonlyPersonal"
                                :label="`Tid in ${checkAddAsterisk(shouldBeRequired)}`"
                                type="timepicker"
                                useNow
                                name="intid"
                                :validation="shouldBeRequired ? 'required' : ''"
                                validation-name="Tid in"
                            />
                            <FormulateInput
                                :readonly="readonlyPersonal"
                                :label="`Tid ut ${checkAddAsterisk(shouldBeRequired)}`"
                                type="timepicker"
                                useNow
                                name="uttid"
                                :validation="shouldBeRequired ? 'required' : ''"
                                validation-name="Tid ut"
                            />
                        </template>
                        <template #remove="{ index, removeItem }">
                            <a
                                role="button"
                                class="formulate-input-group-repeatable-remove"
                                @click="removeOperationsunderskoterska(index, removeItem)"
                            >
                                Radera fält
                            </a>
                        </template>

                        <template #addMore="{ addMore }">
                            <div class="formulate-input-group-add-more">
                                <button
                                    type="button"
                                    data-minor=""
                                    data-ghost=""
                                    @click="addMoreOperationsunderskoterska(addMore)"
                                >
                                    <span class="formulate-input-element--button--label"
                                        >Lägg till fler undersköterskor</span
                                    >
                                </button>
                            </div>
                        </template>
                    </FormulateInput>
                </KInputWrapper>
                <div class="gast-pa-sal-section">
                    <KInputWrapper name="gast_pa_sal" v-slot="{ inputName }">
                        <FormulateInput
                            class="gast-pa-sal"
                            :disabled="readonlyPersonal"
                            type="checkbox"
                            :name="inputName"
                            label="Gäst på sal"
                            @change="toggleGastPaSal"
                        />
                    </KInputWrapper>
                    <KInputWrapper name="gaster" v-slot="{ inputName, shouldBeVisible, shouldBeRequired }">
                        <FormulateInput
                            v-if="shouldBeVisible"
                            class="gaster"
                            :readonly="readonlyPersonal"
                            :name="inputName"
                            type="group"
                            :repeatable="personalFormData.gast_pa_sal"
                            add-label="Lägg till gäst"
                            remove-label="Radera fält"
                            remove-position="after"
                            :disabled="!personalFormData.gast_pa_sal"
                        >
                            <template #default="{ index }">
                                <FormulateInput
                                    :readonly="readonlyPersonal"
                                    name="namn"
                                    :validation="personalFormData.gast_pa_sal ? 'required' : ''"
                                    validation-name="Namn gäst"
                                    :label="`Namn gäst ${checkAddAsterisk(personalFormData.gast_pa_sal)}`"
                                    :required="personalFormData.gast_pa_sal"
                                    :disabled="!personalFormData.gast_pa_sal"
                                    @input="gasterChanged(index, 'namn')"
                                />
                                <FormulateInput
                                    :readonly="readonlyPersonal"
                                    name="roll"
                                    :validation="personalFormData.gast_pa_sal ? 'required' : ''"
                                    validation-name="Roll gäst"
                                    :label="`Roll gäst ${checkAddAsterisk(personalFormData.gast_pa_sal)}`"
                                    :required="personalFormData.gast_pa_sal"
                                    :disabled="!personalFormData.gast_pa_sal"
                                    @input="gasterChanged(index, 'roll')"
                                />
                            </template>
                        </FormulateInput>
                    </KInputWrapper>
                </div>
                <div class="wrapper-3-1">
                    <KInputWrapper name="antal_dorroppningar" v-slot="{ inputName, shouldBeVisible, shouldBeRequired }">
                        <KNumber
                            v-if="shouldBeVisible"
                            :name="inputName"
                            label="Antal dörröppningar"
                            :readonly="readonlyPersonal"
                            :validation="[
                                [NumberValidationRule.Decimals, 0],
                                [NumberValidationRule.Min, 0],
                                shouldBeRequired ? 'required' : '',
                            ]"
                            validation-name="Antal dörröppningar"
                        />
                    </KInputWrapper>
                </div>
                <KInputWrapper name="fritext" v-slot="{ inputName, shouldBeVisible, shouldBeRequired }">
                    <FormulateInput
                        v-if="shouldBeVisible"
                        :readonly="readonlyPersonal"
                        type="textarea"
                        :name="inputName"
                        :validation="shouldBeRequired ? 'required' : ''"
                        validation-name="Kommentar personal"
                        :label="`Kommentar personal ${checkAddAsterisk(shouldBeRequired)}`"
                        class="fritext"
                    ></FormulateInput>
                </KInputWrapper>
            </FormulateForm>
            <div class="grey-div">
                <Signeringsansvarig
                    @emitSigneringsansvarigaAction="submitPersonal($event, true)"
                    :readonly="readonlyPersonal"
                    :isSignable="true"
                    :currentProfile="currentProfile"
                    :isSigned="personalFormData.is_signed"
                    :loading="saveLoading"
                    :orgUnit="operationsanmalan.operationsenhet"
                    :accountableHealthcareProfessional="personalFormData.accountableHealthcareProfessional"
                ></Signeringsansvarig>

                <sign-frame
                    v-if="signVisiblePersonal"
                    title="Personal"
                    :url="signUrlPersonal"
                    :data="signDataPersonal"
                    @success="handleSignPersonalSuccess"
                    @error="handleSignPersonalError"
                    @cancel="handleSignPersonalCancel"
                />
            </div>
        </KSection>

        <KSection
            name="anestesi"
            heading="Anestesi"
            :collapsible="true"
            :expanded="sectionsExpanded"
            class="anestesi"
            :custom-config-provide-suffix="anestesiSuffix"
        >
            <FormulateForm
                :readonly="readonlyAnestesi"
                @submit="submitAnestesi"
                v-model="anestesiFormData"
                :errors="formError"
                ref="anestesiFormRef"
                autocomplete="off"
            >
                <div class="huvudanestesikod">
                    <KInputWrapper name="huvudanestesikod" v-slot="{ inputName, shouldBeVisible, shouldBeRequired }">
                        <FormulateInput
                            v-if="shouldBeVisible"
                            :readonly="readonlyAnestesi"
                            :id="inputName"
                            :name="inputName"
                            :validation="shouldBeRequired ? 'required' : ''"
                            validation-name="Huvudanestesikod"
                            :label="`Huvudanestesikod ${checkAddAsterisk(shouldBeRequired)}`"
                            type="vueselect"
                            :options="huvudanestesikoder"
                            @search="searchHuvudanestesikod"
                            v-on:input="huvudanestesikodChanged()"
                            class="autocomplete"
                        />
                    </KInputWrapper>
                </div>
                <KInputWrapper name="bianestesikoder" v-slot="{ inputName, shouldBeVisible, shouldBeRequired }">
                    <FormulateInput
                        v-if="shouldBeVisible"
                        type="group"
                        :name="inputName"
                        :repeatable="true"
                        add-label="Lägg till bianestesikod"
                        remove-position="after"
                    >
                        <template #default="{ index }">
                            <FormulateInput
                                :readonly="readonlyAnestesi"
                                name="kod"
                                :validation="shouldBeRequired ? 'required' : ''"
                                validation-name="Bianestesikod"
                                :label="`Bianestesikod ${checkAddAsterisk(shouldBeRequired)}`"
                                type="vueselect"
                                :options="bianestesikoder[index]"
                                @search="(search, loading) => searchBianestesikoder(index, search, loading)"
                                class="autocomplete"
                            />
                        </template>
                        <template #remove="{ index, removeItem }">
                            <a
                                role="button"
                                class="formulate-input-group-repeatable-remove"
                                @click="removeBianestesikoder(index, removeItem)"
                            >
                                Radera fält
                            </a>
                        </template>

                        <template #addMore="{ addMore }">
                            <div class="formulate-input-group-add-more">
                                <button
                                    type="button"
                                    data-minor=""
                                    data-ghost=""
                                    @click="addMoreBianestesikoder(addMore)"
                                >
                                    <span class="formulate-input-element--button--label"
                                        >Lägg till fler bianestesikoder</span
                                    >
                                </button>
                            </div>
                        </template>
                    </FormulateInput>
                </KInputWrapper>
                <ModalContent
                    title="Komplikationsgrad förklaring"
                    show-close-button
                    v-if="anestesiForklaring"
                    @close="anestesiForklaring = false"
                    class="forklaring-modal"
                >
                    <dl class="forklaringar">
                        <template v-for="grad in anestesikomplikationSvarighetsgrader">
                            <dt>{{ grad.code }}</dt>
                            <dd>{{ grad.displayName }}</dd>
                        </template>
                    </dl>

                    <i> {{ anestesikomplikationSvarighetsgraderVersioner }} </i>

                    <ButtonGroup>
                        <Button type="button" size="primary" @click.prevent="anestesiForklaring = false">
                            Stäng
                        </Button>
                    </ButtonGroup>
                </ModalContent>
                <KInputWrapper name="anestesikomplikationer" v-slot="{ inputName, shouldBeVisible, shouldBeRequired }">
                    <FormulateInput
                        v-if="shouldBeVisible"
                        type="group"
                        :name="inputName"
                        :repeatable="true"
                        add-label="Lägg till fler komplikationer"
                        remove-position="after"
                        class="anestesikomplikationer group-add-button-below"
                    >
                        <template #default="{ index }">
                            <FormulateInput
                                :readonly="readonlyAnestesi"
                                id="anestesikomplikationer-kategori"
                                name="kategori"
                                :validation="shouldBeRequired ? 'required' : ''"
                                validation-name="Anestesikomplikation kategori"
                                :label="`Anestesikomplikation kategori ${checkAddAsterisk(shouldBeRequired)}`"
                                type="vueselect"
                                :options="anestesikomplikationerKategorier"
                                @option:selected="anestesikomplikationerKategorierChanged(index)"
                                @input="anestesikomplikationChanged(index, $event)"
                            />
                            <FormulateInput
                                :readonly="readonlyAnestesi"
                                id="anestesikomplikationer-kod"
                                :label="`Anestesikomplikation ${checkAddAsterisk(shouldBeRequired)}`"
                                validation-name="Anestesikomplikation"
                                :validation="shouldBeRequired ? 'required' : ''"
                                name="kod"
                                type="vueselect"
                                :options="anestesikomplikationer[index]"
                                @input="anestesikomplikationerErrorChanged('kod', index)"
                            >
                            </FormulateInput>

                            <FormulateInput
                                :readonly="readonlyAnestesi"
                                id="anestesikomplikationer-svarighetsgrad"
                                name="svarighetsgrad"
                                :validation="shouldBeRequired ? 'required' : ''"
                                validation-name="Svårighetsgrad"
                                :label="`Svårighetsgrad ${checkAddAsterisk(shouldBeRequired)}`"
                                type="vueselect"
                                :options="anestesikomplikationSvarighetsgraderOptions"
                                @input="anestesikomplikationerErrorChanged('svarighetsgrad', index)"
                            >
                                <ButtonLink
                                    type="button"
                                    class="forklaring-button forklaring-button-anestesi"
                                    @click.prevent="anestesiForklaring = true"
                                >
                                    Förklaring
                                </ButtonLink>
                            </FormulateInput>

                            <FormulateInput
                                :readonly="readonlyAnestesi"
                                id="anestesikomplikationer-tidpunkt"
                                name="tidpunkt"
                                label="Tidpunkt för komplikation"
                                validation-name="Tidpunkt för komplikation"
                                type="timepicker"
                                useNow
                            />
                        </template>
                        <template #remove="{ index, removeItem }">
                            <a
                                role="button"
                                class="formulate-input-group-repeatable-remove"
                                @click="removeAnestesikomplikationer(index, removeItem)"
                            >
                                Radera fält
                            </a>
                        </template>
                    </FormulateInput>
                </KInputWrapper>
                <KInputWrapper name="fritext" v-slot="{ inputName, shouldBeVisible, shouldBeRequired }">
                    <FormulateInput
                        v-if="shouldBeVisible"
                        :name="inputName"
                        :label="`Kommentar anestesi ${checkAddAsterisk(shouldBeRequired)}`"
                        :validation="shouldBeRequired ? 'required' : ''"
                        validation-name="Kommentar anestesi"
                        type="textarea"
                        class="fritext"
                        :readonly="readonlyAnestesi"
                    />
                </KInputWrapper>
            </FormulateForm>
            <div class="grey-div">
                <Signeringsansvarig
                    :readonly="readonlyAnestesi"
                    class=""
                    type="submit"
                    @emitSigneringsansvarigaAction="submitAnestesi($event, true)"
                    :isSignable="true"
                    :currentProfile="currentProfile"
                    :isSigned="anestesiFormData.is_signed"
                    :loading="saveLoading"
                    :orgUnit="operationsanmalan.operationsenhet"
                    :accountableHealthcareProfessional="anestesiFormData.accountableHealthcareProfessional"
                ></Signeringsansvarig>

                <sign-frame
                    v-if="signVisibleAnestesi"
                    title="Anestesi"
                    :url="signUrlAnestesi"
                    :data="signDataAnestesi"
                    @success="handleSignAnestesiSuccess"
                    @error="handleSignAnestesiError"
                    @cancel="handleSignAnestesiCancel"
                />
            </div>
        </KSection>

        <KSection
            name="material"
            heading="Material"
            :numbered="true"
            :collapsible="true"
            :expanded="sectionsExpanded"
            class="material"
            :custom-config-provide-suffix="materialSuffix"
        >
            <FormulateForm
                :readonly="readonlyMaterial"
                @submit="submitMaterial"
                v-model="materialFormData"
                :errors="formError"
                ref="materialFormRef"
                autocomplete="off"
            >
                <MaterialScanner @selected="addMaterial" />
                <KInputWrapper name="material" v-slot="{ inputName, shouldBeVisible, shouldBeRequired }">
                    <FormulateInput
                        v-if="materialFormData.material?.length > 0 && shouldBeVisible"
                        type="group"
                        :name="inputName"
                        :repeatable="true"
                        remove-position="after"
                    >
                        <template #default="{ index }">
                            <FormulateInput readonly label="Artikelnummer" name="artikelnummer" type="text">
                                <!-- TODO Inte en optimal lösning. Väntar till Formkit för en bättre lösning-->
                                <template v-if="!uniqueMaterial.get(index)" #suffix>
                                    <div>
                                        <img src="@/assets/info_gray.svg" :style="{ height: '0.8rem' }" />
                                        <span :style="{ fontSize: '0.8rem' }">
                                            Artikeln används redan. Justera antalet i tidigare fält eller ange ny batch
                                            här.
                                        </span>
                                    </div>
                                </template>
                            </FormulateInput>

                            <FormulateInput
                                readonly
                                class="second"
                                label="Materialnamn"
                                name="materialnamn"
                                type="text"
                            />
                            <FormulateInput
                                :readonly="readonlyMaterial"
                                class="third"
                                :label="`Batchnummer ${checkAddAsterisk(shouldBeRequired)}`"
                                :validation="shouldBeRequired ? 'required' : ''"
                                validation-name="Batchnummer"
                                name="batchnummer"
                                type="text"
                            />
                            <FormulateInput
                                :readonly="readonlyMaterial"
                                class="fourth"
                                :label="`Antal ${checkAddAsterisk(shouldBeRequired)}`"
                                :validation="shouldBeRequired ? 'required' : ''"
                                validation-name="Antal"
                                name="antal"
                                type="knumber"
                                :ref="`antal-${index}`"
                            />
                        </template>
                        <template #addmore>
                            <!-- Nya rader läggs in via MaterialScanner -->
                            <div />
                        </template>
                        <template #remove="{ index, removeItem }">
                            <!-- Override på styling, eftersom remove ska synas på första raden -->
                            <a
                                role="button"
                                class="formulate-input-group-repeatable-remove"
                                :style="{
                                    opacity: 1,
                                    maxWidth: 'fit-content',
                                    minWidth: 'unset',
                                }"
                                @click="removeItem(index)"
                            >
                                Radera fält
                            </a>
                        </template>
                    </FormulateInput>
                </KInputWrapper>
                <KInputWrapper name="fritext" v-slot="{ inputName, shouldBeVisible, shouldBeRequired }">
                    <FormulateInput
                        v-if="shouldBeVisible"
                        :readonly="readonlyMaterial"
                        type="textarea"
                        :name="inputName"
                        :validation="shouldBeRequired ? 'required' : ''"
                        validation-name="Kommentar material"
                        :label="`Kommentar material ${checkAddAsterisk(shouldBeRequired)}`"
                        class="fritext"
                    ></FormulateInput>
                </KInputWrapper>
            </FormulateForm>
            <div class="grey-div">
                <Signeringsansvarig
                    :readonly="readonlyMaterial"
                    type="submit"
                    @emitSigneringsansvarigaAction="submitMaterial($event, true)"
                    :isSignable="true"
                    :currentProfile="currentProfile"
                    :isSigned="materialFormData.is_signed"
                    :loading="saveLoading"
                    :orgUnit="operationsanmalan.operationsenhet"
                    :accountableHealthcareProfessional="materialFormData.accountableHealthcareProfessional"
                ></Signeringsansvarig>

                <sign-frame
                    v-if="signVisibleMaterial"
                    title="Material"
                    :url="signUrlMaterial"
                    :data="signDataMaterial"
                    @success="handleSignMaterialSuccess"
                    @error="handleSignMaterialError"
                    @cancel="handleSignMaterialCancel"
                />
            </div>
        </KSection>

        <KSection
            name="patientdata"
            heading="Patientdata"
            number=""
            :collapsible="true"
            :expanded="sectionsExpanded"
            class="patientdata"
        >
            <KForm
                ref="patientdataFormRef"
                id="operationjournal-patientdata-form"
                :readonly="readonlyPatientData"
                :config-module-name="patientdataModuleName"
                :config-sub-module-name="patientdataSubModuleName"
                :schema="patientDataSchema"
                :errors="formError"
                :initial-values-getter="fetchPatientFormData"
                :schemaTriggers="patientDataFormTriggers"
            >
            </KForm>

            <div class="grey-div">
                <Signeringsansvarig
                    :readonly="readonlyPatientData"
                    type="submit"
                    @emitSigneringsansvarigaAction="submitPatientdata"
                    :isSignable="true"
                    :currentProfile="currentProfile"
                    :isSigned="patientdataFormData.is_signed"
                    :loading="saveLoading"
                    :orgUnit="operationsanmalan.operationsenhet"
                    :accountableHealthcareProfessional="patientdataFormData.accountableProfessional"
                ></Signeringsansvarig>
                <sign-frame
                    v-if="signVisiblePatientdata"
                    title="patientdata"
                    :url="signUrlPatientdata"
                    :data="signDataPatientdata"
                    @success="handleSignPatientdataSuccess"
                    @error="handleSignPatientdataError"
                    @cancel="handleSignPatientdataCancel"
                />
            </div>
        </KSection>

        <KSection
            name="postoperativ_overvakning"
            heading="Postoperativ övervakning"
            :numbered="true"
            :collapsible="true"
            :expanded="sectionsExpanded"
            class="postoperativ-overvakning"
            :custom-config-provide-suffix="postoperativOvervakningSuffix"
        >
            <FormulateForm
                @submit="submitPostoperativ"
                v-model="postoperativOvervakningFormData"
                :errors="formError"
                :readonly="readonlyPostoperativ"
                ref="postoperativFormRef"
                autocomplete="off"
            >
                <div>
                    <div class="wrapper-3-1">
                        <KInputWrapper name="tid_in" v-slot="{ inputName, shouldBeVisible, shouldBeRequired }">
                            <FormulateInput
                                v-if="shouldBeVisible"
                                :validation="shouldBeRequired ? 'required' : ''"
                                validation-name="Patient överlämnad till postop"
                                type="timepicker"
                                useNow
                                :useDropdown="false"
                                :name="inputName"
                                :label="`Patient överlämnad till postop ${checkAddAsterisk(shouldBeRequired)}`"
                                :readonly="readonlyPostoperativ"
                            />
                        </KInputWrapper>

                        <KInputWrapper
                            name="tid_klar_for_utskrivning"
                            v-slot="{ inputName, shouldBeVisible, shouldBeRequired }"
                        >
                            <FormulateInput
                                v-if="shouldBeVisible"
                                :validation="shouldBeRequired ? 'required' : ''"
                                validation-name="Klar för utskrivning"
                                type="timepicker"
                                useNow
                                :useDropdown="false"
                                :name="inputName"
                                :label="`Klar för utskrivning ${checkAddAsterisk(shouldBeRequired)}`"
                                :readonly="readonlyPostoperativ"
                            />
                        </KInputWrapper>
                        <KInputWrapper name="tid_ut" v-slot="{ inputName, shouldBeVisible, shouldBeRequired }">
                            <FormulateInput
                                v-if="shouldBeVisible"
                                :validation="shouldBeRequired ? 'required' : ''"
                                validation-name="Patient ut från postop"
                                type="timepicker"
                                useNow
                                :useDropdown="false"
                                :name="inputName"
                                :label="`Patient ut från postop ${checkAddAsterisk(shouldBeRequired)}`"
                                :readonly="readonlyPostoperativ"
                            />
                        </KInputWrapper>
                        <KInputWrapper name="sangplats" v-slot="{ inputName, shouldBeVisible, shouldBeRequired }">
                            <FormulateInput
                                v-if="shouldBeVisible"
                                :validation="shouldBeRequired ? 'required' : ''"
                                validation-name="Sängplats"
                                :name="inputName"
                                :label="`Sängplats ${checkAddAsterisk(shouldBeRequired)}`"
                                type="vueselect"
                                :options="sangplatser"
                                :readonly="readonlyPostoperativ"
                            />
                        </KInputWrapper>
                    </div>
                </div>

                <KGroup name="hud" row-gap="none">
                    <h4>Hud</h4>
                    <div class="wrapper-3-1">
                        <KInputWrapper
                            name="omlaggning_forband"
                            v-slot="{ inputName, shouldBeVisible, shouldBeRequired }"
                        >
                            <FormulateInput
                                v-if="shouldBeVisible"
                                :validation="shouldBeRequired ? 'required' : ''"
                                validation-name="Omläggning förband"
                                :name="inputName"
                                :label="`Omläggning förband ${checkAddAsterisk(shouldBeRequired)}`"
                                type="vueselect"
                                :options="[
                                    { value: 'true', label: 'Ja' },
                                    { value: 'false', label: 'Nej' },
                                ]"
                                :readonly="readonlyPostoperativ"
                            />
                        </KInputWrapper>
                        <KInputWrapper
                            name="kommentar_opomrade"
                            v-slot="{ inputName, shouldBeVisible, shouldBeRequired }"
                        >
                            <FormulateInput
                                v-if="shouldBeVisible"
                                :validation="shouldBeRequired ? 'required' : ''"
                                validation-name="Kommentar opområde"
                                :name="inputName"
                                :label="`Kommentar opområde ${checkAddAsterisk(shouldBeRequired)}`"
                                type="textarea"
                                class="fritext"
                                :readonly="readonlyPostoperativ"
                            />
                        </KInputWrapper>
                    </div>
                </KGroup>

                <KGroup name="elimination" row-gap="none">
                    <h4>Elimination</h4>
                    <div class="flex-container">
                        <div class="wrapper-3-1">
                            <KInputWrapper name="kad" v-slot="{ inputName, shouldBeVisible, shouldBeRequired }">
                                <FormulateInput
                                    v-if="shouldBeVisible"
                                    :validation="shouldBeRequired ? 'required' : ''"
                                    validation-name="KAD"
                                    :name="inputName"
                                    :label="`KAD ${checkAddAsterisk(shouldBeRequired)}`"
                                    type="vueselect"
                                    :options="[
                                        { value: 'true', label: 'Ja' },
                                        { value: 'false', label: 'Nej' },
                                    ]"
                                    @input="
                                        (val) => {
                                            if (postoperativOvervakningFormData?.kad !== 'true') {
                                                postoperativOvervakningFormData.storlek = null;
                                                postoperativOvervakningFormData.kuff = null;
                                                postoperativOvervakningFormData.xylocaingel = null;
                                            }
                                        }
                                    "
                                />
                            </KInputWrapper>

                            <KInputWrapper name="storlek" v-slot="{ inputName, shouldBeVisible, shouldBeRequired }">
                                <FormulateInput
                                    v-if="shouldBeVisible"
                                    :validation="
                                        postoperativOvervakningFormData?.kad === 'true' && shouldBeRequired
                                            ? 'required'
                                            : ''
                                    "
                                    validation-name="Storlek, nr"
                                    :name="inputName"
                                    :label="`Storlek, nr ${checkAddAsterisk(shouldBeRequired)}`"
                                    type="text"
                                    :disabled="postoperativOvervakningFormData?.kad !== 'true'"
                                    :readonly="readonlyPostoperativ"
                                />
                            </KInputWrapper>

                            <KInputWrapper name="kuff" v-slot="{ inputName, shouldBeVisible, shouldBeRequired }">
                                <FormulateInput
                                    v-if="shouldBeVisible"
                                    :validation="
                                        postoperativOvervakningFormData?.kad === 'true' && shouldBeRequired
                                            ? 'required'
                                            : ''
                                    "
                                    validation-name="Kuff (ml)"
                                    :name="inputName"
                                    :label="`Kuff (ml) ${checkAddAsterisk(shouldBeRequired)}`"
                                    type="knumber"
                                    :disabled="postoperativOvervakningFormData?.kad !== 'true'"
                                    :readonly="readonlyPostoperativ"
                                />
                            </KInputWrapper>

                            <KInputWrapper name="xylocaingel" v-slot="{ inputName, shouldBeVisible, shouldBeRequired }">
                                <FormulateInput
                                    v-if="shouldBeVisible"
                                    :validation="
                                        postoperativOvervakningFormData?.kad === 'true' && shouldBeRequired
                                            ? 'required'
                                            : ''
                                    "
                                    validation-name="Xylocaingel, 10 gram, antal"
                                    :name="inputName"
                                    :label="`Xylocaingel, 10 gram, antal ${checkAddAsterisk(shouldBeRequired)}`"
                                    type="knumber"
                                    :disabled="postoperativOvervakningFormData?.kad !== 'true'"
                                    :readonly="readonlyPostoperativ"
                                />
                            </KInputWrapper>
                        </div>

                        <KInputWrapper name="eliminationer" v-slot="{ inputName, shouldBeVisible, shouldBeRequired }">
                            <FormulateInput
                                v-if="shouldBeVisible"
                                :name="inputName"
                                type="group"
                                :repeatable="true"
                                add-label="Fler bladderscan"
                                remove-position="after"
                                class="elimination group-add-button-below"
                            >
                                <template #default="{ index }">
                                    <div class="wrapper-3-1">
                                        <FormulateInput
                                            name="bladderscan"
                                            :validation="shouldBeRequired ? 'required' : ''"
                                            validation-name="Bladderscan (ml)"
                                            :label="`Bladderscan (ml) ${checkAddAsterisk(shouldBeRequired)}`"
                                            type="knumber"
                                            :readonly="readonlyPostoperativ"
                                        />

                                        <FormulateInput
                                            type="timepicker"
                                            useNow
                                            :useDropdown="false"
                                            name="tid_for_scanning"
                                            :validation="shouldBeRequired ? 'required' : ''"
                                            validation-name="Tid för scanning"
                                            :label="`Tid för scanning ${checkAddAsterisk(shouldBeRequired)}`"
                                            :readonly="readonlyPostoperativ"
                                        />

                                        <FormulateInput
                                            name="urintappning"
                                            :validation="shouldBeRequired ? 'required' : ''"
                                            validation-name="Urintappning"
                                            type="vueselect"
                                            :options="[
                                                { value: 'JA', label: 'Ja' },
                                                { value: 'NEJ', label: 'Nej' },
                                                { value: 'TOALETTBESOK', label: 'Toalettbesök' },
                                            ]"
                                            :label="`Urintappning ${checkAddAsterisk(shouldBeRequired)}`"
                                            v-on:input="(input) => handleUrintappningMl(input, index)"
                                            :readonly="readonlyPostoperativ"
                                        />

                                        <FormulateInput
                                            name="urintappning_ml"
                                            :validation="shouldBeRequired ? 'required' : ''"
                                            validation-name="Urintappning (ml)"
                                            :label="`Urintappning (ml) ${checkAddAsterisk(shouldBeRequired)}`"
                                            type="knumber"
                                            :disabled="
                                                postoperativOvervakningFormData?.eliminationer?.[index]
                                                    ?.urintappning !== 'JA'
                                            "
                                            :readonly="readonlyPostoperativ"
                                        />
                                    </div>
                                </template>

                                <template #remove="{ index, removeItem }">
                                    <a
                                        role="button"
                                        class="formulate-input-group-repeatable-remove"
                                        @click="removeItem(index)"
                                    >
                                        Radera fält
                                    </a>
                                </template>

                                <template #addMore="{ addMore }">
                                    <div class="formulate-input-group-add-more">
                                        <button type="button" data-minor="" data-ghost="" @click="addMore()">
                                            <span class="formulate-input-element--button--label"
                                                >Lägg till fler bladderscan</span
                                            >
                                        </button>
                                    </div>
                                </template>
                            </FormulateInput>
                        </KInputWrapper>
                    </div>
                </KGroup>

                <KGroup name="cirkulation" class="cirkulation" row-gap="none">
                    <h4>Cirkulation</h4>
                    <div class="wrapper-3-1">
                        <KInputWrapper
                            name="cirkulatorisk_stabil"
                            v-slot="{ inputName, shouldBeVisible, shouldBeRequired }"
                        >
                            <FormulateInput
                                v-if="shouldBeVisible"
                                :validation="shouldBeRequired ? 'required' : ''"
                                validation-name="Cirkulatorisk stabil"
                                :name="inputName"
                                :label="`Cirkulatorisk stabil ${checkAddAsterisk(shouldBeRequired)}`"
                                type="vueselect"
                                :options="[
                                    { value: 'true', label: 'Ja' },
                                    { value: 'false', label: 'Nej' },
                                ]"
                                :readonly="readonlyPostoperativ"
                            />
                        </KInputWrapper>
                        <KInputWrapper
                            name="kommentar_cirkulation"
                            v-slot="{ inputName, shouldBeVisible, shouldBeRequired }"
                        >
                            <FormulateInput
                                v-if="shouldBeVisible"
                                :validation="shouldBeRequired ? 'required' : ''"
                                validation-name="Kommentar cirkulation"
                                :name="inputName"
                                :label="`Kommentar cirkulation ${checkAddAsterisk(shouldBeRequired)}`"
                                type="textarea"
                                class="fritext"
                                :readonly="readonlyPostoperativ"
                            />
                        </KInputWrapper>
                    </div>
                </KGroup>

                <KGroup name="smarta" row-gap="none">
                    <h4>Smärta</h4>
                    <div class="wrapper-3-1">
                        <KInputWrapper
                            name="smarta_nrs_vid_60_min"
                            v-slot="{ inputName, shouldBeVisible, shouldBeRequired }"
                        >
                            <FormulateInput
                                v-if="shouldBeVisible"
                                :validation="shouldBeRequired ? 'required' : ''"
                                validation-name="Smärta NRS, inom 60 min"
                                :name="inputName"
                                :label="`Smärta NRS, inom 60 min ${checkAddAsterisk(shouldBeRequired)}`"
                                type="vueselect"
                                :options="[
                                    { value: 0, label: '0' },
                                    { value: 1, label: '1' },
                                    { value: 2, label: '2' },
                                    { value: 3, label: '3' },
                                    { value: 4, label: '4' },
                                    { value: 5, label: '5' },
                                    { value: 6, label: '6' },
                                    { value: 7, label: '7' },
                                    { value: 8, label: '8' },
                                    { value: 9, label: '9' },
                                    { value: 10, label: '10' },
                                ]"
                                :readonly="readonlyPostoperativ"
                            />
                        </KInputWrapper>
                        <KInputWrapper
                            name="smarta_nrs_hogsta"
                            v-slot="{ inputName, shouldBeVisible, shouldBeRequired }"
                        >
                            <FormulateInput
                                v-if="shouldBeVisible"
                                :validation="shouldBeRequired ? 'required' : ''"
                                validation-name="Smärta NRS, högsta"
                                :name="inputName"
                                :label="`Smärta NRS, högsta ${checkAddAsterisk(shouldBeRequired)}`"
                                type="vueselect"
                                :options="[
                                    { value: 0, label: '0' },
                                    { value: 1, label: '1' },
                                    { value: 2, label: '2' },
                                    { value: 3, label: '3' },
                                    { value: 4, label: '4' },
                                    { value: 5, label: '5' },
                                    { value: 6, label: '6' },
                                    { value: 7, label: '7' },
                                    { value: 8, label: '8' },
                                    { value: 9, label: '9' },
                                    { value: 10, label: '10' },
                                ]"
                                :readonly="readonlyPostoperativ"
                            />
                        </KInputWrapper>
                        <KInputWrapper
                            name="smarta_nrs_vid_utskrivning"
                            v-slot="{ inputName, shouldBeVisible, shouldBeRequired }"
                        >
                            <FormulateInput
                                v-if="shouldBeVisible"
                                :validation="shouldBeRequired ? 'required' : ''"
                                validation-name="Smärta NRS, vid utskrivning"
                                :name="inputName"
                                :label="`Smärta NRS, vid utskrivning ${checkAddAsterisk(shouldBeRequired)}`"
                                type="vueselect"
                                :options="[
                                    { value: 0, label: '0' },
                                    { value: 1, label: '1' },
                                    { value: 2, label: '2' },
                                    { value: 3, label: '3' },
                                    { value: 4, label: '4' },
                                    { value: 5, label: '5' },
                                    { value: 6, label: '6' },
                                    { value: 7, label: '7' },
                                    { value: 8, label: '8' },
                                    { value: 9, label: '9' },
                                    { value: 10, label: '10' },
                                ]"
                                :readonly="readonlyPostoperativ"
                            />
                        </KInputWrapper>
                        <KInputWrapper
                            name="postop_smarta_sammanfattning"
                            v-slot="{ inputName, shouldBeVisible, shouldBeRequired }"
                        >
                            <FormulateInput
                                v-if="shouldBeVisible"
                                :validation="shouldBeRequired ? 'required' : ''"
                                validation-name="Postop smärta sammanfattning"
                                :name="inputName"
                                :label="`Postop smärta sammanfattning ${checkAddAsterisk(shouldBeRequired)}`"
                                type="vueselect"
                                :options="postopSmartaSammanfattning"
                                :readonly="readonlyPostoperativ"
                            />
                        </KInputWrapper>
                    </div>
                </KGroup>
                <KInputWrapper name="illamaende" v-slot="{ inputName, shouldBeVisible, shouldBeRequired }">
                    <KGroup name="illamaende-temp" row-gap="none">
                        <h4>Illamående/Temp</h4>
                        <div class="wrapper-3-1">
                            <FormulateInput
                                v-if="shouldBeVisible"
                                :validation="shouldBeRequired ? 'required' : ''"
                                validation-name="Illamående"
                                :name="inputName"
                                :label="`Illamående ${checkAddAsterisk(shouldBeRequired)}`"
                                type="vueselect"
                                :options="[
                                    { value: 'Inget illamående', label: 'Inget illamående' },
                                    { value: 'Illamående', label: 'Illamående' },
                                    { value: 'Kräkning', label: 'Kräkning' },
                                ]"
                                :readonly="readonlyPostoperativ"
                            />
                            <KNumber
                                name="temperatur_opslut"
                                label="Temperatur vid opslut"
                                :readonly="readonlyPostoperativ"
                                :validation="[
                                    [NumberValidationRule.Decimals, 1],
                                    [NumberValidationRule.Min, 10],
                                    [NumberValidationRule.Max, 45],
                                ]"
                                validation-name="Temperatur vid opslut"
                            />
                            <KNumber
                                name="temperatur_lagsta"
                                label="Temperatur, lägsta (grader)"
                                :readonly="readonlyPostoperativ"
                                :validation="[
                                    [NumberValidationRule.Decimals, 1],
                                    [NumberValidationRule.Min, 10],
                                    [NumberValidationRule.Max, 45],
                                ]"
                                validation-name="Temperatur, lägsta"
                            />
                            <KNumber
                                name="temperatur_hogsta"
                                label="Temperatur, vid utskrivning (grader)"
                                :readonly="readonlyPostoperativ"
                                :validation="[
                                    [NumberValidationRule.Decimals, 1],
                                    [NumberValidationRule.Min, 10],
                                    [NumberValidationRule.Max, 45],
                                ]"
                                validation-name="Temperatur, vid utskrivning (grader)"
                            />
                        </div>
                    </KGroup>
                </KInputWrapper>

                <KGroup name="mobilisering" row-gap="none">
                    <h4>Mobilisering</h4>
                    <div class="wrapper-3-1">
                        <KInputWrapper name="mobilisering" v-slot="{ inputName, shouldBeVisible, shouldBeRequired }">
                            <FormulateInput
                                v-if="shouldBeVisible"
                                :validation="shouldBeRequired ? 'required' : ''"
                                validation-name="Mobiliserad"
                                :name="inputName"
                                :label="`Mobiliserad ${checkAddAsterisk(shouldBeRequired)}`"
                                type="vueselect"
                                :options="[
                                    { value: 'Ja', label: 'Ja' },
                                    { value: 'Nej', label: 'Nej' },
                                    { value: 'Ordinerad sängläge', label: 'Ordinerad sängläge' },
                                ]"
                                :readonly="readonlyPostoperativ"
                            />
                        </KInputWrapper>
                        <KInputWrapper name="bromage" v-slot="{ inputName, shouldBeVisible, shouldBeRequired }">
                            <FormulateInput
                                v-if="shouldBeVisible"
                                :required="shouldBeRequired"
                                :validation="shouldBeRequired ? 'required' : ''"
                                validation-name="Bromage"
                                :name="inputName"
                                :label="`Bromage ${checkAddAsterisk(shouldBeRequired)}`"
                                type="vueselect"
                                :options="[
                                    { value: 0, label: '0' },
                                    { value: 1, label: '1' },
                                    { value: 2, label: '2' },
                                    { value: 3, label: '3' },
                                ]"
                                :readonly="readonlyPostoperativ"
                            />
                        </KInputWrapper>
                    </div>
                    <KInputWrapper
                        name="kommentar_mobilisering"
                        v-slot="{ inputName, shouldBeVisible, shouldBeRequired }"
                    >
                        <FormulateInput
                            v-if="shouldBeVisible"
                            :validation="shouldBeRequired ? 'required' : ''"
                            validation-name="Kommentar mobiliserad"
                            :name="inputName"
                            :label="`Kommentar mobiliserad ${checkAddAsterisk(shouldBeRequired)}`"
                            type="textarea"
                            class="fritext"
                            :readonly="readonlyPostoperativ"
                        />
                    </KInputWrapper>
                </KGroup>

                <KInputWrapper name="postopkomplikationer" v-slot="{ inputName, shouldBeVisible, shouldBeRequired }">
                    <PostopavvikelseGroup
                        v-if="shouldBeVisible"
                        :name="inputName"
                        :readonly="readonlyPostoperativ"
                        :required="shouldBeRequired"
                    />
                </KInputWrapper>

                <News2 :readonly="readonlyPostoperativ" :updatedAt="postoperativOvervakningUpdatedAt" />

                <div class="wrapper-3-1">
                    <KInputWrapper name="kommentar_postop" v-slot="{ inputName, shouldBeVisible, shouldBeRequired }">
                        <FormulateInput
                            v-if="shouldBeVisible"
                            :validation="shouldBeRequired ? 'required' : ''"
                            validation-name="Kommentar postop"
                            :name="inputName"
                            :label="`Kommentar postop ${checkAddAsterisk(shouldBeRequired)}`"
                            type="textarea"
                            class="fritext"
                            :readonly="readonlyPostoperativ"
                        />
                    </KInputWrapper>
                </div>
            </FormulateForm>

            <div class="grey-div">
                <Signeringsansvarig
                    :readonly="readonlyPostoperativ"
                    type="submit"
                    @emitSigneringsansvarigaAction="submitPostoperativ($event, true)"
                    :isSignable="true"
                    :currentProfile="currentProfile"
                    :isSigned="postoperativOvervakningSavedFormData.is_signed"
                    :loading="saveLoading"
                    :orgUnit="operationsanmalan.operationsenhet"
                    :accountableHealthcareProfessional="
                        postoperativOvervakningFormData.accountableHealthcareProfessional
                    "
                ></Signeringsansvarig>

                <sign-frame
                    v-if="signVisiblePostoperativOvervakning"
                    title="Postoperativ övervakning"
                    :url="signUrlPostoperativOvervakning"
                    :data="signDataPostoperativOvervakning"
                    @success="handleSignPostoperativSuccess"
                    @error="handleSignPostoperativError"
                    @cancel="handleSignPostoperativCancel"
                />
            </div>
        </KSection>

        <KSection
            name="stralningsregistrering"
            heading="Strålningsregistrering"
            :numbered="true"
            :collapsible="true"
            :expanded="sectionsExpanded"
            class="stralningsregistrering"
            :custom-config-provide-suffix="stralningsregistreringSuffix"
        >
            <KForm
                id="operationsjournal-stralningsregistrering-form"
                ref="stralningsregistreringFormRef"
                :initial-values-getter="fetchStralningsregistreringFormData"
                :readonly="readonlyStralningsregistrering"
            >
                <template #default>
                    <div class="form-content">
                        <KRepeater
                            name="stralningsregistreringar"
                            add-label="Lägg till strålningsregistrering"
                            remove-label="Ta bort strålningsregistrering"
                        >
                            <KInputWrapper
                                name="genomlysningsapparat"
                                v-slot="{ inputName, shouldBeVisible, shouldBeRequired }"
                            >
                                <FormulateInput
                                    v-if="shouldBeVisible"
                                    :validation="shouldBeRequired ? 'required' : ''"
                                    validation-name="Genomlysningsapparat"
                                    type="vueselect"
                                    :options="genomlysningsapparater"
                                    :name="inputName"
                                    :label="`Genomlysningsapparat ${checkAddAsterisk(shouldBeRequired)}`"
                                />
                            </KInputWrapper>
                            <KInputWrapper name="dosvarde" v-slot="{ inputName, shouldBeVisible, shouldBeRequired }">
                                <FormulateInput
                                    v-if="shouldBeVisible"
                                    :validation="shouldBeRequired ? 'required' : ''"
                                    validation-name="Dosvärde (cGycm2)"
                                    :decimals="3"
                                    type="knumber"
                                    :name="inputName"
                                    :label="`Dosvärde (cGycm2) ${checkAddAsterisk(shouldBeRequired)}`"
                                />
                            </KInputWrapper>
                            <KInputWrapper
                                name="genomlysningstid"
                                v-slot="{ inputName, shouldBeVisible, shouldBeRequired }"
                            >
                                <FormulateInput
                                    v-if="shouldBeVisible"
                                    :validation="shouldBeRequired ? 'required' : ''"
                                    validation-name="Genomlysningstid (sekunder)"
                                    type="knumber"
                                    :name="inputName"
                                    :label="`Genomlysningstid (sekunder) ${checkAddAsterisk(shouldBeRequired)}`"
                                    :max="3600"
                                />
                            </KInputWrapper>
                        </KRepeater>
                    </div>
                </template>
                <template #actions>
                    <Signeringsansvarig
                        :readonly="readonlyStralningsregistrering"
                        type="submit"
                        @emitSigneringsansvarigaAction="submitStralningsregistrering"
                        :isSignable="true"
                        :currentProfile="currentProfile"
                        :isSigned="stralningsregistreringSavedFormData.is_signed"
                        :loading="saveLoading"
                        :orgUnit="operationsanmalan.operationsenhet"
                        :accountableHealthcareProfessional="
                            stralningsregistreringFormData.accountableHealthcareProfessional
                        "
                    />

                    <sign-frame
                        v-if="signVisibleStralningsregistrering"
                        title="Strålningsregistrering"
                        :url="signUrlStralningsregistrering"
                        :data="signDataStralningsregistrering"
                        @success="handleSignStralningsregistreringSuccess"
                        @error="handleSignStralningsregistreringError"
                        @cancel="handleSignStralningsregistreringCancel"
                    />
                </template>
            </KForm>
        </KSection>

        <SektionExternStralningsregistrering
            @submit="submit"
            :operationsanmalanId="parameters.operationsanmalan_id"
            :readonly="readonlyStralningsregistrering"
            :expanded="sectionsExpanded"
        />
    </div>
</template>

<script>
    import { getConfig, getErrorMessage, klinikenApi } from "@/api";
    import tabIcon from "@/assets/bed_icon.svg";
    import SignFrame from "@/components/Auth/SignFrame.vue";
    import Button from "@/components/Button.vue";
    import ButtonGroup from "@/components/ButtonGroup.vue";
    import ButtonLink from "@/components/ButtonLink.vue";

    import KForm from "@/components/forms/Form/KForm.vue";
    import { generateSchema } from "@/components/forms/Form/schemaHelpers";
    import KGroup from "@/components/forms/groups/Group/KGroup.vue";
    import {
        addMinimumRows,
        defaultRepeaterConverter,
        dynamicPropsRepeaterConverter,
    } from "@/components/forms/groups/Repeater/helpers";
    import KRepeater from "@/components/forms/groups/Repeater/KRepeater.vue";
    import KInputWrapper from "@/components/forms/inputs/InputWrapper/KInputWrapper.vue";
    import KNumber from "@/components/forms/inputs/Number/KNumber.vue";
    import { replaceSeparator } from "@/components/forms/inputs/Number/numberUtils";
    import { NumberValidationRule } from "@/components/forms/inputs/Number/numberValidation";
    import KText from "@/components/forms/inputs/Text/KText.vue";
    import { convertDigitumData } from "@/components/forms/reusables/digitumHelper";
    import { convertImplantatData } from "@/components/forms/reusables/implantatHelper";
    import { convertNivaData } from "@/components/forms/reusables/nivaerHelper";
    import { convertResursData } from "@/components/forms/reusables/resurserHelper";
    import { convertVardformData } from "@/store/modules/operation/vardformModule";
    import KLabel from "@/components/forms/subComponents/Label/KLabel.vue";
    import KSection from "@/components/generic/KSection.vue";
    import ModalBasic, { BasicModalVariant } from "@/components/generic/ModalBasic.vue";
    import ModalContent from "@/components/generic/ModalContent.vue";
    import MaterialScanner from "@/components/MaterialScanner.vue";
    import { mapGenomlysningsapparat } from "@/store/modules/genomlysningsapparatModule";
    import {
        CONFIG_PROVIDE_NAME,
        ConfigModuleName,
        OperationConfigModuleName,
    } from "@/store/modules/types/configModuleTypes";
    import BaseTab from "@/tabs/mixins/BaseTab.js";
    import DialogAktivPatient from "@/tabs/Operation/DialogAktivPatient.vue";
    import News2 from "@/tabs/Operation/Operationsjournal/Postoperativ/News2.vue";
    import {
        filterEmptyNews2Entries,
        NEWS2_REQUIRED_FIELDS,
    } from "@/tabs/Operation/Operationsjournal/Postoperativ/news2";
    import PostopavvikelseGroup from "@/tabs/Operation/Operationsjournal/Postoperativ/PostopavvikelseGroup.vue";
    import SektionExternStralningsregistrering from "@/tabs/Operation/Operationsjournal/SektionExternStralningsregistrering.vue";

    import Signeringsansvarig from "@/tabs/Operation/Signeringsansvarig.vue";
    import TidInput from "@/tabs/Operation/TidInput.vue";

    import {
        anestesikomplikationerKategorierOptions,
        anestesikomplikationerOptions,
        arrayPropertyExistsNotEmpty,
        avtalOptions,
        changeTimeToDate,
        checkRequired,
        createErrorForMissingFields,
        cvOptions,
        cvOptionsShort,
        cvSimpleOptions,
        extractHourMinuteFromDateTime,
        fetchData,
        getPropertyValue,
        getTimeFromDatetime,
        isEmptyObject,
        isNotEmptyArray,
        isNotEmptyObject,
        isNull,
        profileOptions,
        propertyExists,
        removeEmptyObjectsFromArray,
        DIGITUM,
        RYGGKOTOR,
        salarOptions,
        scrollToAndFocusError,
        SetField,
        typoperationerOptions,
    } from "@/tabs/Operation/utils";
    import { calculateAntalNivaer, collectErrors } from "@/tabs/Operation/utilsTs";
    import { flashMessage, flashWarning, openDialog } from "@/utils";
    import { cloneDeep, isEmpty } from "lodash";
    import moment from "moment";
    import emitter from "tiny-emitter/instance";
    import { provide } from "vue";
    import { mapActions, mapGetters } from "vuex";

    export default {
        extends: BaseTab,
        name: "Operationsjournal",
        tabName: "Operationsjournal",
        icon: tabIcon,
        patientBound: true,
        moduleName: ConfigModuleName.Operation,
        subModuleName: OperationConfigModuleName.Journal,
        components: {
            KInputWrapper,
            KGroup,
            MaterialScanner,
            News2,
            PostopavvikelseGroup,
            TidInput,
            ModalContent,
            ModalBasic,
            Button,
            ButtonGroup,
            ButtonLink,
            DialogAktivPatient,
            Signeringsansvarig,
            SignFrame,
            KSection,
            KRepeater,
            KForm,
            KNumber,
            KText,
            KLabel,
            SektionExternStralningsregistrering,
        },
        props: {
            parameters: {
                type: Object,
                required: false,
            },

            uid: Number,
            tabId: Number,
        },
        setup() {
            provide(CONFIG_PROVIDE_NAME, {
                moduleName: ConfigModuleName.Operation,
                subModuleName: OperationConfigModuleName.Journal,
            });
            provide(`${CONFIG_PROVIDE_NAME}-${OperationConfigModuleName.JournalOperationsuppgifter}`, {
                moduleName: ConfigModuleName.Operation,
                subModuleName: OperationConfigModuleName.JournalOperationsuppgifter,
            });
            provide(`${CONFIG_PROVIDE_NAME}-${OperationConfigModuleName.JournalTider}`, {
                moduleName: ConfigModuleName.Operation,
                subModuleName: OperationConfigModuleName.JournalTider,
            });
            provide(`${CONFIG_PROVIDE_NAME}-${OperationConfigModuleName.OperationJournalPatientdata}`, {
                moduleName: ConfigModuleName.Operation,
                subModuleName: OperationConfigModuleName.OperationJournalPatientdata,
            });
            provide(`${CONFIG_PROVIDE_NAME}-${OperationConfigModuleName.JournalPostoperativOvervakning}`, {
                moduleName: ConfigModuleName.Operation,
                subModuleName: OperationConfigModuleName.JournalPostoperativOvervakning,
            });
            provide(`${CONFIG_PROVIDE_NAME}-${OperationConfigModuleName.JournalStralningsregistrering}`, {
                moduleName: ConfigModuleName.Operation,
                subModuleName: OperationConfigModuleName.JournalStralningsregistrering,
            });
            provide(`${CONFIG_PROVIDE_NAME}-${OperationConfigModuleName.JournalPersonal}`, {
                moduleName: ConfigModuleName.Operation,
                subModuleName: OperationConfigModuleName.JournalPersonal,
            });
            provide(`${CONFIG_PROVIDE_NAME}-${OperationConfigModuleName.JournalAnestesi}`, {
                moduleName: ConfigModuleName.Operation,
                subModuleName: OperationConfigModuleName.JournalAnestesi,
            });
            provide(`${CONFIG_PROVIDE_NAME}-${OperationConfigModuleName.JournalMaterial}`, {
                moduleName: ConfigModuleName.Operation,
                subModuleName: OperationConfigModuleName.JournalMaterial,
            });

            return {
                operationsuppgifterConfigSuffix: OperationConfigModuleName.JournalOperationsuppgifter,
                tiderConfigSuffix: OperationConfigModuleName.JournalTider,
                personalConfigSuffix: OperationConfigModuleName.JournalPersonal,
                postoperativOvervakningSuffix: OperationConfigModuleName.JournalPostoperativOvervakning,
                stralningsregistreringSuffix: OperationConfigModuleName.JournalStralningsregistrering,
                anestesiSuffix: OperationConfigModuleName.JournalAnestesi,
                materialSuffix: OperationConfigModuleName.JournalMaterial,
                patientdataModuleName: ConfigModuleName.Operation,
                patientdataSubModuleName: OperationConfigModuleName.OperationJournalPatientdata,
            };
        },
        data() {
            return {
                formError: {},
                validation: {},
                operationsdatum: null,
                defaultRepeaterConverter,
                operationsanmalan: {},
                loading: false,
                saveLoading: false,
                sectionsExpanded: false,
                NumberValidationRule,

                // Sektion Operationsuppgifter
                operationsuppgifterFormData: {
                    typoperation: null,
                    huvuddiagnos: null,
                    bidiagnoser: [{}],
                    bioperationskoder: [{}],
                    operationsdatum: null,
                    sal: "",
                    reop: false,
                    garantiop: false,
                    lateralitet: "",
                    avtal: null,
                    anmarkning: "",
                    is_signed: false,
                    accountableHealthcareProfessional: "",
                    digitum: [],
                    niva_fran_till: [],
                    niva_fran_till_display: [],
                    oplage: null,
                    resurser: [{}],
                    implantat: [{}],
                    vardform: null,
                },
                operationsuppgifterSavedFormData: {},
                ryggkotor: RYGGKOTOR.map((rygskota) => ({
                    value: rygskota.value,
                    label: rygskota.text,
                })),
                oplageOptions: [],
                typoperationer: [],
                huvuddiagnoser: [],
                bidiagnoser: [],
                huvudoperationskoder: [],
                bioperationskoder: [],
                avtal: [],
                salar: [],
                vardformOptions: [],
                digitumOptions: DIGITUM,
                lateraliteter: [
                    { value: "", label: "" },
                    { value: "HÖGER_SIDA", label: "Höger sida" },
                    { value: "VÄNSTER_SIDA", label: "Vänster sida" },
                    { value: "BILATERALT", label: "Bilateralt" },
                    { value: "EJ_AKTUELLT", label: "Ej aktuellt" },
                ],
                signVisibleOperationsuppgifter: false,
                signUrlOperationsuppgifter: "",
                signDataOperationsuppgifter: "",
                readonlyOperationsUppgifter: false,

                // Sektion Patientdata
                patientdataFormData: {
                    is_signed: false,
                    accountableHealthcareProfessional: "",
                },
                patientdataSavedFormData: {},
                signVisiblePatientdata: false,
                signUrlPatientdata: "",
                signDataPatientdata: "",
                readonlyPatientData: false,

                // Sektion Personal
                personalFormData: {
                    huvudoperator: null,
                    ass_operatorer: [{}],
                    operationssjukskoterskor: [{}],
                    operationsunderskoterskor: [{}],
                    is_signed: false,
                    accountableHealthcareProfessional: "",
                    anestesisjukskoterska_intid: null,
                    anestesisjukskoterska_uttid: null,
                    anestesisjukskoterskor: [{}],
                    fritext: "",
                },
                personalSavedFormData: {},
                id_kontroll: [],
                operatorer: [],
                ass_operatorer: [],
                operationssjukskoterskor: [],
                operationsunderskoterskor: [],
                anestesilakare: [],
                anestesisjukskoterskor: [],
                signVisiblePersonal: false,
                signUrlPersonal: "",
                signDataPersonal: "",
                readonlyPersonal: false,

                // Sektion WHO-checklista
                whochecklistaForberedelseFormData: {
                    fungerande_pulsoximeter: null,
                    kand_allergi: null,
                    operationsomrade_markerat: null,
                    patient_bekraftat: null,
                    risk_for_aspiration: null,
                    risk_for_blodforlust: null,
                    risk_for_hypotermi: null,
                    sakerhetskontroll_for_anestesi: null,
                    updated_at: null,
                    updated_by: null,
                },
                whochecklistaTimeoutFormData: {
                    antibiotikaprofylax: null,
                    bedomning_korrekt_upplagd: null,
                    bedomning_kritiska_moment: null,
                    bedomning_patientspecifika_problem: null,
                    bedomning_problem_utrustning: null,
                    bedomning_sterilitet_bekraftat: null,
                    bekraftar_muntligt: null,
                    bildinformation: null,
                    medlemmar_presenterat_sig: null,
                    trombosprofylax: null,
                    updated_at: null,
                    updated_by: null,
                },
                whochecklistaAvslutningFormData: {
                    antal_instrument_torkar_nalar: null,
                    huvudpunkter_postoperativt_omhandertagande: null,
                    preparat_markta: null,
                    problem_utrustning_uppmarksammas: null,
                    vad_kan_vi_lara: null,
                    vilket_ingrepp: null,
                    updated_at: null,
                    updated_by: null,
                },
                showWHOchecklistaForberedelse: false,
                showWHOchecklistaTimeOut: false,
                showWHOchecklistaAvslutning: false,
                readonlyWho: false,
                readonlyWhoForberwdelse: false,
                readonlyWhoTimeout: false,
                readonlyWhoAvslutning: false,

                // Sektion Material
                materialFormData: {
                    material: [],
                    is_signed: false,
                    accountableHealthcareProfessional: "",
                    fritext: "",
                },
                materialSavedFormData: {},
                material: [],
                signVisibleMaterial: false,
                signUrlMaterial: "",
                signDataMaterial: "",
                readonlyMaterial: false,

                // Sektion Anestesi
                anestesiFormData: {
                    anestesikomplikationer: [{}],
                    huvudanestesikod: null,
                    bianestesikoder: [{}],
                    is_signed: false,
                    accountableHealthcareProfessional: "",
                },
                anestesiSavedFormData: {},
                huvudanestesikoder: [],
                bianestesikoder: [],
                rawAnestesikomplikationer: [],
                anestesikomplikationerKategorier: [],
                anestesikomplikationer: [],
                anestesikomplikationSvarighetsgrader: [],
                anestesikomplikationSvarighetsgraderOptions: [],
                anestesiForklaring: false,
                signVisibleAnestesi: false,
                signUrlAnestesi: "",
                signDataAnestesi: "",
                readonlyAnestesi: false,

                // Sektion Tider
                tiderFormData: {
                    uppdukning_start: null,
                    uppdukning_slut: null,
                    antibiotikaprofylax_start: null,
                    ankomsttid_preopenhet: null,
                    preoperativ_anestesi_start: null,
                    patienttid_start: null,
                    patient_in_opsal: null,
                    anestesitid_start: null,
                    anestesitid_klar: null,
                    operationsforberedelse_klar: null,
                    opstart: null,
                    robot_in: null,
                    robot_ut: null,
                    blodtomt_falt_start: null,
                    blodtomt_falt_slut: null,
                    x_minuter_kvar: null,
                    opslut: null,
                    anestesi_slut: null,
                    patient_ut_opsal: null,
                    patienttid_slut: null,
                    efterarbete_sal_slut: null,
                    fritext: "",
                },
                tiderSavedFormData: {},
                tiderForklaring: false,
                readonlyTider: false,

                // Sektion Postoperativ övervakning
                postoperativOvervakningFormData: {
                    is_signed: false,
                    accountableHealthcareProfessional: "",

                    tid_in: null,
                    tid_klar_for_utskrivning: null,
                    tid_ut: null,
                    sangplats: null,

                    omlaggning_forband: null,
                    kommentar_opomrade: null,

                    kommentar_mobilisering: null,
                    mobilisering: null,

                    kommentar_postop: null,

                    illamaende: null,
                    temperatur_opslut: null,
                    temperatur_lagsta: null,
                    temperatur_hogsta: null,

                    smarta_nrs_vid_60_min: null,
                    smarta_nrs_hogsta: null,
                    smarta_nrs_vid_utskrivning: null,

                    cirkulatorisk_stabil: null,
                    kommentar_cirkulation: null,

                    eliminationer: [{}],
                    postopkomplikationer: [],
                    news2: [],

                    kad: null,
                    storlek: null,
                    kuff: null,
                    xylocaingel: null,
                },
                postoperativOvervakningSavedFormData: {},
                postoperativOvervakningUpdatedAt: null,
                sangplatser: [],
                postopSmartaSammanfattning: [],
                postoperativkomplikationer: [],
                postoperativkomplikationerSvarighetsgrader: [],
                signVisiblePostoperativOvervakning: false,
                signUrlPostoperativOvervakning: "",
                signDataPostoperativOvervakning: "",
                readonlyPostoperativ: false,

                // Sektion Strålningsregistrering
                stralningsregistreringFormData: {
                    is_signed: false,
                    accountableHealthcareProfessional: "",
                    stralningsregistreringar: [
                        {
                            id: null,
                            namn: null,
                            dosvarde: null,
                            genomlysningsapparat: null,
                            genomlysningstid: null,
                        },
                    ],
                },
                stralningsregistreringSavedFormData: {},
                genomlysningsapparater: [],
                signVisibleStralningsregistrering: false,
                signUrlStralningsregistrering: "",
                signDataStralningsregistrering: "",
                readonlyStralningsregistrering: false, // Används även för Extern Strålningsregistrering
            };
        },

        computed: {
            ...mapGetters("systemData", ["operationXMinuterKvar"]),
            ...mapGetters("userData", ["currentProfile"]),
            ...mapGetters("ability", ["can"]),
            huvudoperatorURLParams() {
                return new URLSearchParams({
                    roll__code: "kirurg",
                    orgUnit: this.operationsanmalan?.operationsenhet,
                    is_active: true,
                });
            },
            patientDataSchema() {
                const schema = this.$store.getters["config/configByName"]({
                    moduleName: ConfigModuleName.Operation,
                    subModuleName: OperationConfigModuleName.OperationJournalPatientdata,
                });
                if (!schema) return [];
                return generateSchema(schema);
            },
            patientDataFormTriggers() {
                const json = this.$store.getters["config/configByName"]({
                    moduleName: ConfigModuleName.Operation,
                    subModuleName: OperationConfigModuleName.OperationJournalPatientdata,
                });

                return json?.triggers ?? [];
            },
            isEmpty() {
                return isEmpty;
            },
            moment() {
                return moment;
            },
            nivaDiffs() {
                return calculateAntalNivaer(this.operationsuppgifterFormData.niva_fran_till) || "-";
            },
            antalDigitum() {
                const digitum = this.operationsuppgifterFormData.digitum;
                if (!digitum) return "-";
                return `${
                    digitum.filter((d) => {
                        if (!d?.id) return false;
                        const val = typeof d.id === "object" ? d.id?.value : d.id;
                        return !!Number(val);
                    }).length
                }`;
            },
            nivaerGroupVisible() {
                return this.operationsanmalan?.kontaktorsak_display?.config?.groups?.nivaer?.visible ?? true;
            },
            digitumGroupVisible() {
                return this.operationsanmalan?.kontaktorsak_display?.config?.groups?.digitum?.visible ?? true;
            },
            hasUnsavedData() {
                if (this.loading) {
                    return;
                }

                const modifiedPostoperativOvervakningSavedFormData = cloneDeep(
                    this.postoperativOvervakningSavedFormData
                );

                /*
                Default sätts i PostavvikelseGroup.vue.
                Vi vill ta med det i jämförelsen, för att undvika varningar om osparad data pga defaultvärde
            */
                if (modifiedPostoperativOvervakningSavedFormData.postopkomplikationer?.length === 0) {
                    modifiedPostoperativOvervakningSavedFormData.postopkomplikationer =
                        this.postoperativOvervakningFormData.postopkomplikationer;
                }

                /*
                    Behövs pga hur KNumber lagrar värden och hur våra gamla formulär lagrar värden.
                    Kan tas bort när allt gått över till KForm
                 */
                modifiedPostoperativOvervakningSavedFormData.temperatur_hogsta =
                    modifiedPostoperativOvervakningSavedFormData.temperatur_hogsta?.replace?.(".", ",");
                modifiedPostoperativOvervakningSavedFormData.temperatur_lagsta =
                    modifiedPostoperativOvervakningSavedFormData.temperatur_lagsta?.replace?.(".", ",");
                modifiedPostoperativOvervakningSavedFormData.temperatur_opslut =
                    modifiedPostoperativOvervakningSavedFormData.temperatur_opslut?.replace?.(".", ",");

                return (
                    this.isNotEqual(this.operationsuppgifterFormData, this.operationsuppgifterSavedFormData) ||
                    this.$refs.patientdataFormRef?.hasUnsavedData() ||
                    this.$refs.stralningsregistreringFormRef?.hasUnsavedData() ||
                    this.isNotEqual(this.personalFormData, this.personalSavedFormData) ||
                    this.isNotEqual(this.materialFormData, this.materialSavedFormData) ||
                    this.isNotEqual(this.anestesiFormData, this.anestesiSavedFormData) ||
                    this.isNotEqual(this.tiderFormData, this.tiderSavedFormData) ||
                    this.isNotEqual(this.postoperativOvervakningFormData, modifiedPostoperativOvervakningSavedFormData)
                );
            },
            getCurrentHourMinute() {
                return moment().format("HH:mm");
            },
            getCurrentDate() {
                return moment().format("YYYY-MM-DD");
            },
            /*
                Returnerar map med index som key och material som value.
                Används för att avgöra om en viss rad i material redan existerar.
                Reglerna:
                - artikel utan batchnummer som krockar med någon annan artikel (artikelnummer)
                - artikel med batchnummer som krockar med artikel inom samma batch (artikelnummer + batchnummer)
            */
            uniqueMaterial() {
                return (
                    this.materialFormData?.material?.reduce((previous, current, index) => {
                        const { artikelnummer, batchnummer } = current;

                        if (!artikelnummer) {
                            return new Map([...previous.entries(), [index, current]]);
                        }

                        if (
                            [...previous.values()].find(
                                (foundMaterial) =>
                                    foundMaterial.artikelnummer === artikelnummer &&
                                    (!batchnummer || batchnummer === foundMaterial.batchnummer)
                            )
                        ) {
                            return previous;
                        }

                        return new Map([...previous.entries(), [index, current]]);
                    }, new Map()) ?? new Map()
                );
            },
            anestesikomplikationSvarighetsgraderVersioner() {
                const versions = Array.from(
                    new Set(this.anestesikomplikationSvarighetsgrader.map((item) => item.codeSystemVersion))
                );
                return versions.length > 1
                    ? `SPOR versioner: ${versions.join(", ")}`
                    : `SPOR version: ${versions.join(", ")}`;
            },
            getOperationsTidInMinute() {
                const { opstart, opslut } = this.tiderFormData;

                return this.calculateTimeDifference(opstart, opslut);
            },
            getBlodtomtTidInMinute() {
                const { blodtomt_falt_start, blodtomt_falt_slut } = this.tiderFormData;

                return this.calculateTimeDifference(blodtomt_falt_start, blodtomt_falt_slut);
            },
        },
        methods: {
            async fetchResurserOptions() {
                try {
                    const response = await klinikenApi.get("operation/resurser/", getConfig({}));
                    return response.data.results.map((item) => convertResursData({ resurs: item }));
                } catch (error) {
                    openDialog("Kunde inte hämta resurser.", "warning");
                }
            },
            async fetchImplantatOptions() {
                try {
                    const response = await klinikenApi.get("operation/implantat/", getConfig({}));
                    return response.data.results.map((item) => convertImplantatData({ implantat: item }));
                } catch (error) {
                    openDialog("Kunde inte hämta implantat.", "warning");
                }
            },
            ...mapActions("tabs", ["closeTab"]),
            ...mapActions("operation/operationoversikt", ["updateOversikt"]),
            ...mapActions("operation/operationsjournal", ["sign"]),
            removeEmpty(obj) {
                return Object.fromEntries(
                    Object.entries(obj)
                        // eslint-disable-next-line no-unused-vars
                        .filter(([_, v]) => v != null && v !== "")
                        .map(([k, v]) => [k, v === Object(v) ? this.removeEmpty(v) : v], this)
                );
            },
            isNotEqual(formData, savedFormData) {
                if ((formData, savedFormData)) {
                    return (
                        JSON.stringify(this.removeEmpty(formData)) !== JSON.stringify(this.removeEmpty(savedFormData))
                    );
                }
            },

            removeError(name) {
                this.$delete(this.formError, name);
            },
            removeErrors(names) {
                const existingErrors = names.filter((name) => name in this.formError);
                if (!existingErrors.length) {
                    return;
                }
                const newErrors = Object.fromEntries(
                    Object.entries(this.formError).filter(([key]) => !existingErrors.includes(key))
                );
                this.formError = newErrors;
            },
            checkAddAsterisk(isRequired) {
                return isRequired ? "*" : "";
            },
            typoperationChanged() {
                if (this.loading) {
                    return;
                }

                let id = this.operationsuppgifterFormData.typoperation;
                if (isNull(id)) {
                    this.removeError("typoperation");
                    return;
                }

                const typoperation = this.typoperationer.find((item) => `${item.id}` === `${id}`);

                if (typoperation === undefined) {
                    return;
                }

                this.removeError("typoperation");

                let operationsuppgifterFormData = {};

                this.huvudoperationskoder = [
                    {
                        value: typoperation.huvudoperationskod_display.id,
                        label: `${typoperation.huvudoperationskod_display.code} ${typoperation.huvudoperationskod_display.displayName}`,
                    },
                ];
                operationsuppgifterFormData.huvudoperationskod = `${typoperation.huvudoperationskod}`;

                operationsuppgifterFormData.bioperationskoder = [{}];
                typoperation.opkoder.forEach((value, index) => {
                    this.$set(this.bioperationskoder, index, [
                        {
                            value: typoperation.opkoder_display[index].id,
                            label: `${typoperation.opkoder_display[index].code} ${typoperation.opkoder_display[index].displayName}`,
                        },
                    ]);
                    operationsuppgifterFormData.bioperationskoder[index] = { bioperationskod: `${value}` };
                });

                this.huvuddiagnoser = [
                    {
                        value: typoperation.huvuddiagnos_display.id,
                        label: `${typoperation.huvuddiagnos_display.code} ${typoperation.huvuddiagnos_display.displayName}`,
                    },
                ];
                operationsuppgifterFormData.huvuddiagnos = `${typoperation.huvuddiagnos}`;

                operationsuppgifterFormData.bidiagnoser = [{}];
                typoperation.bidiagnoser.forEach((value, index) => {
                    this.$set(this.bidiagnoser, index, [
                        {
                            value: typoperation.bidiagnoser_display[index].id,
                            label: `${typoperation.bidiagnoser_display[index].code} ${typoperation.bidiagnoser_display[index].displayName}`,
                        },
                    ]);
                    operationsuppgifterFormData.bidiagnoser[index] = { bidiagnos: `${value}` };
                });

                this.operationsuppgifterFormData = {
                    ...this.operationsuppgifterFormData,
                    ...operationsuppgifterFormData,
                };
            },
            async clearTypoperation() {
                if (
                    await this.$refs.dialog.show({
                        variant: BasicModalVariant.WARNING,
                        title: "De valda operationsuppgifterna stämmer inte med den valda typoperationen. Typoperationen kommer tas bort. Vill du fortsätta?",
                        okButton: "OK",
                        cancelButton: "Avbryt",
                    })
                ) {
                    this.operationsuppgifterFormData.typoperation = null;
                } else {
                    this.typoperationChanged();
                }
            },

            huvuddiagnosChanged() {
                if (isNull(this.operationsuppgifterFormData.huvuddiagnos)) {
                    fetchData({
                        url: "/core/kodverk/icdkoder/",
                    }).then((response) => {
                        this.huvuddiagnoser = cvOptions(response);
                    });
                } else {
                    this.removeError("huvuddiagnos");
                }

                if (!this.loading) {
                    const typoperation = this.typoperationer.find(
                        (item) => `${item.id}` === `${this.operationsuppgifterFormData.typoperation}`
                    );
                    if (
                        typoperation &&
                        `${this.operationsuppgifterFormData.huvuddiagnos}` !== `${typoperation.huvuddiagnos}`
                    ) {
                        this.clearTypoperation();
                    }
                }
            },

            bidiagnosChanged(index) {
                if (isNull(this.operationsuppgifterFormData.bidiagnoser[index].bidiagnos)) {
                    fetchData({
                        url: "/core/kodverk/icdkoder/",
                    }).then((response) => {
                        this.$set(this.bidiagnoser, index, cvOptions(response));
                    });
                }

                if (!this.loading) {
                    const typoperation = this.typoperationer.find(
                        (item) => `${item.id}` === `${this.operationsuppgifterFormData.typoperation}`
                    );
                    if (
                        typoperation &&
                        `${this.operationsuppgifterFormData.bidiagnoser[index].bidiagnos}` !==
                            `${typoperation.bidiagnoser[index]}`
                    ) {
                        this.clearTypoperation();
                    }
                }
            },

            huvudoperationskodChanged() {
                if (isNull(this.operationsuppgifterFormData.huvudoperationskod)) {
                    fetchData({
                        url: "/operation/kodverk/operationskoder/",
                    }).then((response) => {
                        this.huvudoperationskoder = cvOptions(response);
                    });
                } else {
                    this.removeError("huvudoperationskod");
                }

                if (!this.loading) {
                    const typoperation = this.typoperationer.find(
                        (item) => `${item.id}` === `${this.operationsuppgifterFormData.typoperation}`
                    );
                    if (
                        typoperation &&
                        `${this.operationsuppgifterFormData.huvudoperationskod}` !==
                            `${typoperation.huvudoperationskod}`
                    ) {
                        this.clearTypoperation();
                    }
                }
            },

            bioperationskodChanged(index) {
                if (isNull(this.operationsuppgifterFormData.bioperationskoder[index].bioperationskod)) {
                    fetchData({
                        url: "/operation/kodverk/operationskoder/",
                    }).then((response) => {
                        this.$set(this.bioperationskoder, index, cvOptions(response));
                    });
                }

                if (!this.loading) {
                    const typoperation = this.typoperationer.find(
                        (item) => `${item.id}` === `${this.operationsuppgifterFormData.typoperation}`
                    );
                    if (
                        typoperation &&
                        `${this.operationsuppgifterFormData.bioperationskoder[index].bioperationskod}` !==
                            `${typoperation.opkoder[index]}`
                    ) {
                        this.clearTypoperation();
                    }
                }
            },

            async searchHuvudoperationskod(search, loading) {
                if (search.trim().length >= 3) {
                    loading(true);
                    this.huvudoperationskoder = cvOptions(
                        await fetchData({
                            url: "/operation/kodverk/operationskoder/all/",
                            params: { q: search },
                        })
                    );
                    loading(false);
                }
            },

            async searchBioperationskoder(index, search, loading) {
                if (search.trim().length >= 3) {
                    loading(true);
                    const options = cvOptions(
                        await fetchData({
                            url: "/operation/kodverk/operationskoder/all/",
                            params: { q: search },
                        })
                    );
                    this.$set(this.bioperationskoder, index, options);
                    loading(false);
                }
            },

            removeBioperationskoder(index, removeItem) {
                removeItem(index);
                let bioperationskoder = this.bioperationskoder;
                bioperationskoder.splice(index, 1);
                for (let i = 0; i < this.bioperationskoder.length; i++) {
                    this.$set(this.bioperationskoder, i, bioperationskoder[i]);
                }
            },

            async searchHuvuddiagnos(search, loading) {
                if (search.trim().length >= 3) {
                    loading(true);
                    this.huvuddiagnoser = cvOptions(
                        await fetchData({
                            url: "/core/kodverk/icdkoder/all/",
                            params: { q: search },
                        })
                    );
                    loading(false);
                }
            },

            async searchBidiagnoser(index, search, loading) {
                if (search.trim().length >= 3) {
                    loading(true);
                    const options = cvOptions(
                        await fetchData({
                            url: "/core/kodverk/icdkoder/all/",
                            params: { q: search },
                        })
                    );
                    this.$set(this.bidiagnoser, index, options);
                    loading(false);
                }
            },

            removeBidiagnoser(index, removeItem) {
                removeItem(index);
                let bidiagnoser = this.bidiagnoser;
                bidiagnoser.splice(index, 1);
                for (let i = 0; i < this.bidiagnoser.length; i++) {
                    this.$set(this.bidiagnoser, i, bidiagnoser[i]);
                }
            },

            async addMoreBidiagnoser(addMore) {
                addMore();
                let bidiagnoser = cvOptions(
                    await fetchData({
                        url: "/core/kodverk/icdkoder/",
                    })
                );

                this.$set(this.bidiagnoser, this.bidiagnoser.length, bidiagnoser);
            },

            async addMoreBioperationskoder(addMore) {
                addMore();

                let bioperationskoder = cvOptions(
                    await fetchData({
                        url: "/operation/kodverk/operationskoder/",
                    })
                );

                this.$set(this.bioperationskoder, this.bioperationskoder.length, bioperationskoder);
            },

            async searchProfile(url, search, loading) {
                loading(true);
                const options = profileOptions(
                    await fetchData({
                        url: url,
                        params: { q: search },
                    })
                );
                loading(false);
                return options;
            },

            personalChanged(index, fieldName) {
                this.removeError(`${fieldName}.${index}.profile`);
                if (this.personalFormData[fieldName][index].profile === null) {
                    this.personalFormData[fieldName][index] = {};
                }
            },
            gasterChanged(index, fieldName) {
                this.removeError(`gaster.${index}.${fieldName}`);
            },

            async addMoreAssOperatorer(addMore) {
                addMore();

                const ass_operatorer = profileOptions(
                    await fetchData({
                        url: "core/profiles/?roll__code=kirurg",
                    })
                );

                this.$set(this.ass_operatorer, this.ass_operatorer.length, ass_operatorer);
            },

            async addMoreOperationssjukskoterska(addMore) {
                addMore();

                const operationssjukskoterskor = profileOptions(
                    await fetchData({
                        url: "core/profiles/?roll__code=operationssjukskoterska",
                    })
                );

                this.$set(
                    this.operationssjukskoterskor,
                    this.operationssjukskoterskor.length,
                    operationssjukskoterskor
                );
            },

            async addMoreOperationsunderskoterska(addMore) {
                addMore();

                const operationsunderskoterskor = profileOptions(
                    await fetchData({
                        url: "core/profiles/?roll__code=operationsunderskoterska",
                    })
                );

                this.$set(
                    this.operationsunderskoterskor,
                    this.operationsunderskoterskor.length,
                    operationsunderskoterskor
                );
            },

            async searchOperatorer(search, loading) {
                if (search.trim().length >= 2) {
                    this.operatorer = await this.searchProfile(
                        `core/profiles/?${this.huvudoperatorURLParams.toString()}`,
                        search,
                        loading
                    );
                }
            },

            removeOpassistenter(index, removeItem) {
                removeItem(index);
            },

            removeOperationssjukskoterska(index, removeItem) {
                removeItem(index);
            },

            removeOperationsunderskoterska(index, removeItem) {
                removeItem(index);
            },
            toggleGastPaSal(e) {
                const gastPaSal = e.target.checked;
                this.personalFormData.gaster = [{ namn: null, roll: null }];

                if (!gastPaSal) {
                    this.removeError("gaster.0.namn");
                    this.removeError("gaster.0.roll");
                }
            },

            async addMaterial(sentValue) {
                const currentMaterial = [...(this.materialFormData.material ?? [])];

                if (sentValue.type === "grupp") {
                    this.$set(this.materialFormData, "material", [
                        ...sentValue.value.material.map((singleMaterial) => ({
                            antal: singleMaterial.antal,
                            material: singleMaterial.material.id,
                            materialnamn: singleMaterial.material.namn,
                            artikelnummer: singleMaterial.material.artikelnummer,
                        })),
                        ...currentMaterial,
                    ]);

                    return;
                }

                const material = sentValue.value;

                this.$set(this.materialFormData, "material", [
                    {
                        material: material.id,
                        materialnamn: material.namn,
                        artikelnummer: material.artikelnummer,
                        antal: 1,
                    },
                    ...currentMaterial,
                ]);
            },

            async searchAnestesilakare(search, loading) {
                if (search.trim().length >= 2) {
                    this.anestesilakare = await this.searchProfile(
                        "core/profiles/?roll__code=anestesilakare",
                        search,
                        loading
                    );
                } else if (search.trim().length < 1) {
                    this.anestesilakare = await this.searchProfile(
                        "core/profiles/?roll__code=anestesilakare",
                        "",
                        loading
                    );
                }
            },

            huvudanestesikodChanged() {
                if (isNull(this.anestesiFormData.huvudanestesikod)) {
                    fetchData({
                        url: "/operation/kodverk/anestesikoder/all/",
                        filename: "Operationsjournal.vue",
                        description: "Hämta huvudanestesikoder",
                    }).then((response) => {
                        this.huvudanestesikoder = cvOptions(response);
                    });
                } else {
                    this.removeError("huvudanestesikod");
                }
            },

            anestesikomplikationerKategorierChanged(index) {
                const options = anestesikomplikationerOptions(
                    this.rawAnestesikomplikationer.filter(
                        (obj) => obj.kapitel === this.anestesiFormData.anestesikomplikationer[index].kategori
                    )
                );
                this.$set(this.anestesikomplikationer, index, options);

                if (this.anestesiFormData.anestesikomplikationer[index].kategori === "A0") {
                    const obj = this.rawAnestesikomplikationer.find((obj) => obj.code === "A0");

                    // Set for form data
                    this.$set(this.anestesiFormData.anestesikomplikationer, index, {
                        kategori: this.anestesiFormData.anestesikomplikationer[index].kategori,
                        kod: `${obj.id}`,
                        svarighetsgrad: "1",
                    });

                    // Set for saved form data
                    this.$set(this.anestesiSavedFormData.anestesikomplikationer, index, {
                        kategori: this.anestesiSavedFormData.anestesikomplikationer[index].kategori,
                        kod: `${obj.id}`,
                        svarighetsgrad: "1",
                    });

                    this.removeError("anestesikomplikationer");
                    this.removeError(`anestesikomplikationer.${index}.kod`);
                    this.removeError(`anestesikomplikationer.${index}.svarighetsgrad`);
                } else {
                    // Set for form data
                    this.$set(this.anestesiFormData.anestesikomplikationer, index, {
                        kategori: this.anestesiFormData.anestesikomplikationer[index].kategori,
                        kod: "",
                        svarighetsgrad: "",
                    });

                    // Set for saved form data
                    this.$set(this.anestesiSavedFormData.anestesikomplikationer, index, {
                        kategori: this.anestesiSavedFormData.anestesikomplikationer[index].kategori,
                        kod: "",
                        svarighetsgrad: "",
                    });
                }
            },
            anestesikomplikationChanged(index, v) {
                if (v === null) {
                    this.$set(this.anestesiFormData.anestesikomplikationer, index, {
                        kod: "",
                        svarighetsgrad: "",
                    });
                }
                this.removeError("anestesikomplikationer");
            },

            anestesikomplikationerErrorChanged(name, index) {
                this.removeError(`anestesikomplikationer.${index}.${name}`);
            },
            removeAnestesikomplikationer(index, removeItem) {
                removeItem(index);
                let anestesikomplikationer = this.anestesikomplikationer;
                anestesikomplikationer.splice(index, 1);
                for (let i = 0; i < this.anestesikomplikationer.length; i++) {
                    this.$set(this.anestesikomplikationer, i, anestesikomplikationer[i]);
                }
            },

            async searchHuvudanestesikod(search, loading) {
                if (search.trim().length >= 3) {
                    loading(true);
                    this.huvudanestesikoder = cvOptions(
                        await fetchData({
                            url: "/operation/kodverk/anestesikoder/all/",
                            params: { q: search },
                        })
                    );
                    loading(false);
                }
            },

            async searchAnestesisjukskoterska(_, search, loading) {
                if (search.trim().length >= 3) {
                    loading(true);
                    this.anestesisjukskoterskor = profileOptions(
                        await fetchData({
                            url: "core/profiles/?roll__code=anestesisjukskoterska",
                            params: { q: search },
                        })
                    );
                    loading(false);
                } else if (search.trim().length < 1) {
                    this.anestesisjukskoterskor = profileOptions(
                        await fetchData({
                            url: "core/profiles/?roll__code=anestesisjukskoterska",
                            params: { q: search },
                        })
                    );
                }
            },

            async searchBianestesikoder(index, search, loading) {
                if (search.trim().length >= 3) {
                    loading(true);
                    const anestesikoder = cvOptions(
                        await fetchData({
                            url: "/operation/kodverk/anestesikoder/all/",
                            params: { q: search },
                        })
                    );
                    this.$set(this.bianestesikoder, index, anestesikoder);
                    loading(false);
                }
            },

            removeBianestesikoder(index, removeItem) {
                removeItem(index);
                let bianestesikoder = this.bianestesikoder;
                bianestesikoder.splice(index, 1);
                for (let i = 0; i < this.bianestesikoder.length; i++) {
                    this.$set(this.bianestesikoder, i, bianestesikoder[i]);
                }
            },

            async addMoreBianestesikoder(addMore) {
                addMore();
                let bianestesikoder = cvOptions(
                    await fetchData({
                        url: "/operation/kodverk/anestesikoder/all/",
                    })
                );

                this.$set(this.bianestesikoder, this.bianestesikoder.length, bianestesikoder);
            },

            removeNullFromObject(payload) {
                const filteredDataEntries = Object.entries(payload).filter(([_, value]) => {
                    return value !== null;
                });
                return Object.fromEntries(filteredDataEntries);
            },

            // Ska hela översiktslistan refreshas och ska det göras här?
            async updateEntry(id) {
                try {
                    const response = await fetchData({
                        url: `/operation/oversikt/${id}/`,
                    });
                    await this.updateOversikt(response);
                } catch (error) {
                    openDialog(error, "error");
                }
            },
            // validering vid submit - ej KForm-formulär
            async checkValidateOnSubmit(button, manualSubmit, refName) {
                // ingen validering på vanlig save
                if (button === "save") {
                    return true;
                }
                /**
                 * Manuell submit: vi måste anropa formSubmitted (vue formulate) manuellt för validering
                 */
                if (manualSubmit) {
                    await this.$refs[refName].formSubmitted();
                }
                // Blockerar submit om det finns valideringsfel
                if (await this.$refs[refName].hasValidationErrors()) {
                    scrollToAndFocusError(this.$refs[refName].$el, this.$nextTick);
                    return false;
                }
                return true;
            },
            async submitOperationsuppgifter({ button, signeringsansvarig }, manualSubmit = false) {
                const validationResult = await this.checkValidateOnSubmit(
                    button,
                    manualSubmit,
                    "operationsuppgifterFormRef"
                );
                if (!validationResult) {
                    return;
                }

                let payload = cloneDeep(this.operationsuppgifterFormData);

                if (payload.typoperation === "null") {
                    delete payload.typoperation;
                }

                if (payload.bidiagnoser) {
                    payload.bidiagnoser = this.extractValues(payload.bidiagnoser);
                    payload.bidiagnoser = removeEmptyObjectsFromArray(payload.bidiagnoser);
                }
                if (payload.bioperationskoder) {
                    payload.bioperationskoder = this.extractValues(payload.bioperationskoder);
                    payload.bioperationskoder = removeEmptyObjectsFromArray(payload.bioperationskoder);
                }
                if (payload.niva_fran_till) {
                    payload.niva_fran_till = dynamicPropsRepeaterConverter({ value: payload.niva_fran_till }).filter(
                        (item) => item.fran && item.till
                    );
                }
                if (payload.digitum) {
                    payload.digitum = defaultRepeaterConverter({
                        value: payload.digitum.filter((item) => item?.id != null),
                        alwaysArray: true,
                    });
                }
                if (payload.resurser) {
                    payload.resurser = defaultRepeaterConverter({
                        value: payload.resurser.filter((item) => item?.id != null),
                        alwaysArray: true,
                    });
                }
                if (payload.implantat) {
                    payload.implantat = defaultRepeaterConverter({
                        value: payload.implantat.filter((item) => item?.id != null),
                        alwaysArray: true,
                    });
                }
                if (button === "save") {
                    const result = await this.submit(
                        payload,
                        "Operationsuppgifter sparade",
                        "operationsuppgifter/",
                        signeringsansvarig,
                        "Operationsuppgifter"
                    );

                    if (result?.error) {
                        scrollToAndFocusError(this.$refs.operationsuppgifterFormRef.$el, this.$nextTick);
                    }
                    this.operationsuppgifterSavedFormData = this.operationsuppgifterFormData;
                } else if (button === "sign") {
                    await this.signSectionOperationsuppgifter({
                        payload: payload,
                        signeringsansvarig: signeringsansvarig,
                    });
                }
            },

            async signSectionOperationsuppgifter({ payload, signeringsansvarig }) {
                payload.id = this.operationsanmalan.id;
                payload.accountableHealthcareProfessional = signeringsansvarig;

                const missingFields = checkRequired(["huvuddiagnos", "huvudoperationskod"], payload);

                if (missingFields.length > 0) {
                    scrollToAndFocusError(this.$refs.operationsuppgifterFormRef.$el, this.$nextTick);
                    this.formError = createErrorForMissingFields(missingFields);

                    return;
                }

                const url = `/operation/operationsjournaler/${this.operationsanmalan.id}/operationsuppgifter/`;

                this.signDataOperationsuppgifter = payload;
                this.signUrlOperationsuppgifter = url;
                this.signVisibleOperationsuppgifter = true;
            },

            async handleSignOperationsuppgifterSuccess(data) {
                this.operationsuppgifterFormData.is_signed = data.is_signed;
                this.operationsuppgifterSavedFormData = cloneDeep(this.operationsuppgifterFormData);

                this.signVisibleOperationsuppgifter = false;
            },

            handleSignOperationsuppgifterError(errors) {
                scrollToAndFocusError(this.$refs.operationsuppgifterFormRef.$el, this.$nextTick);

                if (typeof errors === "object") {
                    this.formError = collectErrors(errors);
                } else {
                    openDialog(errors, "error");
                }
                this.signVisibleOperationsuppgifter = false;
            },

            handleSignOperationsuppgifterCancel() {
                this.signVisibleOperationsuppgifter = false;
            },

            // Används för att formatera bidiagnoskoder, bioperationskoder och bianestesikoder i payload till backend
            extractValues(list) {
                return list.map((item) => getPropertyValue(item));
            },

            async submitPatientdata({ button, signeringsansvarig }) {
                let patientFormData = null;

                // ingen validering på vanlig save
                if (button === "save") {
                    patientFormData = await this.$refs.patientdataFormRef?.manualSubmit(true);
                } else {
                    patientFormData = await this.$refs.patientdataFormRef?.manualSubmit();
                }
                if (!patientFormData) {
                    return false;
                }
                this.saveLoading = true;
                let payload = cloneDeep({ patientdata: patientFormData });
                payload.accountableHealthcareProfessional = signeringsansvarig;
                if (button === "save") {
                    const result = await this.submit(
                        payload,
                        "Patientdata sparad",
                        "patientdata/",
                        signeringsansvarig,
                        "Patientdata"
                    );
                    if (result?.error) {
                        scrollToAndFocusError(this.$refs.patientdataFormRef.$el, this.$nextTick);
                    }

                    this.patientdataSavedFormData = cloneDeep({ patientdata: patientFormData });
                } else if (button === "sign") {
                    this.signSectionPatientdata({
                        signeringsansvarig: signeringsansvarig,
                        payload: payload,
                    });
                }
                this.saveLoading = false;
            },

            async signSectionPatientdata({ payload, signeringsansvarig }) {
                payload.id = this.operationsanmalan.id;
                payload.accountableHealthcareProfessional = signeringsansvarig;

                const url = `/operation/operationsjournaler/${this.operationsanmalan.id}/patientdata/`;

                this.signDataPatientdata = payload;
                this.signUrlPatientdata = url;
                this.signVisiblePatientdata = true;
            },

            handleSignPatientdataSuccess(data) {
                this.patientdataFormData.is_signed = data.is_signed;
                this.patientdataSavedFormData = cloneDeep(this.patientdataFormData);

                this.signVisiblePatientdata = false;
            },

            handleSignPatientdataError(errors) {
                scrollToAndFocusError(this.$refs.patientdataFormRef.$el, this.$nextTick);

                if (typeof errors === "object") {
                    this.formError = collectErrors(errors);
                } else {
                    openDialog(errors, "error");
                }

                this.signVisiblePatientdata = false;
            },

            handleSignPatientdataCancel() {
                this.signVisiblePatientdata = false;
            },

            async submitPostoperativ({ button, signeringsansvarig }, manualSubmit = false) {
                const validationResult = await this.checkValidateOnSubmit(button, manualSubmit, "postoperativFormRef");
                if (!validationResult) {
                    return;
                }

                this.formError = {}; // Manually reset the form-error object
                this.saveLoading = true;
                let payload = cloneDeep(this.postoperativOvervakningFormData);

                payload.accountableHealthcareProfessional = signeringsansvarig;

                payload.tid_in = this.setDateOnTid(this.postoperativOvervakningFormData.tid_in);
                payload.tid_klar_for_utskrivning = this.setDateOnTid(
                    this.postoperativOvervakningFormData.tid_klar_for_utskrivning
                );
                payload.tid_ut = this.setDateOnTid(this.postoperativOvervakningFormData.tid_ut);

                payload.omlaggning_forband = this.parseBooleanString(payload.omlaggning_forband);
                payload.cirkulatorisk_stabil = this.parseBooleanString(payload.cirkulatorisk_stabil);

                // replaceSeparator() behövs pga <KNumber> istället för <FormulateInput type="knumber">
                payload.temperatur_opslut = replaceSeparator({ value: payload.temperatur_opslut, toComma: false });
                payload.temperatur_lagsta = replaceSeparator({ value: payload.temperatur_lagsta, toComma: false });
                payload.temperatur_hogsta = replaceSeparator({ value: payload.temperatur_hogsta, toComma: false });

                payload.eliminationer.forEach((item) => {
                    if (item.urintappning === "") {
                        item.urintappning = null;
                    } else {
                        item.tid_for_scanning = this.setDateOnTid(item.tid_for_scanning);
                    }
                });

                payload.postopkomplikationer = this.postoperativOvervakningFormData.postopkomplikationer.reduce(
                    (filteredKomplikationer, komplikation) => {
                        if (komplikation.kod) {
                            return [
                                ...filteredKomplikationer,
                                {
                                    ...komplikation,
                                    tidpunkt: this.setDateOnTid(komplikation.tidpunkt),
                                },
                            ];
                        }

                        return filteredKomplikationer;
                    },
                    []
                );

                const filteredNews2Entries = filterEmptyNews2Entries(this.postoperativOvervakningFormData.news2);

                if (filteredNews2Entries.length !== this.postoperativOvervakningFormData.news2.length) {
                    this.postoperativOvervakningFormData.news2 = filteredNews2Entries;
                    await this.$nextTick();
                }

                if (filteredNews2Entries.length > 0) {
                    const missingNews2Fields = checkRequired([["news2", NEWS2_REQUIRED_FIELDS, 1]], {
                        news2: filteredNews2Entries,
                    });

                    if (missingNews2Fields.length > 0) {
                        this.formError = createErrorForMissingFields(missingNews2Fields);
                        scrollToAndFocusError(this.$refs.postoperativFormRef.$el, this.$nextTick);
                        this.saveLoading = false;
                        return;
                    }
                }

                payload.news2 = filteredNews2Entries.map((news2Entry) => this.mapNews2BedomningForPayload(news2Entry));

                if (button === "save") {
                    const result = await this.submit(
                        payload,
                        "Postoperativ övervakning sparad",
                        "postop/",
                        signeringsansvarig,
                        "Postoperativ"
                    );

                    if (result?.error) {
                        scrollToAndFocusError(this.$refs.postoperativFormRef.$el, this.$nextTick);
                    }
                    this.postoperativOvervakningUpdatedAt = result?.data?.updated_at
                        ? moment(result.data.updated_at).format("YYYY-MM-DD HH:mm")
                        : this.postoperativOvervakningUpdatedAt;
                    this.postoperativOvervakningSavedFormData = cloneDeep(this.postoperativOvervakningFormData);

                    if (this.postoperativOvervakningSavedFormData.tid_ut) {
                        emitter.emit("pagaende-op-tider-updated");
                    }
                } else if (button === "sign") {
                    this.signSectionPostoperativ({
                        signeringsansvarig: signeringsansvarig,
                        payload: payload,
                    });
                }
                this.saveLoading = false;
            },
            async signSectionPostoperativ({ payload, signeringsansvarig }) {
                payload.id = this.operationsanmalan.id;
                payload.accountableHealthcareProfessional = signeringsansvarig;

                // replaceSeparator() behövs pga <KNumber> istället för <FormulateInput type="knumber">
                payload.temperatur_opslut = replaceSeparator({ value: payload.temperatur_opslut, toComma: false });
                payload.temperatur_lagsta = replaceSeparator({ value: payload.temperatur_lagsta, toComma: false });
                payload.temperatur_hogsta = replaceSeparator({ value: payload.temperatur_hogsta, toComma: false });

                const url = `/operation/operationsjournaler/${this.operationsanmalan.id}/postop/`;

                this.signDataPostoperativOvervakning = payload;
                this.signUrlPostoperativOvervakning = url;
                this.signVisiblePostoperativOvervakning = true;
            },
            async submitStralningsregistrering({ button, signeringsansvarig }) {
                let stralningsregistreringFormData = null;

                // ingen validering på vanlig save
                if (button === "save") {
                    stralningsregistreringFormData = await this.$refs.stralningsregistreringFormRef?.manualSubmit(true);
                } else {
                    stralningsregistreringFormData = await this.$refs.stralningsregistreringFormRef?.manualSubmit();
                }
                if (!stralningsregistreringFormData) {
                    return false;
                }
                this.saveLoading = true;

                if (button === "save") {
                    const result = await this.submit(
                        stralningsregistreringFormData,
                        "Strålningsregistrering sparad",
                        "stralningsregistrering/",
                        signeringsansvarig,
                        "Strålningsregistrering"
                    );
                    if (result?.error) {
                        scrollToAndFocusError(this.$refs.stralningsregistreringFormRef.$el, this.$nextTick);
                    }
                } else if (button === "sign") {
                    this.signSectionStralningsregistrering({
                        signeringsansvarig: signeringsansvarig,
                        payload: stralningsregistreringFormData,
                    });
                }
                this.saveLoading = false;
            },

            async signSectionStralningsregistrering({ payload, signeringsansvarig }) {
                payload.id = this.operationsanmalan.id;
                payload.accountableHealthcareProfessional = signeringsansvarig;

                const url = `/operation/operationsjournaler/${this.operationsanmalan.id}/stralningsregistrering/`;

                this.signDataStralningsregistrering = payload;
                this.signUrlStralningsregistrering = url;
                this.signVisibleStralningsregistrering = true;
            },

            async handleSignPostoperativSuccess(data) {
                if (data && Object.keys(data).length > 0) {
                    this.postoperativOvervakningFormData.is_signed = data.is_signed;
                    this.postoperativOvervakningUpdatedAt = data.updated_at
                        ? moment(data.updated_at).format("YYYY-MM-DD HH:mm")
                        : this.postoperativOvervakningUpdatedAt;
                    this.postoperativOvervakningSavedFormData = cloneDeep(this.postoperativOvervakningFormData);
                }

                this.signVisiblePostoperativOvervakning = false;
            },

            handleSignPostoperativError(errors) {
                if (typeof errors === "object") {
                    this.formError = collectErrors(errors);
                } else {
                    openDialog(errors, "error");
                }
                this.signVisiblePostoperativOvervakning = false;
            },

            handleSignPostoperativCancel() {
                this.signVisiblePostoperativOvervakning = false;
            },

            async handleSignStralningsregistreringSuccess(data) {
                if (data && Object.keys(data).length > 0) {
                    this.stralningsregistreringFormData.is_signed = data.is_signed;
                    this.stralningsregistreringSavedFormData = cloneDeep(this.stralningsregistreringFormData);
                }

                this.signVisibleStralningsregistrering = false;
            },

            handleSignStralningsregistreringError(errors) {
                scrollToAndFocusError(this.$refs.stralningsregistreringFormRef.$el, this.$nextTick);

                if (typeof errors === "object") {
                    this.formError = collectErrors(errors);
                } else {
                    openDialog(errors, "error");
                }

                this.signVisibleStralningsregistrering = false;
            },

            handleSignStralningsregistreringCancel() {
                this.signVisibleStralningsregistrering = false;
            },

            handleUrintappningMl(value, index) {
                if (value !== "JA") {
                    delete this.postoperativOvervakningFormData.eliminationer?.[index]?.urintappning_ml; // Hantera på ett bärtre sätt för vue3 migrationen
                }
            },

            formatPersonalGroup(payload, name) {
                // Tar bort rader utan vald profil
                payload[name] = payload[name].filter((obj) => Boolean(obj.profile));
                // Extraherar uuid från profilen
                payload[name].forEach((obj) => (obj.profile = obj.profile.value ?? obj.profile));
                // Lägger till datum på in- och uttider
                payload[name].forEach((item, index) => {
                    payload[name][index].intid = this.setDateOnTid(item.intid);
                    payload[name][index].uttid = this.setDateOnTid(item.uttid);
                });
            },
            async submitPersonal({ button, signeringsansvarig }, manualSubmit = false) {
                const validationResult = await this.checkValidateOnSubmit(button, manualSubmit, "personalFormRef");
                if (!validationResult) {
                    return;
                }
                let payload = cloneDeep(this.personalFormData);
                this.saveLoading = true;

                payload.huvudoperator_intid = this.setDateOnTid(payload.huvudoperator_intid);
                payload.huvudoperator_uttid = this.setDateOnTid(payload.huvudoperator_uttid);

                payload.anestesilakare_intid = this.setDateOnTid(payload.anestesilakare_intid);
                payload.anestesilakare_uttid = this.setDateOnTid(payload.anestesilakare_uttid);

                if (payload.ass_operatorer) this.formatPersonalGroup(payload, "ass_operatorer");
                if (payload.operationssjukskoterskor) this.formatPersonalGroup(payload, "operationssjukskoterskor");
                if (payload.operationsunderskoterskor) this.formatPersonalGroup(payload, "operationsunderskoterskor");
                if (payload.anestesisjukskoterskor) this.formatPersonalGroup(payload, "anestesisjukskoterskor");

                if (!payload.gast_pa_sal && payload.gaster) payload.gaster = null;

                // FormulateForm hanterar som default tomma värden som tomma strängar, och det tycker backend inte om
                if (payload.antal_dorroppningar === "") payload.antal_dorroppningar = null;

                if (button === "save") {
                    const result = await this.submit(
                        payload,
                        "Personal sparad",
                        "personal/",
                        signeringsansvarig,
                        "Personal"
                    );
                    if (result?.error) {
                        scrollToAndFocusError(this.$refs.personalFormRef.$el, this.$nextTick);
                    }
                    this.personalSavedFormData = cloneDeep(this.personalFormData);
                } else if (button === "sign") {
                    await this.signSectionPersonal({
                        signeringsansvarig: signeringsansvarig,
                        payload: payload,
                    });
                }
                this.saveLoading = false;
            },

            async signSectionPersonal({ payload, signeringsansvarig }) {
                payload.id = this.operationsanmalan.id;
                payload.accountableHealthcareProfessional = signeringsansvarig;

                const requiredFields = ["huvudoperator"];
                if (payload.gast_pa_sal) {
                    requiredFields.push(["gaster", ["namn", "roll"], 1]);
                }
                const missingFields = checkRequired(requiredFields, payload);

                if (missingFields.length > 0) {
                    scrollToAndFocusError(this.$refs.personalFormRef.$el, this.$nextTick);
                    this.formError = createErrorForMissingFields(missingFields);

                    return;
                }

                const url = `/operation/operationsjournaler/${this.operationsanmalan.id}/personal/`;

                this.signDataPersonal = payload;
                this.signUrlPersonal = url;
                this.signVisiblePersonal = true;
            },

            async handleSignPersonalSuccess(data) {
                this.personalFormData.is_signed = data.is_signed;
                this.personalSavedFormData = cloneDeep(this.personalFormData);

                this.signVisiblePersonal = false;
            },

            handleSignPersonalError(errors) {
                scrollToAndFocusError(this.$refs.personalFormRef.$el, this.$nextTick);

                if (typeof errors === "object") {
                    this.formError = collectErrors(errors);
                } else {
                    openDialog(errors, "error");
                }
                this.signVisiblePersonal = false;
            },

            handleSignPersonalCancel() {
                this.signVisiblePersonal = false;
            },

            async submitWHOchecklistaForberedelse(data) {
                this.showWHOchecklistaForberedelse = false;

                const payload = this.removeNullFromObject(cloneDeep(data));
                payload.is_completed = true;

                const response = await this.submit(
                    payload,
                    "WHO-checklista 1 - Förberedelse sparad",
                    "whochecklista/forberedelse/",
                    undefined,
                    "WHO-checklista 1 - Förberedelse"
                );

                if (response.success) {
                    const { data } = response;
                    this.whochecklistaForberedelseFormData.updated_by = data.updated_by;
                    this.whochecklistaForberedelseFormData.updated_at = moment(data.updated_at).format("YYYY-MM-DD");
                    this.whochecklistaForberedelseFormData.is_completed = data.is_completed;
                }
            },

            async submitWHOchecklistaTimeOut(data) {
                this.showWHOchecklistaTimeOut = false;

                const payload = this.removeNullFromObject(cloneDeep(this.whochecklistaTimeoutFormData));
                payload.is_completed = true;

                const response = await this.submit(
                    payload,
                    "WHO-checklista 2 - Timeout sparad",
                    "whochecklista/timeout/",
                    undefined,
                    "WHO-checklista 2 - Timeout"
                );

                if (response.success) {
                    const { data } = response;
                    this.whochecklistaTimeoutFormData.updated_by = data.updated_by;
                    this.whochecklistaTimeoutFormData.updated_at = moment(data.updated_at).format("YYYY-MM-DD");
                    this.whochecklistaTimeoutFormData.is_completed = data.is_completed;
                }
            },

            async submitWHOchecklistaAvslutning(data) {
                this.showWHOchecklistaAvslutning = false;

                const payload = this.removeNullFromObject(cloneDeep(data));
                payload.is_completed = true;

                const response = await this.submit(
                    payload,
                    "WHO-checklista 3 - Avslutning sparad",
                    "whochecklista/avslutning/",
                    undefined,
                    "WHO-checklista 3 - Avslutning"
                );

                if (response.success) {
                    const { data } = response;
                    this.whochecklistaAvslutningFormData = data;
                    this.whochecklistaAvslutningFormData.updated_at = moment(data.updated_at).format("YYYY-MM-DD");
                }
            },

            async completeWhochecklistaForberedelse() {
                this.loading = true;

                /* Om värdet avtogglas så ska formuläret resettas */
                if (this.whochecklistaForberedelseFormData.is_completed) {
                    // Check if any field in the formData is not null and
                    const hasValues = !isEmpty(this.whochecklistaForberedelseFormData.updated_by);

                    this.whochecklistaForberedelseFormData = {
                        is_completed: false,
                        has_values: hasValues,
                    };

                    const response = await this.submit(
                        cloneDeep(this.whochecklistaForberedelseFormData),
                        "WHO-checklista 1 - Förberedelse sparad",
                        "whochecklista/forberedelse/",
                        undefined,
                        "WHO-checklista 1 - Förberedelse"
                    );

                    if (response.success) {
                        this.whochecklistaForberedelseFormData = response?.data;
                    }

                    this.loading = false;
                    return;
                }

                this.showWHOchecklistaForberedelse = false;

                const payload = this.removeNullFromObject(cloneDeep(this.whochecklistaForberedelseFormData));

                payload.is_completed = true;

                const response = await this.submit(
                    payload,
                    "WHO-checklista 1 - Förberedelse sparad",
                    "whochecklista/forberedelse/",
                    undefined,
                    "WHO-checklista 1 - Förberedelse"
                );

                if (response.success) {
                    const { data } = response;
                    this.whochecklistaForberedelseFormData.updated_by = data.updated_by;
                    this.whochecklistaForberedelseFormData.updated_at = moment(data.updated_at).format("YYYY-MM-DD");
                    this.whochecklistaForberedelseFormData.is_completed = data.is_completed;
                }

                this.loading = false;
            },

            async completeWhochecklistaTimeout() {
                this.loading = true;

                if (this.whochecklistaTimeoutFormData.is_completed) {
                    const hasValues = !isEmpty(this.whochecklistaTimeoutFormData.updated_by);

                    this.whochecklistaTimeoutFormData = {
                        has_values: hasValues,
                        is_completed: false,
                    };

                    const response = await this.submit(
                        cloneDeep(this.whochecklistaTimeoutFormData),
                        "WHO-checklista 2 - Timeout sparad",
                        "whochecklista/timeout/",
                        undefined,
                        "WHO-checklista 2 - Timeout"
                    );

                    if (response.success) {
                        this.whochecklistaTimeoutFormData = response?.data;
                    }

                    this.loading = false;
                    return;
                }

                this.showWHOchecklistaTimeOut = false;

                const payload = this.removeNullFromObject(cloneDeep(this.whochecklistaTimeoutFormData));

                payload.is_completed = true;

                const response = await this.submit(
                    payload,
                    "WHO-checklista 2 - Timeout sparad",
                    "whochecklista/timeout/",
                    undefined,
                    "WHO-checklista 2 - Timeout"
                );

                if (response.success) {
                    this.whochecklistaTimeoutFormData = response?.data;
                }

                this.loading = false;
            },

            async completeWhochecklistaAvslutning() {
                this.loading = true;

                if (this.whochecklistaAvslutningFormData.is_completed) {
                    const hasValues = !isEmpty(this.whochecklistaAvslutningFormData.updated_by);
                    this.whochecklistaAvslutningFormData = {
                        has_values: hasValues,
                        is_completed: false,
                    };

                    const response = await this.submit(
                        cloneDeep(this.whochecklistaAvslutningFormData),
                        "WHO-checklista 3 - Avslutning sparad",
                        "whochecklista/avslutning/",
                        undefined,
                        "WHO-checklista 3 - Avslutning"
                    );

                    if (response.success) {
                        const { data } = response;
                        this.whochecklistaAvslutningFormData = data;
                        this.whochecklistaAvslutningFormData.updated_at = moment(data.updated_at).format("YYYY-MM-DD");
                    }
                    this.loading = false;
                    return;
                }

                this.showWHOchecklistaAvslutning = false;

                const payload = this.removeNullFromObject(cloneDeep(this.whochecklistaAvslutningFormData));

                payload.is_completed = true;

                const response = await this.submit(
                    payload,
                    "WHO-checklista 3 - Avslutning sparad",
                    "whochecklista/avslutning/",
                    undefined,
                    "WHO-checklista 3 - Avslutning"
                );

                if (response.success) {
                    const { data } = response;
                    this.whochecklistaAvslutningFormData = data;
                    this.whochecklistaAvslutningFormData.updated_at = moment(data.updated_at).format("YYYY-MM-DD");
                }

                this.loading = false;
            },

            async submitMaterial({ button, signeringsansvarig }, manualSubmit = false) {
                const validationResult = await this.checkValidateOnSubmit(button, manualSubmit, "materialFormRef");
                if (!validationResult) {
                    return;
                }
                this.saveLoading = true;
                let payload = cloneDeep({
                    ...this.materialFormData,
                    // Tom lista med material leder till att .material inte är satt
                    material: this.materialFormData.material ?? [],
                });

                if (button === "save") {
                    const result = await this.submit(
                        payload,
                        "Material sparade",
                        "material/",
                        signeringsansvarig,
                        "Material"
                    );
                    if (result?.error) {
                        scrollToAndFocusError(this.$refs.materialFormRef.$el, this.$nextTick);
                    }
                    this.materialSavedFormData = cloneDeep(this.materialFormData);
                } else if (button === "sign") {
                    await this.signSectionMaterial({
                        signeringsansvarig: signeringsansvarig,
                        payload: payload,
                    });
                }
                this.saveLoading = false;
            },

            async signSectionMaterial({ payload, signeringsansvarig }) {
                payload.id = this.operationsanmalan.id;
                payload.accountableHealthcareProfessional = signeringsansvarig;

                if (payload.material) {
                    payload.material = payload.material.filter((material) => material.material);
                }

                const url = `/operation/operationsjournaler/${this.operationsanmalan.id}/material/`;

                this.signDataMaterial = payload;
                this.signUrlMaterial = url;
                this.signVisibleMaterial = true;
            },

            async handleSignMaterialSuccess(data) {
                this.materialFormData.is_signed = data.is_signed;
                this.materialSavedFormData = cloneDeep(this.materialFormData);

                this.signVisibleMaterial = false;
            },

            handleSignMaterialError(errors) {
                scrollToAndFocusError(this.$refs.materialFormRef.$el, this.$nextTick);

                if (typeof errors === "object") {
                    this.formError = collectErrors(errors);
                } else {
                    openDialog(errors, "error");
                }

                this.signVisibleMaterial = false;
            },

            handleSignMaterialCancel() {
                this.signVisibleMaterial = false;
            },

            async submitAnestesi({ button, signeringsansvarig }, manualSubmit = false) {
                const validationResult = await this.checkValidateOnSubmit(button, manualSubmit, "anestesiFormRef");
                if (!validationResult) {
                    return;
                }
                this.saveLoading = true;
                let payload = cloneDeep(this.anestesiFormData);

                if (payload.bianestesikoder) {
                    payload.bianestesikoder = this.extractValues(payload.bianestesikoder);
                    payload.bianestesikoder = removeEmptyObjectsFromArray(payload.bianestesikoder);
                }

                if (payload.anestesikomplikationer) {
                    payload.anestesikomplikationer = removeEmptyObjectsFromArray(payload.anestesikomplikationer);
                    payload.anestesikomplikationer.forEach((komplikation, index) => {
                        payload.anestesikomplikationer[index].tidpunkt = this.setDateOnTid(komplikation.tidpunkt);
                    });
                }

                if (button === "save") {
                    const result = await this.submit(
                        payload,
                        "Anestesi sparad",
                        "anestesi/",
                        signeringsansvarig,
                        "Anestesi"
                    );
                    if (result?.error) {
                        scrollToAndFocusError(this.$refs.anestesiFormRef.$el, this.$nextTick);
                    }
                    this.anestesiSavedFormData = cloneDeep(this.anestesiFormData);
                } else if (button === "sign") {
                    await this.signSectionAnestesi({
                        signeringsansvarig: signeringsansvarig,
                        payload: payload,
                    });
                }
                this.saveLoading = false;
            },

            async signSectionAnestesi({ payload, signeringsansvarig }) {
                payload.id = this.operationsanmalan.id;
                payload.accountableHealthcareProfessional = signeringsansvarig;

                const antalRader = this.anestesiFormData.anestesikomplikationer.length;

                const missingFields = checkRequired(
                    ["huvudanestesikod", [`anestesikomplikationer`, ["kod", "kategori", "svarighetsgrad"], antalRader]],
                    payload
                );

                if (missingFields.length > 0) {
                    scrollToAndFocusError(this.$refs.anestesiFormRef.$el, this.$nextTick);
                    this.formError = createErrorForMissingFields(missingFields);

                    return;
                }

                const url = `/operation/operationsjournaler/${this.operationsanmalan.id}/anestesi/`;

                this.signDataAnestesi = payload;
                this.signUrlAnestesi = url;
                this.signVisibleAnestesi = true;
            },

            async handleSignAnestesiSuccess(data) {
                this.anestesiFormData.is_signed = data.is_signed;
                this.anestesiSavedFormData = cloneDeep(this.anestesiFormData);

                this.signVisibleAnestesi = false;
            },

            handleSignAnestesiError(errors) {
                scrollToAndFocusError(this.$refs.anestesiFormRef.$el, this.$nextTick);

                if (typeof errors === "object") {
                    this.formError = collectErrors(errors);
                } else {
                    openDialog(errors, "error");
                }
                this.signVisibleAnestesi = false;
            },

            handleSignAnestesiCancel() {
                this.signVisibleAnestesi = false;
            },

            /**
             * Inskickad tid är exklusive datum. Funktionen använder sig av operationsdatum för att sätta tid på,
             * eftersom BE vill ha datum + tid
             * @param tid Klockslag
             * @returns {moment}
             */
            setDateOnTid(tid) {
                return tid
                    ? changeTimeToDate(
                          this.operationsuppgifterFormData.operationsdatum ??
                              this.operationsanmalan.bokad_operation_datum,
                          tid
                      )
                    : null;
            },

            parseBooleanString(value) {
                if (value === "true") {
                    return true;
                } else if (value === "false") {
                    return false;
                } else {
                    return null;
                }
            },

            /**
             * Calculate the difference in minutes between two time strings in "HH:MM" format.
             *
             * @param {string} start - The start time in "HH:MM" format.
             * @param {string} end - The end time in "HH:MM" format.
             * @returns {number|string} The difference in minutes, or "-" if either time is missing.
             */
            calculateTimeDifference(start, end) {
                const isValidTimeFormat = (time) => /^([01]\d|2[0-3]):[0-5]\d$/.test(time);

                if (!isValidTimeFormat(start) || !isValidTimeFormat(end)) {
                    return "-";
                }

                // Convert time string to total minutes
                const toMinutes = (time) => {
                    const [hours, minutes] = time.split(":").map(Number);
                    return hours * 60 + minutes;
                };

                return toMinutes(end) - toMinutes(start);
            },

            normalizeNews2Value(value) {
                if (value === "" || value == null) {
                    return null;
                }
                return value;
            },

            mapNews2BedomningForPayload(news2Entry) {
                return {
                    tidpunkt_news2_bedomning:
                        news2Entry?.tidpunkt_news2_bedomning && this.setDateOnTid(news2Entry.tidpunkt_news2_bedomning),
                    data: {
                        respiratory_rate: this.normalizeNews2Value(news2Entry?.respiratoryRate),
                        oxygen_saturation: this.normalizeNews2Value(news2Entry?.oxygenSaturation),
                        oxygen_saturation_2: this.normalizeNews2Value(news2Entry?.oxygenSaturation2),
                        oxygen_supply: this.normalizeNews2Value(news2Entry?.oxygenSupply),
                        systolic_bp: this.normalizeNews2Value(news2Entry?.systolicBP),
                        diastolic_bp: this.normalizeNews2Value(news2Entry?.diastolicBP),
                        pulse: this.normalizeNews2Value(news2Entry?.pulse),
                        consciousness: this.normalizeNews2Value(news2Entry?.consciousness),
                        temperature: this.normalizeNews2Value(news2Entry?.temperature),
                    },
                    news2_score: this.normalizeNews2Value(news2Entry?.news2_score),
                    risk_kategori: this.normalizeNews2Value(news2Entry?.risk_kategori),
                    fornyad_news2_bedomning: this.normalizeNews2Value(news2Entry?.fornyad_news2_bedomning),
                    avsteg_fran_fornyad_news2_bedomning: this.normalizeNews2Value(
                        news2Entry?.avsteg_fran_fornyad_news2_bedomning
                    ),
                };
            },

            mapNews2BedomningFromResponse(news2Entry) {
                return {
                    tidpunkt_news2_bedomning: news2Entry?.tidpunkt_news2_bedomning
                        ? extractHourMinuteFromDateTime(news2Entry.tidpunkt_news2_bedomning)
                        : null,
                    respiratoryRate: news2Entry?.data?.respiratory_rate ?? null,
                    oxygenSaturation: news2Entry?.data?.oxygen_saturation ?? null,
                    oxygenSaturation2: news2Entry?.data?.oxygen_saturation_2 ?? null,
                    oxygenSupply: news2Entry?.data?.oxygen_supply ?? null,
                    systolicBP: news2Entry?.data?.systolic_bp ?? null,
                    diastolicBP: news2Entry?.data?.diastolic_bp ?? null,
                    pulse: news2Entry?.data?.pulse ?? null,
                    consciousness: news2Entry?.data?.consciousness ?? null,
                    temperature: news2Entry?.data?.temperature ?? null,
                    news2_score: news2Entry?.news2_score ?? null,
                    risk_kategori: news2Entry?.risk_kategori ?? null,
                    fornyad_news2_bedomning: news2Entry?.fornyad_news2_bedomning ?? null,
                    avsteg_fran_fornyad_news2_bedomning: news2Entry?.avsteg_fran_fornyad_news2_bedomning ?? null,
                };
            },

            async submitTid(key) {
                let value = this.tiderFormData[key];
                value = this.setDateOnTid(value);

                if (
                    (!value && !this.tiderSavedFormData[key]) ||
                    this.tiderSavedFormData[key] === value?.format("HH:mm")
                ) {
                    return;
                }

                const payload = { [key]: value };

                payload.fritext = this.tiderFormData.fritext;

                const data = await this.submit(payload, "Tid sparad", "tider/", undefined, "Tider");

                if (data?.error) {
                    openDialog(getErrorMessage(data.error), "error");
                    this.tiderFormData[key] = this.tiderSavedFormData[key];

                    return;
                }

                if (
                    [
                        "ankomsttid_preopenhet",
                        "patienttid_start",
                        "opstart",
                        "x_minuter_kvar",
                        "opslut",
                        "patienttid_slut",
                        "efterarbete_sal_slut",
                    ].includes(key)
                ) {
                    emitter.emit("pagaende-op-tider-updated");
                }

                this.tiderSavedFormData = cloneDeep(this.tiderFormData);
            },

            /**
             * Returnerar { success:true, data: response.data } vid lyckat anrop. Returnerar { success:false, error } vid misslyckat anrop
             */
            async submit(payload, saveMessage, url, signeringsansvarig, sektionNamn) {
                if (!isEmptyObject(this.validation) && arrayPropertyExistsNotEmpty(this.validation.errors)) {
                    return { success: false, error: true };
                }
                if (signeringsansvarig) {
                    payload.accountableHealthcareProfessional = signeringsansvarig;
                }

                const logName = `Operationsjournal${sektionNamn ? ` - ${sektionNamn}` : ""}`;

                try {
                    const response = await klinikenApi.put(
                        `operation/operationsjournaler/${this.operationsanmalan.id}/${url}`,
                        payload,
                        getConfig({}, logName)
                    );

                    if (response.status === 200) {
                        flashMessage(saveMessage);
                        await this.updateEntry(this.operationsanmalan.id);
                        return { success: true, data: response.data };
                    } else {
                        flashWarning("Något gick fel");
                    }
                } catch (error) {
                    if (error?.response?.status === 400) {
                        if (url === "tider/") {
                            return { success: false, error };
                        }
                        this.formError = collectErrors(error.response.data);
                        return { success: false, error };
                    }

                    openDialog("Kunde inte spara sektionen", "error");
                    return { success: false, error };
                }
            },

            async cancel() {
                if (this.hasUnsavedData) {
                    if (
                        await this.$refs.dialog.show({
                            variant: BasicModalVariant.WARNING,
                            title: "Denna flik har osparad data. Vill du stänga ändå?",
                            okButton: "Ja",
                            cancelButton: "Nej",
                        })
                    ) {
                        await this.$store.dispatch("tabs/closeTab", this.tabId);
                    }
                } else {
                    await this.$store.dispatch("tabs/closeTab", this.tabId);
                }
            },

            async openWhoForberedelse() {
                this.showWHOchecklistaForberedelse = true;

                const response = await fetchData({
                    url: `/operation/operationsjournaler/${this.operationsanmalan.id}/whochecklista/forberedelse/`,
                });

                this.whochecklistaForberedelseFormData = response;

                const { kand_allergi, risk_for_aspiration, risk_for_blodforlust, risk_for_hypotermi } = response;

                if (typeof kand_allergi === "boolean") {
                    this.whochecklistaForberedelseFormData.kand_allergi = kand_allergi.toString();
                }

                if (typeof risk_for_aspiration === "boolean") {
                    this.whochecklistaForberedelseFormData.risk_for_aspiration = risk_for_aspiration.toString();
                }

                if (typeof risk_for_blodforlust === "boolean") {
                    this.whochecklistaForberedelseFormData.risk_for_blodforlust = risk_for_blodforlust.toString();
                }

                if (typeof risk_for_hypotermi === "boolean") {
                    this.whochecklistaForberedelseFormData.risk_for_hypotermi = risk_for_hypotermi.toString();
                }
            },
            async openWhoTimeout() {
                this.showWHOchecklistaTimeOut = true;

                const response = await fetchData({
                    url: `/operation/operationsjournaler/${this.operationsanmalan.id}/whochecklista/timeout/`,
                });
                const { antibiotikaprofylax, bildinformation, trombosprofylax } = response;

                this.whochecklistaTimeoutFormData = response;
                if (typeof antibiotikaprofylax === "boolean") {
                    this.whochecklistaTimeoutFormData.antibiotikaprofylax = antibiotikaprofylax.toString();
                }

                if (typeof trombosprofylax === "boolean") {
                    this.whochecklistaTimeoutFormData.trombosprofylax = trombosprofylax.toString();
                }

                if (typeof bildinformation === "boolean") {
                    this.whochecklistaTimeoutFormData.bildinformation = bildinformation.toString();
                }
            },
            async openWhoAvslutning() {
                this.showWHOchecklistaAvslutning = true;
                this.whochecklistaAvslutningFormData = await fetchData({
                    url: `/operation/operationsjournaler/${this.operationsanmalan.id}/whochecklista/avslutning/`,
                });
            },

            setOperationsuppgifter({ sektion, operationsanmalan }) {
                const data = sektion ?? operationsanmalan;
                this.operationsuppgifterFormData.is_signed = data.is_signed;
                this.operationsuppgifterFormData.accountableHealthcareProfessional =
                    data.accountableHealthcareProfessional && data.accountableHealthcareProfessional;
                this.operationsuppgifterFormData.typoperation = isNull(data.typoperation) ? "" : `${data.typoperation}`;

                if (propertyExists(data.huvuddiagnos_display) && isNotEmptyObject(data.huvuddiagnos_display)) {
                    this.huvuddiagnoser = SetField({
                        value: data.huvuddiagnos_display.id,
                        label: `${data.huvuddiagnos_display.code} ${data.huvuddiagnos_display.displayName}`,
                        formData: this.operationsuppgifterFormData,
                        objectPropertyKey: "huvuddiagnos",
                    });
                }
                if (propertyExists(data.bidiagnoser_display) && isNotEmptyObject(data.bidiagnoser_display)) {
                    data.bidiagnoser_display.forEach(function (obj, index) {
                        this.$set(this.bidiagnoser, index, [
                            {
                                value: obj.id,
                                label: `${obj.code} ${obj.displayName}`,
                            },
                        ]);
                        this.$set(this.operationsuppgifterFormData.bidiagnoser, index, { bidiagnos: `${obj.id}` });
                    }, this);
                }

                if (
                    propertyExists(data.huvudoperationskod_display) &&
                    isNotEmptyObject(data.huvudoperationskod_display)
                ) {
                    this.huvudoperationskoder = SetField({
                        value: data.huvudoperationskod_display.id,
                        label: `${data.huvudoperationskod_display.code} ${data.huvudoperationskod_display.displayName}`,
                        formData: this.operationsuppgifterFormData,
                        objectPropertyKey: "huvudoperationskod",
                    });
                }
                if (
                    propertyExists(data.bioperationskoder_display) &&
                    isNotEmptyObject(data.bioperationskoder_display)
                ) {
                    data.bioperationskoder_display.forEach(function (obj, index) {
                        this.$set(this.bioperationskoder, index, [
                            {
                                value: obj.id,
                                label: `${obj.code} ${obj.displayName}`,
                            },
                        ]);
                        this.$set(this.operationsuppgifterFormData.bioperationskoder, index, {
                            bioperationskod: `${obj.id}`,
                        });
                    }, this);
                }

                if (sektion) {
                    if (data.sal) {
                        this.operationsuppgifterFormData.sal = `${data.sal}`;
                    }
                } else {
                    if (data.current_bokad_sal) {
                        this.operationsuppgifterFormData.sal = `${data.current_bokad_sal}`;
                    }
                }

                const nivaSource = data.niva_fran_till ?? operationsanmalan.niva_fran_till;
                const nivaDisplay =
                    data.niva_fran_till_display != null
                        ? data.niva_fran_till_display
                        : (operationsanmalan.niva_fran_till_display ?? []);
                this.operationsuppgifterFormData.niva_fran_till_display = nivaDisplay;
                this.operationsuppgifterFormData.niva_fran_till = nivaSource?.length
                    ? convertNivaData(nivaSource, nivaDisplay)
                    : [{ fran: null, till: null }];

                if (data.oplage) {
                    this.operationsuppgifterFormData.oplage = data.oplage.toString();
                }

                /*
                Sätter datum till datum från opjournalen med fallback till anmälan, om sektionen Operationsuppgifter
                inte existerar i opjournalen än
             */
                this.operationsuppgifterFormData.operationsdatum =
                    data.operationsdatum ?? operationsanmalan.bokad_operation_datum;

                if (typeof data.reop === "boolean") {
                    this.operationsuppgifterFormData.reop = data.reop;
                }
                if (typeof data.garantiop === "boolean") {
                    this.operationsuppgifterFormData.garantiop = data.garantiop;
                }
                if (data.lateralitet) {
                    this.operationsuppgifterFormData.lateralitet = data.lateralitet;
                }
                if (data.avtal) {
                    this.operationsuppgifterFormData.avtal = data.avtal;
                }
                if (data.anmarkning) {
                    this.operationsuppgifterFormData.anmarkning = data.anmarkning;
                }

                const digitumSource = data.digitum ?? operationsanmalan.digitum;
                if (digitumSource?.length) {
                    this.operationsuppgifterFormData.digitum = addMinimumRows(
                        digitumSource.map((digit) => ({ id: convertDigitumData({ digit }) }))
                    );
                }

                const resurserDisplay =
                    data.resurser_display != null ? data.resurser_display : operationsanmalan.resurser_display;
                if (resurserDisplay?.length) {
                    this.operationsuppgifterFormData.resurser = addMinimumRows(
                        resurserDisplay.map((item) => ({ id: convertResursData({ resurs: item }) }))
                    );
                }

                const implantatDisplay =
                    data.implantat_display != null ? data.implantat_display : operationsanmalan.implantat_display;
                if (implantatDisplay?.length) {
                    this.operationsuppgifterFormData.implantat = addMinimumRows(
                        implantatDisplay.map((item) => ({ id: convertImplantatData({ implantat: item }) }))
                    );
                }

                const vardformSource = propertyExists(data.vardform)
                    ? data.vardform
                    : operationsanmalan.planerad_vardform;
                if (vardformSource) {
                    this.operationsuppgifterFormData.vardform = vardformSource.toString();
                }
            },

            async setPersonal({ sektion, operationsanmalan }) {
                const data = sektion ?? operationsanmalan;

                this.personalFormData.is_signed = data.is_signed;
                this.personalFormData.accountableHealthcareProfessional =
                    data.accountableHealthcareProfessional && data.accountableHealthcareProfessional;

                if (data.current_bokad_huvudoperator || data.huvudoperator) {
                    this.personalFormData.huvudoperator = data.current_bokad_huvudoperator
                        ? `${data.current_bokad_huvudoperator}`
                        : `${data.huvudoperator}`;

                    if (
                        !this.operatorer.find(
                            (operator) => operator.value === (data.current_bokad_huvudoperator ?? data.huvudoperator)
                        )
                    ) {
                        this.operatorer = [
                            profileOptions([data.bokad_huvudoperator_display ?? data.huvudoperator_display])[0],
                            ...this.operatorer,
                        ];
                    }
                }

                if (data.huvudoperator_intid) {
                    this.personalFormData.huvudoperator_intid = getTimeFromDatetime(data.huvudoperator_intid);
                }

                if (data.huvudoperator_uttid) {
                    this.personalFormData.huvudoperator_uttid = getTimeFromDatetime(data.huvudoperator_uttid);
                }

                if (data.anestesilakare) {
                    this.personalFormData.anestesilakare = data.anestesilakare;
                }

                if (data.anestesilakare_intid) {
                    this.personalFormData.anestesilakare_intid = getTimeFromDatetime(data.anestesilakare_intid);
                }

                if (data.anestesilakare_uttid) {
                    this.personalFormData.anestesilakare_uttid = getTimeFromDatetime(data.anestesilakare_uttid);
                }

                this.operatorer = await this.addInactiveProfile(this.operatorer, data.current_bokad_huvudoperator);

                if (sektion) {
                    this.anestesilakare = await this.addInactiveProfile(
                        this.anestesilakare,
                        this.personalFormData.anestesilakare
                    );
                    this.setPersonalField(data, "ass_operatorer");
                } else {
                    if (arrayPropertyExistsNotEmpty(data.ass_operatorer)) {
                        data.ass_operatorer.forEach((ass_operator, groupIndex) => {
                            this.$set(this.ass_operatorer, groupIndex, [
                                {
                                    value: `${ass_operator}`,
                                    label: data.ass_operatorer_display[groupIndex].user_display.name,
                                },
                            ]);
                            this.$set(this.personalFormData.ass_operatorer, groupIndex, {
                                profile: `${ass_operator}`,
                            });
                        });
                    }
                    this.ass_operatorer = await this.addInactiveProfiles(
                        this.ass_operatorer,
                        this.personalFormData.ass_operatorer
                    );
                }

                if (arrayPropertyExistsNotEmpty(data.anestesisjukskoterskor)) {
                    this.setPersonalField(data, "anestesisjukskoterskor");
                }

                if (arrayPropertyExistsNotEmpty(data.operationssjukskoterskor)) {
                    this.setPersonalField(data, "operationssjukskoterskor");
                }

                if (arrayPropertyExistsNotEmpty(data.operationsunderskoterskor)) {
                    this.setPersonalField(data, "operationsunderskoterskor");
                }

                if (typeof data.gast_pa_sal === "boolean") {
                    this.personalFormData.gast_pa_sal = data.gast_pa_sal;
                }

                if (data.gaster) {
                    this.personalFormData.gaster = data.gaster;
                }

                if (data.antal_dorroppningar !== null) {
                    this.personalFormData.antal_dorroppningar = data.antal_dorroppningar;
                }

                if (data.fritext) {
                    this.personalFormData.fritext = data.fritext;
                }
            },

            async fetchPatientFormData() {
                let retrievedPatientData;
                if (this.parameters.operationsanmalan_id) {
                    retrievedPatientData = await fetchData({
                        url: `/operation/operationsjournaler/${this.parameters.operationsanmalan_id}/patientdata/`,
                    });
                    const accountableProfessional = retrievedPatientData.accountableHealthcareProfessional;
                    this.patientdataFormData = {
                        accountableProfessional: accountableProfessional,
                        is_signed: retrievedPatientData?.is_signed,
                    };
                }

                return retrievedPatientData.patientdata;
            },

            async fetchStralningsregistreringFormData() {
                try {
                    this.genomlysningsapparater = await this.fetchGenomlysningsapparater();

                    if (this.parameters.operationsanmalan_id) {
                        const retrievedStralningsregistreringsData = await fetchData({
                            url: `/operation/operationsjournaler/${this.parameters.operationsanmalan_id}/stralningsregistrering`,
                        });

                        retrievedStralningsregistreringsData.stralningsregistreringar =
                            retrievedStralningsregistreringsData.stralningsregistreringar.map((strReg) => {
                                return {
                                    dosvarde: strReg.dosvarde,
                                    genomlysningsapparat: strReg.genomlysningsapparat?.toString(),
                                    genomlysningstid: `${moment.duration(strReg.genomlysningstid).as("seconds")}`,
                                };
                            });

                        this.stralningsregistreringFormData = {
                            accountableHealthcareProfessional:
                                retrievedStralningsregistreringsData.accountableHealthcareProfessional,
                            is_signed: retrievedStralningsregistreringsData?.is_signed,
                        };

                        return retrievedStralningsregistreringsData;
                    }
                } catch (error) {
                    openDialog(error, "error");

                    return {};
                }
            },

            setWHOChecklistaForberedelse({ sektion, timeoutSektion, avslutningSektion }) {
                if (!sektion) {
                    return;
                }

                this.whochecklistaForberedelseFormData.updated_at = moment(sektion.updated_at).format("YYYY-MM-DD");
                this.whochecklistaForberedelseFormData.updated_by = sektion.updated_by;
                this.whochecklistaForberedelseFormData.is_completed = sektion.is_completed;

                if (timeoutSektion) {
                    this.whochecklistaTimeoutFormData.updated_at = moment(timeoutSektion.updated_at).format(
                        "YYYY-MM-DD"
                    );
                    this.whochecklistaTimeoutFormData.updated_by = timeoutSektion.updated_by;
                }

                if (avslutningSektion) {
                    this.whochecklistaAvslutningFormData.updated_at = moment(avslutningSektion.updated_at).format(
                        "YYYY-MM-DD"
                    );
                    this.whochecklistaAvslutningFormData.updated_by = avslutningSektion.updated_by;
                }
            },
            setWHOChecklistaTimeout({ sektion, avslutningSektion }) {
                if (!sektion) {
                    return;
                }

                this.whochecklistaTimeoutFormData.updated_at = moment(sektion.updated_at).format("YYYY-MM-DD");
                this.whochecklistaTimeoutFormData.updated_by = sektion.updated_by;
                this.whochecklistaTimeoutFormData.is_completed = sektion.is_completed;

                if (avslutningSektion) {
                    this.whochecklistaAvslutningFormData.updated_at = moment(avslutningSektion.updated_at).format(
                        "YYYY-MM-DD"
                    );
                    this.whochecklistaAvslutningFormData.updated_by = avslutningSektion.updated_by;
                }
            },
            setWHOChecklistaAvslutning({ sektion }) {
                if (!sektion) {
                    return;
                }

                this.whochecklistaAvslutningFormData.updated_at = moment(sektion.updated_at).format("YYYY-MM-DD");
                this.whochecklistaAvslutningFormData.updated_by = sektion.updated_by;
                this.whochecklistaAvslutningFormData.is_completed = sektion.is_completed;
            },

            setMaterial({ sektion }) {
                if (!sektion) {
                    return;
                }

                this.materialFormData.is_signed = sektion.is_signed;
                this.materialFormData.accountableHealthcareProfessional =
                    sektion.accountableHealthcareProfessional && sektion.accountableHealthcareProfessional;
                if (arrayPropertyExistsNotEmpty(sektion.material)) {
                    this.materialFormData.material = sektion.material.map((item) => {
                        const { antal, batchnummer } = item;
                        const { artikelnummer, id, namn } = item.material_display;
                        return { material: id, artikelnummer, materialnamn: namn, antal, batchnummer };
                    });
                }
                this.materialFormData.fritext = sektion.fritext;
            },

            async setAnestesi({ sektion, operationsanmalan }) {
                if (!sektion) {
                    // Förifyll huvudanestesikod och bianestesikoder från preanestesibedömningens anestesikoder eller opanmälans anestesi
                    if (operationsanmalan.aktuell_preanestesibedomning) {
                        try {
                            const preanestesibedomningResponse = await klinikenApi.get(
                                `/operation/preanestesibedomningar/${operationsanmalan.aktuell_preanestesibedomning}`,
                                getConfig({ params: {} }, "Operationsjournal")
                            );
                            const preanestesibedomning = preanestesibedomningResponse.data;

                            if (preanestesibedomning.huvudanestesikod_display) {
                                this.huvudanestesikoder = SetField({
                                    value: preanestesibedomning.huvudanestesikod_display.id,
                                    label: `${preanestesibedomning.huvudanestesikod_display.code} ${preanestesibedomning.huvudanestesikod_display.displayName}`,
                                    formData: this.anestesiFormData,
                                    objectPropertyKey: "huvudanestesikod",
                                });
                            }
                            if (preanestesibedomning.bianestesikoder_display) {
                                preanestesibedomning.bianestesikoder_display.forEach((bianestesiDisplay, index) => {
                                    this.$set(this.bianestesikoder, index, [
                                        {
                                            value: `${bianestesiDisplay.id}`,
                                            label: `${bianestesiDisplay.code} ${bianestesiDisplay.displayName}`,
                                        },
                                    ]);
                                    this.$set(this.anestesiFormData.bianestesikoder, index, {
                                        kod: `${bianestesiDisplay.id}`,
                                    });
                                });
                            }
                        } catch (error) {
                            openDialog(getErrorMessage(error), "error");
                            return;
                        }
                    } else {
                        // Ingen preanestesibedömning, hämta från opanmälan
                        try {
                            const anestesierResponse = await klinikenApi.get(
                                `/operation/kodverk/anestesier`,
                                getConfig({ params: {} })
                            );
                            const matchingAnestesi = anestesierResponse.data.results.find(
                                (result) => result.id === operationsanmalan.anestesi
                            );

                            if (matchingAnestesi) {
                                if (matchingAnestesi.huvudanestesikod_display) {
                                    this.huvudanestesikoder = SetField({
                                        value: matchingAnestesi.huvudanestesikod_display.id,
                                        label: `${matchingAnestesi.huvudanestesikod_display.code} ${matchingAnestesi.huvudanestesikod_display.displayName}`,
                                        formData: this.anestesiFormData,
                                        objectPropertyKey: "huvudanestesikod",
                                    });
                                }
                                if (matchingAnestesi.bianestesikoder_display) {
                                    matchingAnestesi.bianestesikoder_display.forEach((bianestesiDisplay, index) => {
                                        this.$set(this.bianestesikoder, index, [
                                            {
                                                value: `${bianestesiDisplay.id}`,
                                                label: `${bianestesiDisplay.code} ${bianestesiDisplay.displayName}`,
                                            },
                                        ]);
                                        this.$set(this.anestesiFormData.bianestesikoder, index, {
                                            kod: `${bianestesiDisplay.id}`,
                                        });
                                    });
                                }
                            }
                        } catch (error) {
                            openDialog(getErrorMessage(error), "error");
                            return;
                        }
                    }
                    return;
                }

                this.anestesiFormData.is_signed = sektion.is_signed;
                this.anestesiFormData.accountableHealthcareProfessional =
                    sektion.accountableHealthcareProfessional && sektion.accountableHealthcareProfessional;

                if (
                    propertyExists(sektion.huvudanestesikod_display) &&
                    isNotEmptyObject(sektion.huvudanestesikod_display)
                ) {
                    this.huvudanestesikoder = SetField({
                        value: sektion.huvudanestesikod_display.id,
                        label: `${sektion.huvudanestesikod_display.code} ${sektion.huvudanestesikod_display.displayName}`,
                        formData: this.anestesiFormData,
                        objectPropertyKey: "huvudanestesikod",
                    });
                }

                if (arrayPropertyExistsNotEmpty(sektion.bianestesikoder)) {
                    sektion.bianestesikoder.forEach((bianestesikod, groupIndex) => {
                        this.$set(this.bianestesikoder, groupIndex, [
                            {
                                value: `${bianestesikod}`,
                                label: `${sektion.bianestesikoder_display[groupIndex].code} ${sektion.bianestesikoder_display[groupIndex].displayName}`,
                            },
                        ]);
                        this.$set(this.anestesiFormData.bianestesikoder, groupIndex, {
                            kod: `${bianestesikod}`,
                        });
                    });
                }

                if (arrayPropertyExistsNotEmpty(sektion.anestesikomplikationer)) {
                    sektion.anestesikomplikationer.forEach((anestesikomplikation, groupIndex) => {
                        this.anestesikomplikationer[groupIndex] = [
                            {
                                value: `${anestesikomplikation.kod}`,
                                label: anestesikomplikation.kod_display.displayName,
                            },
                        ];

                        this.$set(this.anestesiFormData.anestesikomplikationer, groupIndex, {
                            kategori: `${anestesikomplikation.kod_display.kapitel}`,
                            kod: `${anestesikomplikation.kod}`,
                            svarighetsgrad: `${anestesikomplikation.svarighetsgrad}`,
                            tidpunkt: extractHourMinuteFromDateTime(anestesikomplikation.tidpunkt),
                        });
                    });
                }

                if (sektion.anestesikomplikationer) {
                    isNotEmptyArray(sektion.anestesikomplikationer)
                        ? (this.anestesiFormData.anestesikomplikationer = sektion.anestesikomplikationer.map(
                              (komplikation) => {
                                  return {
                                      kategori:
                                          komplikation.kod_display.kapitel && `${komplikation.kod_display.kapitel}`,
                                      svarighetsgrad: `${komplikation.svarighetsgrad}`,
                                      kod: `${komplikation.kod}`,
                                      tidpunkt: extractHourMinuteFromDateTime(komplikation.tidpunkt),
                                  };
                              }
                          ))
                        : (sektion.anestesikomplikationer = [{}]);
                }

                if (sektion?.fritext) {
                    this.anestesiFormData.fritext = sektion.fritext;
                }
            },

            setTider({ sektion }) {
                if (!sektion) {
                    return;
                }

                if (sektion?.uppdukning_start) {
                    this.tiderFormData.uppdukning_start = extractHourMinuteFromDateTime(sektion.uppdukning_start);
                }

                if (sektion?.uppdukning_slut) {
                    this.tiderFormData.uppdukning_slut = extractHourMinuteFromDateTime(sektion.uppdukning_slut);
                }

                if (sektion?.antibiotikaprofylax_start) {
                    this.tiderFormData.antibiotikaprofylax_start = extractHourMinuteFromDateTime(
                        sektion.antibiotikaprofylax_start
                    );
                }

                if (sektion?.ankomsttid_preopenhet) {
                    this.tiderFormData.ankomsttid_preopenhet = extractHourMinuteFromDateTime(
                        sektion.ankomsttid_preopenhet
                    );
                }

                if (sektion?.preoperativ_anestesi_start) {
                    this.tiderFormData.preoperativ_anestesi_start = extractHourMinuteFromDateTime(
                        sektion.preoperativ_anestesi_start
                    );
                }

                if (sektion?.patienttid_start) {
                    this.tiderFormData.patienttid_start = extractHourMinuteFromDateTime(sektion.patienttid_start);
                }

                if (sektion?.patient_in_opsal) {
                    this.tiderFormData.patient_in_opsal = extractHourMinuteFromDateTime(sektion.patient_in_opsal);
                }

                if (sektion?.anestesitid_start) {
                    this.tiderFormData.anestesitid_start = extractHourMinuteFromDateTime(sektion.anestesitid_start);
                }

                if (sektion?.anestesitid_klar) {
                    this.tiderFormData.anestesitid_klar = extractHourMinuteFromDateTime(sektion.anestesitid_klar);
                }

                if (sektion?.operationsforberedelse_klar) {
                    this.tiderFormData.operationsforberedelse_klar = extractHourMinuteFromDateTime(
                        sektion.operationsforberedelse_klar
                    );
                }

                if (sektion?.opstart) {
                    this.tiderFormData.opstart = extractHourMinuteFromDateTime(sektion.opstart);
                }

                if (sektion?.robot_in) {
                    this.tiderFormData.robot_in = extractHourMinuteFromDateTime(sektion.robot_in);
                }

                if (sektion?.robot_ut) {
                    this.tiderFormData.robot_ut = extractHourMinuteFromDateTime(sektion.robot_ut);
                }

                if (sektion?.blodtomt_falt_start) {
                    this.tiderFormData.blodtomt_falt_start = extractHourMinuteFromDateTime(sektion.blodtomt_falt_start);
                }

                if (sektion?.blodtomt_falt_slut) {
                    this.tiderFormData.blodtomt_falt_slut = extractHourMinuteFromDateTime(sektion.blodtomt_falt_slut);
                }

                if (sektion?.x_minuter_kvar) {
                    this.tiderFormData.x_minuter_kvar = extractHourMinuteFromDateTime(sektion.x_minuter_kvar);
                }

                if (sektion?.opslut) {
                    this.tiderFormData.opslut = extractHourMinuteFromDateTime(sektion.opslut);
                }

                if (sektion?.anestesi_slut) {
                    this.tiderFormData.anestesi_slut = extractHourMinuteFromDateTime(sektion.anestesi_slut);
                }

                if (sektion?.patient_ut_opsal) {
                    this.tiderFormData.patient_ut_opsal = extractHourMinuteFromDateTime(sektion.patient_ut_opsal);
                }

                if (sektion?.patienttid_slut) {
                    this.tiderFormData.patienttid_slut = extractHourMinuteFromDateTime(sektion.patienttid_slut);
                }

                if (sektion?.efterarbete_sal_slut) {
                    this.tiderFormData.efterarbete_sal_slut = extractHourMinuteFromDateTime(
                        sektion.efterarbete_sal_slut
                    );
                }

                if (sektion?.knivtid_start) {
                    this.tiderFormData.knivtid_start = extractHourMinuteFromDateTime(sektion.knivtid_start);
                }

                if (sektion?.knivtid_slut) {
                    this.tiderFormData.knivtid_slut = extractHourMinuteFromDateTime(sektion.knivtid_slut);
                }

                this.tiderFormData.fritext = sektion?.fritext ?? "";
            },

            setPostop({ sektion }) {
                if (!sektion) {
                    return;
                }

                this.postoperativOvervakningUpdatedAt = sektion?.updated_at
                    ? moment(sektion.updated_at).format("YYYY-MM-DD HH:mm")
                    : null;

                this.postoperativOvervakningFormData.is_signed = sektion?.is_signed ?? false;
                this.postoperativOvervakningFormData.accountableHealthcareProfessional =
                    sektion?.accountableHealthcareProfessional ?? "";

                if (sektion?.tid_in) {
                    this.postoperativOvervakningFormData.tid_in = `${extractHourMinuteFromDateTime(sektion.tid_in)}`;
                }

                if (sektion?.tid_klar_for_utskrivning) {
                    this.postoperativOvervakningFormData.tid_klar_for_utskrivning = `${extractHourMinuteFromDateTime(
                        sektion.tid_klar_for_utskrivning
                    )}`;
                }

                if (sektion?.tid_ut) {
                    this.postoperativOvervakningFormData.tid_ut = `${extractHourMinuteFromDateTime(sektion.tid_ut)}`;
                }

                /* Sängplats kommer som ID (int) från backend, hämta det i sängplatser som vi har hämtat.
                id kommer in som int från backend men vue gör om det till en sträng så casta det till Number */
                if (sektion?.sangplats) {
                    const matchSangplats = this.sangplatser.find((sp) => Number(sp.value) === sektion.sangplats);

                    if (matchSangplats) {
                        this.postoperativOvervakningFormData.sangplats = matchSangplats.value;
                    }
                }

                // this.postoperativOvervakningFormData.omlaggning_forband = sektion?.omlaggning_forband ?? null;
                this.postoperativOvervakningFormData.kommentar_opomrade = sektion?.kommentar_opomrade ?? null;

                this.postoperativOvervakningFormData.mobilisering = sektion?.mobilisering ?? null;
                this.postoperativOvervakningFormData.bromage = sektion?.bromage ?? null;
                this.postoperativOvervakningFormData.kommentar_mobilisering = sektion?.kommentar_mobilisering ?? null;

                this.postoperativOvervakningFormData.kommentar_postop = sektion?.kommentar_postop ?? null;

                this.postoperativOvervakningFormData.illamaende = sektion?.illamaende ?? null;
                this.postoperativOvervakningFormData.temperatur_opslut = sektion?.temperatur_opslut ?? null;
                this.postoperativOvervakningFormData.temperatur_lagsta = sektion?.temperatur_lagsta ?? null;
                this.postoperativOvervakningFormData.temperatur_hogsta = sektion?.temperatur_hogsta ?? null;

                this.postoperativOvervakningFormData.smarta_nrs_vid_60_min = sektion?.smarta_nrs_vid_60_min ?? null;
                this.postoperativOvervakningFormData.smarta_nrs_hogsta = sektion?.smarta_nrs_hogsta ?? null;
                this.postoperativOvervakningFormData.smarta_nrs_vid_utskrivning =
                    sektion?.smarta_nrs_vid_utskrivning ?? null;

                this.postoperativOvervakningFormData.kommentar_cirkulation = sektion?.kommentar_cirkulation ?? null;

                this.postoperativOvervakningFormData.kad = sektion?.kad?.toString();
                this.postoperativOvervakningFormData.storlek = sektion?.kad ? sektion?.storlek : null;
                this.postoperativOvervakningFormData.kuff = sektion?.kad ? sektion?.kuff : null;
                this.postoperativOvervakningFormData.xylocaingel = sektion?.kad ? sektion?.xylocaingel : null;

                if (Array.isArray(sektion?.eliminationer) && sektion.eliminationer.length > 0) {
                    sektion.eliminationer.forEach((item, index) => {
                        this.postoperativOvervakningFormData.eliminationer[index] = {
                            ...item,
                            kad: item?.kad?.toString(),
                            storlek: item?.kad ? item?.storlek : null,
                            tid_for_scanning: `${extractHourMinuteFromDateTime(item?.tid_for_scanning)}`,
                        };
                    });
                }

                if (sektion?.postop_smarta_sammanfattning) {
                    const obj = this.postopSmartaSammanfattning.find(
                        (item) => item.value == sektion.postop_smarta_sammanfattning // iten.value is string and sektion.postop_smarta_sammanfattning is number
                    );

                    if (obj) {
                        this.postoperativOvervakningFormData.postop_smarta_sammanfattning = `${obj.value}`;
                    }
                }

                this.postoperativOvervakningFormData.omlaggning_forband =
                    sektion?.omlaggning_forband?.toString() ?? null;
                this.postoperativOvervakningFormData.cirkulatorisk_stabil =
                    sektion?.cirkulatorisk_stabil?.toString() ?? null;

                this.postoperativOvervakningFormData.postopkomplikationer = sektion?.postopkomplikationer.map(
                    (komplikation) => ({
                        kategori: komplikation.kod_display?.kapitelnamn,
                        kod: komplikation.kod,
                        svarighetsgrad: komplikation.svarighetsgrad,
                        tidpunkt: komplikation.tidpunkt ? extractHourMinuteFromDateTime(komplikation.tidpunkt) : null,
                    })
                );

                this.postoperativOvervakningFormData.news2 = Array.isArray(sektion?.news2)
                    ? sektion.news2.map((news2Entry) => this.mapNews2BedomningFromResponse(news2Entry))
                    : [];
            },
            setPersonalField(personalSection = {}, field) {
                personalSection[field]?.forEach((item, groupIndex) => {
                    if (isEmptyObject(item)) throw new Error(`item object is empty! ${item}`);

                    this.$set(personalSection[field], groupIndex, [
                        {
                            value: `${item.profile}`,
                            label: `${item.profile_display.user_display.name}`,
                        },
                    ]);
                    this.$set(this.personalFormData[field], groupIndex, {
                        profile: {
                            value: `${item.profile}`,
                            label: `${item.profile_display.user_display.name}`,
                        },
                        intid: getTimeFromDatetime(item.intid),
                        uttid: getTimeFromDatetime(item.uttid),
                    });
                });
            },

            async fetchOperation({ anmalanId }) {
                try {
                    const operationsanmalan = await fetchData({
                        tabbnamn: "Operationsjournal",
                        url: `/operation/anmalan/${anmalanId}/`,
                    });

                    const journal = await fetchData({ url: `/operation/operationsjournaler/${operationsanmalan.id}/` });

                    this.operationsanmalan = operationsanmalan;

                    this.operatorer = profileOptions(
                        await fetchData({
                            url: "core/profiles/",
                            params: this.huvudoperatorURLParams,
                        })
                    );

                    this.salar = salarOptions(
                        await fetchData({
                            url: "/operation/salar/",
                            params: {
                                orgunit: operationsanmalan.operationsenhet,
                            },
                        })
                    );

                    this.setOperationsuppgifter({
                        operationsanmalan,
                        sektion: journal.sektion_operationsuppgifter,
                    });

                    await this.setPersonal({
                        operationsanmalan,
                        sektion: journal.sektion_personal,
                    });
                    this.setWHOChecklistaForberedelse({
                        sektion: journal.sektion_whochecklistaforberedelse,
                    });
                    this.setWHOChecklistaTimeout({
                        sektion: journal.sektion_whochecklistatimeout,
                    });
                    this.setWHOChecklistaAvslutning({
                        sektion: journal.sektion_whochecklistaavslutning,
                    });
                    this.setMaterial({ sektion: journal.sektion_material });
                    await this.setAnestesi({
                        sektion: journal.sektion_anestesi,
                        operationsanmalan: operationsanmalan,
                    });
                    this.setTider({ sektion: journal.sektion_tider });
                    this.setPostop({ sektion: journal.sektion_postop });

                    /*
                        Default vid inläsning sätts utanför PostopavvikelseGroup.vue pga hur formuläret är uppbyggt
                        (där vi manipulerar formData direkt). Om någon vill göra en snyggare lösning får ni gärna försöka,
                        men att sätta en default i komponenten går inte att göra utan att det pajar registreringen av
                        komponenten i formulate. Åsikter om fulkod kan skickas till void.
                        Bättre lösning händer i övergången till KForm.
                    */
                    if (
                        !Array.isArray(this.postoperativOvervakningFormData?.postopkomplikationer) ||
                        this.postoperativOvervakningFormData.postopkomplikationer.length === 0
                    ) {
                        // Duplicerade hårdkodningar får vi leva med tills KForm
                        const defaultGradCode = "Grad 0";
                        const defaultCode = "U0";
                        const defaultKategori = "Normalförlopp";

                        this.postoperativOvervakningFormData.postopkomplikationer = [
                            {
                                kategori: defaultKategori,
                                kod: this.postoperativkomplikationer.find(
                                    (komplikation) => komplikation.code === defaultCode
                                )?.id,
                                svarighetsgrad: this.postoperativkomplikationerSvarighetsgrader.find(
                                    (grad) => grad.code === defaultGradCode
                                )?.id,
                            },
                        ];
                    }

                    this.operationsuppgifterSavedFormData = cloneDeep(this.operationsuppgifterFormData);
                    this.patientdataSavedFormData = cloneDeep(this.patientdataFormData);
                    this.personalSavedFormData = cloneDeep(this.personalFormData);
                    this.materialSavedFormData = cloneDeep(this.materialFormData);
                    this.anestesiSavedFormData = cloneDeep(this.anestesiFormData);
                    this.tiderSavedFormData = cloneDeep(this.tiderFormData);
                    this.postoperativOvervakningSavedFormData = cloneDeep(this.postoperativOvervakningFormData);
                    this.stralningsregistreringSavedFormData = cloneDeep(this.stralningsregistreringFormData);
                } catch (error) {
                    openDialog("Kunde inte hämta journal", "error");
                }
            },
            async addInactiveProfiles(profiles, formProfiles) {
                let newArray = cloneDeep(profiles);

                for (let dropdownProfile of formProfiles) {
                    if (
                        dropdownProfile.profile &&
                        !newArray.some((profile) => profile.value === dropdownProfile.profile)
                    ) {
                        const response = await klinikenApi.get(
                            `/core/profiles/${dropdownProfile.profile}/`,
                            getConfig({})
                        );
                        newArray.push({
                            value: response.data.pk,
                            label: response.data.user_display.name,
                        });
                    }
                }

                return newArray;
            },
            async addInactiveProfile(profiles, id) {
                let new_array = cloneDeep(profiles);
                if (id !== undefined && id !== null) {
                    if (!new_array.some((element) => element.value === id)) {
                        const response = await klinikenApi.get(`/core/profiles/${id}/`, getConfig({}));
                        new_array.push({
                            value: response.data.pk,
                            label: response.data.user_display.name,
                        });
                    }
                }
                return new_array;
            },
            /**
             * Set a default value for the last item in the `anestesikomplikationer`
             *
             * Updates the `kategori`, `kod`, and `svarighetsgrad` fields with default values
             * for both anestesiFormData and anestesiSavedFormData.
             */
            setDefaultValueForAnestesikomplikationer() {
                const firstAnestesikomplikation = this.rawAnestesikomplikationer.at(0);

                if (firstAnestesikomplikation) {
                    const lastIndex = this.anestesiFormData.anestesikomplikationer.length - 1;
                    const defaultValue = {
                        kategori: `${firstAnestesikomplikation.kapitel ?? ""}`,
                    };

                    // Set for anestesiFormData
                    this.$set(this.anestesiFormData.anestesikomplikationer, lastIndex, defaultValue);

                    // Set for anestesiSavedFormData
                    this.$set(this.anestesiSavedFormData.anestesikomplikationer, lastIndex, defaultValue);

                    this.anestesikomplikationerKategorierChanged(lastIndex);
                }
            },
            async fetchGenomlysningsapparater() {
                const res = await fetchData({
                    url: "/operation/genomlysningsapparater",
                });

                return res.map(mapGenomlysningsapparat);
            },
        },

        async created() {
            this.readonlyOperationsUppgifter = !this.can({
                action: "update",
                subject: "sektionoperationsuppgifter",
            });
            this.readonlyTider = !this.can({
                action: "update",
                subject: "sektiontider",
            });
            this.readonlyWhoForberwdelse = !this.can({
                action: "update",
                subject: "sektionwhochecklistaforberedelse",
            });
            this.readonlyWhoTimeout = !this.can({
                action: "update",
                subject: "sektionwhochecklistatimeout",
            });
            this.readonlyWhoAvslutning = !this.can({
                action: "update",
                subject: "sektionwhochecklistaavslutning",
            });
            this.readonlyPersonal = !this.can({
                action: "update",
                subject: "sektionpersonal",
            });
            this.readonlyAnestesi = !this.can({
                action: "update",
                subject: "sektionanestesi",
            });
            this.readonlyMaterial = !this.can({
                action: "update",
                subject: "sektionmaterial",
            });
            this.readonlyPatientData = !this.can({
                action: "update",
                subject: "sektionpatientdata",
            });
            this.readonlyPostoperativ = !this.can({
                action: "update",
                subject: "sektionpostop",
            });
            this.readonlyStralningsregistrering = !this.can({
                action: "update",
                subject: "sektionstralningsregistrering",
            });

            await this.$store.dispatch("tabs/displaySpinner", { id: this.tabId, display: true });
            this.loading = true;

            this.typoperationer = typoperationerOptions(
                await fetchData({
                    url: "/operation/typoperationer/",
                })
            );

            let diagnoser = cvOptions(
                await fetchData({
                    url: "/core/kodverk/icdkoder/",
                })
            );

            this.huvuddiagnoser = diagnoser;
            this.$set(this.bidiagnoser, 0, diagnoser);

            let operationskoder = cvOptions(
                await fetchData({
                    url: "/operation/kodverk/operationskoder/",
                })
            );

            this.huvudoperationskoder = operationskoder;

            this.avtal = avtalOptions(
                await fetchData({
                    url: "/core/avtal/",
                })
            );

            this.oplageOptions = cvSimpleOptions(
                await fetchData({
                    url: "/operation/kodverk/oplagen/",
                })
            );

            const vardformer = await fetchData({ url: "/operation/kodverk/vardformer/" });
            if (vardformer) {
                this.vardformOptions = vardformer.map((vf) => ({ value: vf.id, label: vf.namn }));
            }

            const localSangplatser = await fetchData({ url: "/operation/sangplatser" });

            if (localSangplatser && localSangplatser.length > 0) {
                this.sangplatser = localSangplatser
                    .map((sp) => {
                        return { value: sp.id, label: sp.namn };
                    })
                    .sort((spOne, spTwo) => {
                        return spOne.ordning - spTwo.ordning;
                    });
            }

            this.ass_operatorer = profileOptions(
                await fetchData({
                    url: "core/profiles/",
                    params: {
                        roll__code: "kirurg",
                        is_active: true,
                    },
                })
            );

            let operationssjukskoterskor = profileOptions(
                await fetchData({
                    url: "core/profiles/",
                    params: {
                        roll__code: "operationssjukskoterska",
                        is_active: true,
                    },
                })
            );

            let operationsunderskoterska = profileOptions(
                await fetchData({
                    url: "core/profiles/",
                    params: {
                        roll__code: "operationsunderskoterska",
                        is_active: true,
                    },
                })
            );

            this.id_kontroll = operationssjukskoterskor;
            this.operationssjukskoterskor = operationssjukskoterskor;

            this.operationsunderskoterskor = operationsunderskoterska;

            this.anestesilakare = profileOptions(
                await fetchData({
                    url: "core/profiles/",
                    params: {
                        roll__code: "anestesilakare",
                        is_active: true,
                    },
                })
            );

            this.anestesisjukskoterskor = profileOptions(
                await fetchData({
                    url: "core/profiles/",
                    params: {
                        roll__code: "anestesisjukskoterska",
                        is_active: true,
                    },
                })
            );

            let anestesikoder = cvOptions(
                await fetchData({
                    url: "/operation/kodverk/anestesikoder/",
                })
            );

            this.huvudanestesikoder = anestesikoder;
            this.$set(this.bianestesikoder, 0, anestesikoder);

            this.rawAnestesikomplikationer = await fetchData({
                url: "/operation/kodverk/anestesikomplikationer/",
            });
            this.anestesikomplikationerKategorier = anestesikomplikationerKategorierOptions(
                this.rawAnestesikomplikationer
            );

            this.anestesikomplikationSvarighetsgrader = await fetchData({
                url: "/operation/kodverk/anestesikomplikationsvarighetsgrader/",
            });

            this.anestesikomplikationSvarighetsgraderOptions = cvOptionsShort(
                this.anestesikomplikationSvarighetsgrader
            );

            this.postopSmartaSammanfattning = cvSimpleOptions(
                await fetchData({
                    url: "/operation/kodverk/smartasammanfattning/",
                })
            );

            /*
            Hämtas här, pga behov för inläsning av defaultvärde i formuläret för postopkomplikationer.
            Se setOperation()
         */
            this.postoperativkomplikationer = await fetchData({
                url: "/operation/kodverk/postopkomplikationer/",
            });

            /*
            Hämtas här, pga behov för inläsning av defaultvärde i formuläret för postopkomplikationer.
            Se setOperation()
         */
            this.postoperativkomplikationerSvarighetsgrader = await fetchData({
                url: "/operation/kodverk/postopkomplikationsvarighetsgrader/",
            });

            this.$set(this.bioperationskoder, 0, operationskoder);

            await this.fetchOperation({ anmalanId: this.parameters.operationsanmalan_id });

            this.loading = false;
            await this.$store.dispatch("tabs/displaySpinner", { id: this.tabId, display: false });

            /**
             * Sets the default value for 'anestesikomplikationer' if it contains only a single empty row.
             */
            if (JSON.stringify(this.anestesiFormData.anestesikomplikationer) === "[{}]") {
                this.setDefaultValueForAnestesikomplikationer();
            }
        },
    };
</script>

<style lang="scss" scoped>
    @import "@/style/variables.scss";

    .operationsjournal {
        width: $form-width;

        .wrapper-3-1 {
            display: grid;
            grid-template-columns: repeat(4, 1fr);
            grid-column-gap: 26px;
        }

        .forklaring-button {
            background-color: transparent;
            border: none;
            cursor: pointer;
            background-image: url("@/assets/link_internal.svg");
            background-position: left;
            background-size: 13px 13px;
            height: auto;
            padding-left: 18px;
            position: relative;
            top: 4px;
        }

        .forklaring-button-anestesi {
            left: 6px;
        }

        .forklaring-modal {
            .forklaringar {
                display: grid;
                grid-gap: 22px 42px;
                margin: 22px 0 53px 0;
                grid-template-columns: max-content;

                dt {
                    font-family:
                        Roboto Medium,
                        sans-serif;
                    margin: 0;
                }

                dd {
                    grid-column-start: 2;
                    margin: 0;
                }
            }

            .button-group {
                margin-top: 42px;
            }
        }

        .fritext {
            ::v-deep textarea {
                max-width: 33.56rem;
                max-height: 5.75rem;
            }
        }

        // Behövs till grupper där lägg till-knappen är placerad under raden med position absolute,
        // annars flyter den in i nästkommande fält.
        .group-add-button-below {
            margin-bottom: 50px;
        }

        .operationsuppgifter {
            min-width: 1160px;

            ::v-deep .formulate-input-group-repeatable {
                column-gap: 18px;
            }

            .typoperationer {
                padding: 20px;
                margin-bottom: 20px;
                background-color: $color-blue-light;
            }

            ::v-deep .formulate-input-wrapper {
                #huvuddiagnos,
                #huvudoperationskod {
                    width: 75.3%;
                }
            }

            .row {
                display: flex;
                gap: $column-gap;
                width: 75%;

                [data-type="checkbox"] {
                    align-self: end;
                }

                .anmarkning {
                    width: 100%;

                    ::v-deep textarea {
                        height: 46px;
                    }
                }
                .k-form-field-display {
                    padding: 0.6rem 0;
                    ::v-deep span {
                        align-content: center;
                    }
                }
            }
        }

        .patientdata,
        .stralningsregistrering {
            //FIXME: Ska tas bort när vi fixar bredden på formuläret
            min-width: 1160px;
        }

        .personal {
            min-width: 1160px;

            .gast-pa-sal-section {
                // TODO: Om möjligt hitta en bättre lösning som återanvänder befintlig styling.
                display: flex;
                flex-direction: row;
                gap: 15px;

                .gast-pa-sal {
                    margin-top: 35px;
                    min-width: 25%;
                }

                .gaster {
                    ::v-deep .formulate-input-group-repeatable {
                        .formulate-input {
                            min-width: 246px;
                        }
                    }
                }
            }
        }

        // WHO-checklista
        .link-section {
            display: flex;
            flex-direction: column;
            gap: 20px;
            align-items: flex-start;

            .two-elements-grid {
                display: grid;
                grid-template-columns: 70% 50%;
                width: 65%;
                gap: 20px;

                .bg-grey {
                    /* padding: 1.25rem; */
                    padding-left: 2.25rem;
                    padding-top: 1.25rem;
                    width: 100%;
                    background: rgb(246, 248, 251);
                    border-radius: 4px;
                    border: 1px #8080802b solid;
                    height: 90%;
                }

                .checkbox-green {
                    ::v-deep input[type="checkbox"]:checked + label.formulate-input-element-decorator {
                        background-color: #2d9367;
                    }
                }
            }

            .button-link {
                height: 70px;
                width: 480px;
                padding: 15px inherit 15px 20px;

                display: flex;
                flex-direction: column;
                gap: 5px;
                align-items: stretch !important;
                text-align: left;

                small {
                    display: flex;
                    justify-content: space-between;

                    .updated {
                        font-style: italic;
                    }
                }
            }
        }

        .who-modal {
            .header {
                display: flex;
                justify-content: flex-end;
                align-items: flex-end;
                gap: 85px;
                margin-bottom: 30px;

                .dialog-aktiv-patient {
                    margin-right: auto;
                }

                .datum,
                .tid {
                    strong {
                        display: block;
                        font-style: normal;
                        font-weight: 500;
                        font-size: 16px;
                        line-height: 21px;
                        color: $color-input-help;
                    }
                }
            }

            hr {
                border: 0;
                border-bottom: 1px solid $divider-color;
                margin-bottom: 20px;
            }

            .formulate-input[data-type="checkbox"] {
                ul {
                    padding-left: 18px;
                    margin: 0;
                }
            }

            i {
                font-size: 14px;
                font-style: italic;
                font-weight: 400;
                line-height: 16px;
                letter-spacing: 0;
                text-align: left;
            }

            .checkbox-group {
                p {
                    margin-bottom: 10px;
                }

                & > .formulate-input {
                    margin-bottom: 15px;
                }
            }
        }

        .anestesi {
            min-width: 1160px;

            ::v-deep .formulate-input-wrapper {
                .formulate-input-group-repeatable {
                    column-gap: 10px;
                }

                #anestesikomplikationer-kategori {
                    width: 230px;
                }

                #anestesikomplikationer-kod,
                #anestesikomplikationer-svarighetsgrad,
                [data-type="timepicker"] {
                    width: 230px;
                }
            }

            .anestesikomplikationer {
                ::v-deep .formulate-input-errors {
                    position: absolute;
                    top: 80px;
                }

                ::v-deep .formulate-input-group-add-more {
                    top: auto;
                    right: auto;
                    bottom: -10px;
                }

                ::v-deep .formulate-input-element--button {
                    margin-top: 32px;
                }
            }

            .huvudanestesikod {
                width: calc(100% - 275px);
            }
        }
        .repeater-row {
            display: grid;
            grid-template-columns: repeat(4, 1fr);
            grid-template-columns: 100%;

            ::v-deep .formulate-input-group-repeatable {
                display: grid !important;
                grid-template-columns: repeat(4, 1fr) !important;

                .formulate-input-group-repeatable-remove {
                    grid-column-end: -1;
                    align-self: flex-end;
                    justify-self: flex-end;
                }
            }
        }
        .tider {
            min-width: 1160px;

            h4 {
                margin-top: 20px;
                margin-bottom: 0;
            }

            .fritext-normal-width {
                grid-column: span 2;
            }

            .formulate-input-label {
                color: $color-label-grey;
            }
        }

        .material {
            min-width: 1160px;
        }

        .postoperativ-overvakning {
            min-width: 1160px;

            .formulate-input {
                width: $column-width !important;
            }

            .flex-container {
                display: flex;
                flex-wrap: wrap;
                position: relative;
            }

            .elimination {
                display: flex;
                width: $form-width;

                .formulate-input {
                    width: $column-width !important;
                    flex: 1;
                }

                ::v-deep(.formulate-input-group-add-more) {
                    position: absolute !important;
                    top: 100% !important;
                    left: 0;
                }

                ::v-deep(.formulate-input-element--button) {
                    margin-top: -15px;
                }

                ::v-deep(.formulate-input-group-repeatable-remove) {
                    min-width: 0px !important;
                }
            }

            ::v-deep(h4) {
                margin-top: 20px;
                margin-bottom: 0;
            }

            .fritext {
                ::v-deep(textarea) {
                    width: 33.56rem;
                    min-height: 5.75rem;
                }
            }
        }
    }
</style>
